import GovernanceExportDialog, { GovernanceExportFormat } from '../components/GovernanceExportDialog'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useLang } from '../contexts/LangContext'
import HelpTip from '../components/HelpTip'
import { exportFileName } from '../lib/exportFileName'
import { fetchStudyExport, saveBlob, ExportProgress } from '../lib/studyExport'

import { STUDY_STATUS_LABEL, RECOMMENDATION_LABEL } from '../components/studyLabels'
import IdeaAssessmentView from './innovation/IdeaAssessmentView'
import IdeaMatrix from './innovation/IdeaMatrix'
import { IDEA_STATUS_COLOR, IDEA_STATUS_LABEL, QUADRANT, RECOMMENDATION, label, pickBi, scoreColor } from './innovation/ideaLabels'
const API = process.env.REACT_APP_API_URL || 'https://archmindworks.com/api/v1'

function useApi() {
  return useMemo(() => {
    const token = () => localStorage.getItem('ea_token')
    const get = (p: string) => fetch(`${API}${p}`, { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json())
    const post = (p: string, b?: any) => fetch(`${API}${p}`, { method: 'POST', headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' }, body: b ? JSON.stringify(b) : undefined })
      .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || `HTTP ${r.status}`); return d })
    const put = (p: string, b: any) => fetch(`${API}${p}`, { method: 'PUT', headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(b) })
      .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || `HTTP ${r.status}`); return d })
    const del = (p: string) => fetch(`${API}${p}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } })
      .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || `HTTP ${r.status}`); return d })
    return { get, post, put, del }
  }, [])
}

const S = {
  page: { padding: 0, height: '100%', display: 'flex', flexDirection: 'column' as const, background: 'var(--navy)' },
  header: { padding: '20px 28px 16px', display: 'flex', alignItems: 'center', gap: 16, borderBottom: '1px solid var(--border)' },
  tabs: { display: 'flex', gap: 2, padding: '0 28px', borderBottom: '1px solid var(--border)', background: 'var(--navy-light)' },
  tab: (a: boolean) => ({ padding: '10px 16px', fontSize: 13, fontWeight: a ? 600 : 400, color: a ? 'var(--accent)' : 'var(--text-dim)', borderBottom: a ? '2px solid var(--accent)' : '2px solid transparent', borderTop: 'none', borderLeft: 'none', borderRight: 'none', cursor: 'pointer', background: 'none' }),
  content: { flex: 1, overflow: 'auto', padding: '24px 28px' },
  card: { background: 'var(--navy-light)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 },
  btn: (v: 'primary' | 'secondary' | 'danger' = 'secondary') => ({ padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', background: v === 'primary' ? 'var(--accent)' : v === 'danger' ? '#e74c3c22' : 'var(--navy-mid)', color: v === 'primary' ? 'var(--navy)' : v === 'danger' ? '#e74c3c' : 'var(--text)' }),
  input: { width: '100%', padding: '8px 12px', background: 'var(--navy)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none', marginBottom: 10 },
  label: { fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, marginBottom: 4, display: 'block' },
  badge: (c: string) => ({ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: c + '22', color: c }),
  row: { display: 'flex', alignItems: 'center', gap: 12 },
}

// ── Domain constants ────────────────────────────────────────────────────────
// Category taxonomy mirrors apps/api/src/innovation/technology-radar.service.ts's
// VALID_CATEGORIES (a service-layer configurable list, not a schema enum) —
// keep in sync if that list grows.
const CATEGORIES: Record<string, { icon: string; en: string; ar: string }> = {
  AI:                     { icon: '🤖', en: 'Artificial Intelligence', ar: 'الذكاء الاصطناعي' },
  GENERATIVE_AI:          { icon: '✨', en: 'Generative AI', ar: 'الذكاء الاصطناعي التوليدي' },
  AGENTIC_AI:             { icon: '🕹', en: 'Agentic AI', ar: 'الذكاء الاصطناعي الوكيل' },
  DATA_ANALYTICS:         { icon: '📊', en: 'Data & Analytics', ar: 'البيانات والتحليلات' },
  INTEGRATION:            { icon: '🔗', en: 'Integration', ar: 'التكامل' },
  CLOUD:                  { icon: '☁️', en: 'Cloud', ar: 'الحوسبة السحابية' },
  INFRASTRUCTURE:         { icon: '🏗', en: 'Infrastructure', ar: 'البنية التحتية' },
  CYBERSECURITY:          { icon: '🛡', en: 'Cybersecurity', ar: 'الأمن السيبراني' },
  DIGITAL_EXPERIENCE:     { icon: '🖥', en: 'Digital Experience', ar: 'التجربة الرقمية' },
  AUTOMATION:             { icon: '⚙', en: 'Automation', ar: 'الأتمتة' },
  SOFTWARE_ENGINEERING:   { icon: '💻', en: 'Software Engineering', ar: 'هندسة البرمجيات' },
  DEVSECOPS:              { icon: '🔁', en: 'DevSecOps', ar: 'DevSecOps' },
  ENTERPRISE_ARCHITECTURE:{ icon: '🧩', en: 'Enterprise Architecture', ar: 'هندسة المؤسسات' },
  IOT:                    { icon: '📡', en: 'Internet of Things', ar: 'إنترنت الأشياء' },
  EDGE_COMPUTING:         { icon: '📶', en: 'Edge Computing', ar: 'الحوسبة الطرفية' },
  BLOCKCHAIN_DLT:         { icon: '⛓', en: 'Blockchain / DLT', ar: 'البلوك تشين' },
  SPATIAL_COMPUTING:      { icon: '🥽', en: 'Spatial Computing', ar: 'الحوسبة المكانية' },
  QUANTUM:                { icon: '⚛', en: 'Quantum', ar: 'الحوسبة الكمومية' },
  ROBOTICS:               { icon: '🦾', en: 'Robotics', ar: 'الروبوتات' },
  EMERGING_PLATFORMS:     { icon: '🚀', en: 'Emerging Platforms', ar: 'المنصات الناشئة' },
}
const MATURITY_COLOR: Record<string, string> = { EMERGING: '#9b59b6', GROWING: '#3498db', MATURE: '#2ecc71', DECLINING: '#7f8c8d' }
const MATURITY_LABEL: Record<string, { en: string; ar: string }> = {
  EMERGING: { en: 'Emerging', ar: 'ناشئة' }, GROWING: { en: 'Growing', ar: 'في نمو' },
  MATURE: { en: 'Mature', ar: 'ناضجة' }, DECLINING: { en: 'Declining', ar: 'في تراجع' },
}
const MARKET_POSITION_COLOR: Record<string, string> = { EXPLORE: '#7f8c8d', ASSESS: '#f39c12', TRIAL: '#3498db', ADOPT: '#2ecc71', HOLD: '#e74c3c' }
const MARKET_POSITION_LABEL: Record<string, { en: string; ar: string }> = {
  EXPLORE: { en: 'Explore', ar: 'استكشاف' }, ASSESS: { en: 'Assess', ar: 'تقييم' },
  TRIAL: { en: 'Trial', ar: 'تجربة' }, ADOPT: { en: 'Adopt', ar: 'تبنّي' }, HOLD: { en: 'Hold', ar: 'إيقاف' },
}
const TENANT_STATUS_OPTIONS = ['NOT_RELEVANT', 'WATCH', 'EXPLORE', 'ASSESS', 'PILOT', 'ADOPT', 'SCALE', 'HOLD', 'RETIRE']
const TENANT_STATUS_COLOR: Record<string, string> = {
  NOT_RELEVANT: '#7f8c8d', WATCH: '#95a5a6', EXPLORE: '#3498db', ASSESS: '#f39c12', PILOT: '#e67e22',
  ADOPT: '#2ecc71', SCALE: '#27ae60', HOLD: '#e74c3c', RETIRE: '#c0392b',
}
const TENANT_STATUS_LABEL: Record<string, { en: string; ar: string }> = {
  NOT_RELEVANT: { en: 'Not Relevant', ar: 'غير ذات صلة' }, WATCH: { en: 'Watch', ar: 'متابعة' },
  EXPLORE: { en: 'Explore', ar: 'استكشاف' }, ASSESS: { en: 'Assess', ar: 'تقييم' }, PILOT: { en: 'Pilot', ar: 'تجريب' },
  ADOPT: { en: 'Adopt', ar: 'تبنّي' }, SCALE: { en: 'Scale', ar: 'توسّع' }, HOLD: { en: 'Hold', ar: 'إيقاف' }, RETIRE: { en: 'Retire', ar: 'إيقاف نهائي' },
}
const categoryLabel = (code: string, isAR: boolean) => { const c = CATEGORIES[code]; return c ? (isAR ? c.ar : c.en) : code }

// ── Radar domains (Innovation & Transformation Radar) ──────────────────────
// Mirrors apps/api/src/innovation/technology-radar.service.ts's RadarDomain
// list + VALID_CATEGORIES_BY_DOMAIN — keep in sync if either grows. The
// Technology Radar is now one of five domains rather than the whole module.
const DOMAINS = ['TECHNOLOGY', 'DIGITAL_QUALITY', 'OPERATING_MODEL', 'AGILITY_DELIVERY', 'ARCHITECTURE_ENGINEERING']
const DOMAIN_INFO: Record<string, { icon: string; en: string; ar: string }> = {
  TECHNOLOGY:               { icon: '🔭', en: 'Technology', ar: 'التقنية' },
  DIGITAL_QUALITY:          { icon: '✅', en: 'Digital Quality', ar: 'الجودة الرقمية' },
  OPERATING_MODEL:          { icon: '🏛', en: 'Operating Model', ar: 'نموذج التشغيل' },
  AGILITY_DELIVERY:         { icon: '🏃', en: 'Agility & Delivery', ar: 'المرونة والتسليم' },
  ARCHITECTURE_ENGINEERING: { icon: '📐', en: 'Architecture & Engineering', ar: 'الهندسة المعمارية' },
}
const DIGITAL_QUALITY_CATEGORIES: Record<string, { icon: string; en: string; ar: string }> = {
  QUALITY_ENGINEERING:  { icon: '🛠', en: 'Quality Engineering', ar: 'هندسة الجودة' },
  TEST_AUTOMATION:      { icon: '🤖', en: 'Test Automation', ar: 'أتمتة الاختبار' },
  AI_ENABLED_QUALITY:   { icon: '✨', en: 'AI-Enabled Quality', ar: 'الجودة المعتمدة على الذكاء الاصطناعي' },
  NON_FUNCTIONAL_QUALITY: { icon: '📏', en: 'Non-Functional Quality', ar: 'الجودة غير الوظيفية' },
  CONTINUOUS_QUALITY:   { icon: '🔁', en: 'Continuous Quality', ar: 'الجودة المستمرة' },
  QUALITY_GOVERNANCE:   { icon: '📋', en: 'Quality Governance', ar: 'حوكمة الجودة' },
  OBSERVABILITY:        { icon: '👁', en: 'Observability', ar: 'قابلية الملاحظة' },
  RELIABILITY_ENGINEERING: { icon: '🧯', en: 'Reliability Engineering', ar: 'هندسة الموثوقية' },
  ACCESSIBILITY:        { icon: '♿', en: 'Accessibility', ar: 'إمكانية الوصول' },
  QUALITY_INTELLIGENCE: { icon: '📈', en: 'Quality Intelligence', ar: 'ذكاء الجودة' },
  VENDOR_QUALITY:       { icon: '🤝', en: 'Vendor Quality', ar: 'جودة الموردين' },
  DEVSECOPS_QUALITY:    { icon: '🔒', en: 'DevSecOps Quality', ar: 'جودة DevSecOps' },
}
const OPERATING_MODEL_CATEGORIES: Record<string, { icon: string; en: string; ar: string }> = {
  PRODUCT_OPERATING_MODEL:  { icon: '📦', en: 'Product Operating Model', ar: 'نموذج تشغيل المنتج' },
  PLATFORM_OPERATING_MODEL: { icon: '🧱', en: 'Platform Operating Model', ar: 'نموذج تشغيل المنصة' },
  DIGITAL_OPERATING_MODEL:  { icon: '💾', en: 'Digital Operating Model', ar: 'نموذج التشغيل الرقمي' },
  IT_OPERATING_MODEL:       { icon: '🖥', en: 'IT Operating Model', ar: 'نموذج تشغيل تقنية المعلومات' },
  DATA_OPERATING_MODEL:     { icon: '🗄', en: 'Data Operating Model', ar: 'نموذج تشغيل البيانات' },
  AI_OPERATING_MODEL:       { icon: '🤖', en: 'AI Operating Model', ar: 'نموذج تشغيل الذكاء الاصطناعي' },
  CLOUD_OPERATING_MODEL:    { icon: '☁️', en: 'Cloud Operating Model', ar: 'نموذج التشغيل السحابي' },
  SECURITY_OPERATING_MODEL: { icon: '🛡', en: 'Security Operating Model', ar: 'نموذج التشغيل الأمني' },
  GOVERNANCE_MODEL:         { icon: '⚖️', en: 'Governance Model', ar: 'نموذج الحوكمة' },
  SOURCING_MODEL:           { icon: '🔀', en: 'Sourcing Model', ar: 'نموذج الاستعانة بمصادر خارجية' },
  SERVICE_MANAGEMENT_MODEL: { icon: '🧭', en: 'Service Management Model', ar: 'نموذج إدارة الخدمة' },
}
const AGILITY_DELIVERY_CATEGORIES: Record<string, { icon: string; en: string; ar: string }> = {
  ENTERPRISE_AGILITY:        { icon: '🏃', en: 'Enterprise Agility', ar: 'المرونة المؤسسية' },
  PRODUCT_DELIVERY:          { icon: '🚚', en: 'Product Delivery', ar: 'تسليم المنتج' },
  LEAN:                      { icon: '📉', en: 'Lean', ar: 'المرن (Lean)' },
  DEVOPS:                    { icon: '🔁', en: 'DevOps', ar: 'DevOps' },
  TEAM_DESIGN:               { icon: '👥', en: 'Team Design', ar: 'تصميم الفرق' },
  PORTFOLIO_AGILITY:         { icon: '💼', en: 'Portfolio Agility', ar: 'مرونة المحفظة' },
  FLOW_MANAGEMENT:           { icon: '🌊', en: 'Flow Management', ar: 'إدارة التدفق' },
  CONTINUOUS_IMPROVEMENT:    { icon: '📶', en: 'Continuous Improvement', ar: 'التحسين المستمر' },
  ENGINEERING_PRODUCTIVITY:  { icon: '⚡', en: 'Engineering Productivity', ar: 'إنتاجية الهندسة' },
}
const ARCHITECTURE_ENGINEERING_CATEGORIES: Record<string, { icon: string; en: string; ar: string }> = {
  CONTINUOUS_ARCHITECTURE:  { icon: '🔄', en: 'Continuous Architecture', ar: 'الهندسة المعمارية المستمرة' },
  DOMAIN_DRIVEN_DESIGN:     { icon: '🧩', en: 'Domain-Driven Design', ar: 'التصميم الموجه بالمجال' },
  API_ARCHITECTURE:         { icon: '🔌', en: 'API Architecture', ar: 'هندسة واجهات برمجة التطبيقات' },
  EVENT_DRIVEN_ARCHITECTURE:{ icon: '📡', en: 'Event-Driven Architecture', ar: 'الهندسة المعمارية الموجهة بالأحداث' },
  CLOUD_NATIVE_ARCHITECTURE:{ icon: '☁️', en: 'Cloud-Native Architecture', ar: 'الهندسة السحابية الأصلية' },
  SECURITY_ARCHITECTURE:    { icon: '🛡', en: 'Security Architecture', ar: 'الهندسة الأمنية' },
  PLATFORM_ARCHITECTURE:    { icon: '🧱', en: 'Platform Architecture', ar: 'هندسة المنصة' },
  AI_ASSISTED_ARCHITECTURE: { icon: '✨', en: 'AI-Assisted Architecture', ar: 'الهندسة المعمارية بمساعدة الذكاء الاصطناعي' },
  VALUE_STREAM_ARCHITECTURE:{ icon: '🌊', en: 'Value Stream Architecture', ar: 'هندسة تدفق القيمة' },
}
const CATEGORIES_BY_DOMAIN: Record<string, Record<string, { icon: string; en: string; ar: string }>> = {
  TECHNOLOGY: CATEGORIES,
  DIGITAL_QUALITY: DIGITAL_QUALITY_CATEGORIES,
  OPERATING_MODEL: OPERATING_MODEL_CATEGORIES,
  AGILITY_DELIVERY: AGILITY_DELIVERY_CATEGORIES,
  ARCHITECTURE_ENGINEERING: ARCHITECTURE_ENGINEERING_CATEGORIES,
}
// Flat lookup across every domain's categories, so a card/detail view can
// resolve a category's icon/label without first knowing which domain it's in.
const ALL_CATEGORIES: Record<string, { icon: string; en: string; ar: string }> = Object.assign({}, ...Object.values(CATEGORIES_BY_DOMAIN))
const allCategoryLabel = (code: string, isAR: boolean) => { const c = ALL_CATEGORIES[code]; return c ? (isAR ? c.ar : c.en) : code }
const allCategoryIcon = (code: string) => ALL_CATEGORIES[code]?.icon || '🔷'
const domainLabel = (code: string, isAR: boolean) => { const d = DOMAIN_INFO[code]; return d ? (isAR ? d.ar : d.en) : code }

// ── Radar (blip) visualization — spec section 18 ───────────────────────────
// Rings ordered center → edge (innermost = most committed/mature). Market
// rings use the existing marketPosition field; Organization rings use the
// tenant's own tenantInterest.tenantStatus. Items with no ring match for the
// active basis (e.g. never-assessed items in Organization view) are simply
// excluded from the chart rather than plotted at a fabricated position.
const MARKET_RINGS = ['ADOPT', 'TRIAL', 'ASSESS', 'EXPLORE', 'HOLD']
const ORG_RINGS = ['SCALE', 'ADOPT', 'PILOT', 'ASSESS', 'EXPLORE', 'WATCH', 'HOLD', 'RETIRE']
const MATURITY_RINGS = ['MATURE', 'GROWING', 'EMERGING', 'DECLINING']
const HORIZON_RINGS = ['NOW', 'NEXT', 'LATER', 'WATCH']
const HORIZON_LABEL: Record<string, { en: string; ar: string }> = {
  NOW: { en: 'Now', ar: 'الآن' },
  NEXT: { en: 'Next', ar: 'قادم' },
  LATER: { en: 'Later', ar: 'لاحقًا' },
  WATCH: { en: 'Watch', ar: 'مراقبة' },
}
type RingBasis = 'market' | 'org' | 'maturity' | 'horizon'
const ringLabel = (basis: RingBasis, code: string, isAR: boolean) => {
  const src = basis === 'market' ? MARKET_POSITION_LABEL : basis === 'org' ? TENANT_STATUS_LABEL : basis === 'maturity' ? MATURITY_LABEL : HORIZON_LABEL
  const l = src[code]
  return l ? (isAR ? l.ar : l.en) : code
}
// Deterministic hash → stable jitter, so a chart doesn't re-shuffle blip
// positions on every re-render/re-fetch of the same data.
const hashStr = (s: string) => { let h = 0; for (let i = 0; i < s.length; i++) { h = (h * 31 + s.charCodeAt(i)) | 0 } return Math.abs(h) }

export default function InnovationPage() {
  const api = useApi()
  const { t, isAR } = useLang()
  const { user } = useAuth() as any
  const isAdmin = user?.role === 'TENANT_ADMIN'
  // /innovation?study=<id> (Copilot study cards, study notifications) opens that study.
  const [linkedStudyId] = useState<string | null>(() => {
    try { return new URLSearchParams(window.location.search).get('study') } catch { return null }
  })
  const [tab, setTab] = useState<'radar' | 'favorites' | 'watchlist' | 'ideas' | 'studies' | 'profile'>(linkedStudyId ? 'studies' : 'radar')
  const [selected, setSelected] = useState<any>(null)
  const [ideaSeed, setIdeaSeed] = useState<any>(null)
  const [studySeed, setStudySeed] = useState<any>(null)
  // "Start a study" on an idea opens the study form already pointing at it.
  const startStudyFrom = (idea: any) => {
    const objective = idea.problemStatement || idea.description || ''
    setStudySeed({ title: idea.title, titleAr: idea.titleAr || '', objective, originType: 'IDEA', originIdeaId: idea.id, includeImpactAnalysis: (idea.assessment?.eaImpact?.length || 0) > 0 })
    setTab('studies')
  }
  const createIdeaFrom = (item: any) => {
    setIdeaSeed({ title: isAR && item.nameAr ? item.nameAr : item.name, description: item.description, relatedRadarItemId: item.id })
    setTab('ideas')
  }
  return (
    <div style={S.page} dir={isAR ? 'rtl' : 'ltr'}>
      <div style={S.header}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center' }}>
            🔭 {t('innov.title')}
            <HelpTip text={isAR
              ? 'رادار التقنيات يعرض التقنيات الناشئة والمتاحة في السوق، بينما حالتك الخاصة تُظهر أين تقف مؤسستك من كل تقنية — استكشاف، تجريب، تبنّي، أو غير ذات صلة.'
              : 'The radar shows technologies available in the wider market. Your own status shows where your organization actually stands on each one — exploring, piloting, adopting, or not relevant.'} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{t('innov.subtitle')}</div>
        </div>
      </div>
      <div style={S.tabs}>
        <button style={S.tab(tab === 'radar')} onClick={() => { setTab('radar'); setSelected(null) }}>{t('innov.tab_radar')}</button>
        <button style={S.tab(tab === 'favorites')} onClick={() => { setTab('favorites'); setSelected(null) }}>{t('innov.tab_favorites')}</button>
        <button style={S.tab(tab === 'watchlist')} onClick={() => { setTab('watchlist'); setSelected(null) }}>👁 {isAR ? 'قائمة المتابعة' : 'Watchlist'}</button>
        <button style={S.tab(tab === 'ideas')} onClick={() => { setTab('ideas'); setSelected(null) }}>{t('innov.tab_ideas')}</button>
        <button style={S.tab(tab === 'studies')} onClick={() => { setTab('studies'); setSelected(null) }}>{t('innov.tab_studies')}</button>
        <button style={S.tab(tab === 'profile')} onClick={() => { setTab('profile'); setSelected(null) }}>{t('innov.tab_profile')}</button>
      </div>
      <div style={S.content}>
        {tab === 'radar' && <RadarTab api={api} isAdmin={isAdmin} isAR={isAR} t={t} selected={selected} setSelected={setSelected} onSwitchToStudies={() => setTab('studies')} onCreateIdeaFrom={createIdeaFrom} />}
        {tab === 'favorites' && <FavoritesTab api={api} isAdmin={isAdmin} isAR={isAR} t={t} selected={selected} setSelected={setSelected} onCreateIdeaFrom={createIdeaFrom} />}
        {tab === 'watchlist' && <WatchlistTab api={api} isAdmin={isAdmin} isAR={isAR} t={t} userId={user?.userId} selected={selected} setSelected={setSelected} onCreateIdeaFrom={createIdeaFrom} />}
        {tab === 'ideas' && <IdeasTab api={api} isAR={isAR} t={t} userRole={user?.role} seed={ideaSeed} onSeedConsumed={() => setIdeaSeed(null)} onStartStudy={startStudyFrom} />}
        {tab === 'studies' && <StudiesTab api={api} isAR={isAR} t={t} initialStudyId={linkedStudyId} seed={studySeed} onSeedConsumed={() => setStudySeed(null)} />}
        {tab === 'profile' && <ProfileTab api={api} isAR={isAR} t={t} />}
      </div>
    </div>
  )
}

// ── Radar Tab ────────────────────────────────────────────────────────────────
function RadarTab({ api, isAdmin, isAR, t, selected, setSelected, onSwitchToStudies, onCreateIdeaFrom }: any) {
  const [items, setItems] = useState<any[]>([])
  const [domains, setDomains] = useState<any[]>([])
  const [domain, setDomain] = useState('TECHNOLOGY')
  const [loading, setLoading] = useState(true)
  const [category, setCategory] = useState('')
  const [marketPosition, setMarketPosition] = useState('')
  const [creating, setCreating] = useState(false)
  const [seeding, setSeeding] = useState(false)
  const [viewMode, setViewMode] = useState<'card' | 'radar' | 'portfolio'>('card')
  const [ringBasis, setRingBasis] = useState<RingBasis>('market')
  const [compareMode, setCompareMode] = useState(false)
  const [compareIds, setCompareIds] = useState<string[]>([])
  const [comparing, setComparing] = useState(false)
  const MAX_COMPARE = 5

  useEffect(() => { api.get('/innovation/radar/domains').then((d: any) => setDomains(Array.isArray(d) ? d : [])) }, [api])

  const load = useCallback(() => {
    setLoading(true)
    const params = new URLSearchParams()
    if (domain !== 'ALL') params.set('radarDomain', domain)
    if (category) params.set('category', category)
    if (marketPosition) params.set('marketPosition', marketPosition)
    const qs = params.toString()
    api.get(`/innovation/radar${qs ? `?${qs}` : ''}`).then((d: any) => setItems(Array.isArray(d) ? d : [])).finally(() => setLoading(false))
  }, [api, domain, category, marketPosition])
  useEffect(() => { load() }, [load])

  const openItem = async (id: string) => { const full = await api.get(`/innovation/radar/${id}`); setSelected(full) }
  const refreshSelected = async () => { if (selected) await openItem(selected.id) }

  const seed = async () => { setSeeding(true); try { await api.post('/innovation/radar/seed'); await load(); const d = await api.get('/innovation/radar/domains'); setDomains(Array.isArray(d) ? d : []) } finally { setSeeding(false) } }

  const toggleCompare = (id: string) => {
    setCompareIds(ids => {
      if (ids.includes(id)) return ids.filter(x => x !== id)
      if (ids.length >= MAX_COMPARE) return ids
      return [...ids, id]
    })
  }

  if (selected) return <RadarDetail api={api} item={selected} isAdmin={isAdmin} isAR={isAR} t={t} onBack={() => { setSelected(null); load() }} onRefresh={refreshSelected} onOpenItem={openItem} onCreateIdeaFrom={onCreateIdeaFrom} />
  if (comparing) return <ComparisonView api={api} items={items.filter((i: any) => compareIds.includes(i.id))} isAR={isAR} t={t}
    onBack={() => setComparing(false)}
    onDone={() => { setComparing(false); setCompareMode(false); setCompareIds([]) }}
    onSwitchToStudies={onSwitchToStudies} />

  const domainCategories = domain === 'ALL' ? ALL_CATEGORIES : (CATEGORIES_BY_DOMAIN[domain] || {})

  return (
    <div>
      {/* Innovation & Transformation Radar domain switcher - spec section 2/18 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' as const }}>
        {DOMAINS.map(d => {
          const info = domains.find((x: any) => x.domain === d)
          const active = domain === d
          return (
            <button key={d}
              onClick={() => { setDomain(d); setCategory('') }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
                border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
                background: active ? 'var(--accent)22' : 'var(--navy-light)',
                color: active ? 'var(--accent)' : 'var(--text)', fontSize: 13, fontWeight: active ? 700 : 500, cursor: 'pointer',
              }}>
              <span>{DOMAIN_INFO[d].icon}</span>
              <span>{domainLabel(d, isAR)}</span>
              {info && <span style={{ fontSize: 11, opacity: 0.7 }}>({info.itemCount})</span>}
            </button>
          )
        })}
        <button onClick={() => { setDomain('ALL'); setCategory('') }}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
            border: domain === 'ALL' ? '1px solid var(--accent)' : '1px solid var(--border)',
            background: domain === 'ALL' ? 'var(--accent)22' : 'var(--navy-light)',
            color: domain === 'ALL' ? 'var(--accent)' : 'var(--text)', fontSize: 13, fontWeight: domain === 'ALL' ? 700 : 500, cursor: 'pointer',
          }}>
          <span>🌐</span><span>{isAR ? 'كل الرادارات' : 'All Radars'}</span>
        </button>
      </div>

      <div style={{ ...S.row, marginBottom: 16, flexWrap: 'wrap' as const }}>
        <select style={{ ...S.input, marginBottom: 0, width: 240 }} value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">{t('innov.all_categories')}</option>
          {Object.keys(domainCategories).map(c => <option key={c} value={c}>{domainCategories[c].icon} {isAR ? domainCategories[c].ar : domainCategories[c].en}</option>)}
        </select>
        <select style={{ ...S.input, marginBottom: 0, width: 180 }} value={marketPosition} onChange={e => setMarketPosition(e.target.value)}>
          <option value="">{t('innov.all_positions')}</option>
          {Object.keys(MARKET_POSITION_LABEL).map(p => <option key={p} value={p}>{isAR ? MARKET_POSITION_LABEL[p].ar : MARKET_POSITION_LABEL[p].en}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 2, background: 'var(--navy-mid)', borderRadius: 8, padding: 2 }}>
          <button onClick={() => setViewMode('card')} style={{ ...S.btn(viewMode === 'card' ? 'primary' : 'secondary'), padding: '6px 12px' }}>▦ {isAR ? 'بطاقات' : 'Cards'}</button>
          <button onClick={() => setViewMode('radar')} style={{ ...S.btn(viewMode === 'radar' ? 'primary' : 'secondary'), padding: '6px 12px' }}>🎯 {isAR ? 'رادار' : 'Radar'}</button>
          <button onClick={() => setViewMode('portfolio')} style={{ ...S.btn(viewMode === 'portfolio' ? 'primary' : 'secondary'), padding: '6px 12px' }}>📊 {isAR ? 'المحفظة' : 'Portfolio'}</button>
        </div>
        {viewMode === 'card' && (
          <button style={S.btn(compareMode ? 'primary' : 'secondary')} onClick={() => { setCompareMode(m => !m); setCompareIds([]) }}>
            ⚖️ {isAR ? (compareMode ? 'إلغاء المقارنة' : 'قارن') : (compareMode ? 'Cancel Compare' : 'Compare')}
          </button>
        )}
        {isAdmin && <button style={S.btn()} onClick={seed} disabled={seeding}>{seeding ? t('innov.seeding') : t('innov.seed')}</button>}
        {isAdmin && <button style={S.btn('primary')} onClick={() => setCreating(true)}>{t('innov.add_tech')}</button>}
      </div>

      {compareMode && (
        <div style={{ ...S.card, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, background: 'var(--accent)11', borderColor: 'var(--accent)' }}>
          <span style={{ fontSize: 13 }}>
            {isAR ? `تم تحديد ${compareIds.length} من أصل ${MAX_COMPARE} — اختر عنصرين على الأقل للمقارنة` : `${compareIds.length} of ${MAX_COMPARE} selected — pick at least 2 to compare`}
          </span>
          <div style={{ flex: 1 }} />
          <button style={S.btn('primary')} disabled={compareIds.length < 2} onClick={() => setComparing(true)}>
            {isAR ? `قارن (${compareIds.length})` : `Compare (${compareIds.length})`}
          </button>
        </div>
      )}

      {viewMode === 'radar' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button onClick={() => setRingBasis('market')} style={S.btn(ringBasis === 'market' ? 'primary' : 'secondary')}>{isAR ? 'موضع السوق' : 'Market Position'}</button>
          <button onClick={() => setRingBasis('org')} style={S.btn(ringBasis === 'org' ? 'primary' : 'secondary')}>{isAR ? 'موقف مؤسستنا' : 'Our Organization\u2019s Status'}</button>
          <button onClick={() => setRingBasis('maturity')} style={S.btn(ringBasis === 'maturity' ? 'primary' : 'secondary')}>{isAR ? 'النضج' : 'Maturity'}</button>
          <button onClick={() => setRingBasis('horizon')} style={S.btn(ringBasis === 'horizon' ? 'primary' : 'secondary')}>{isAR ? 'الأفق الزمني' : 'Horizon'}</button>
        </div>
      )}

      {creating && <RadarCreateForm api={api} isAR={isAR} t={t} defaultDomain={domain === 'ALL' ? 'TECHNOLOGY' : domain} onDone={() => { setCreating(false); load() }} onCancel={() => setCreating(false)} />}

      {viewMode === 'portfolio' ? (
        <RadarPortfolioDashboard api={api} isAR={isAR} t={t} onGoToDomain={(d: string) => { setDomain(d); setViewMode('card'); setCategory('') }} />
      ) : loading ? (
        <div style={{ color: 'var(--text-dim)' }}>{isAR ? 'جارٍ التحميل…' : 'Loading…'}</div>
      ) : items.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-dim)', padding: 40 }}>
          {category || marketPosition ? t('innov.no_items') : t('innov.no_radar')}
        </div>
      ) : viewMode === 'radar' ? (
        <RadarChart items={items} isAR={isAR} t={t} ringBasis={ringBasis} onSelect={openItem} />
      ) : (
        <div className="stat-grid-3" style={{ alignItems: 'start' }}>
          {items.map((item: any) => <RadarCard key={item.id} item={item} isAR={isAR} t={t} showDomain={domain === 'ALL'}
            compareMode={compareMode} checked={compareIds.includes(item.id)} onToggleCompare={() => toggleCompare(item.id)}
            onClick={() => (compareMode ? toggleCompare(item.id) : openItem(item.id))} />)}
        </div>
      )}
    </div>
  )
}

// ── Executive Brief (spec section 26) ───────────────────────────────────────
function RadarExecutiveBriefCard({ api, isAR, t }: any) {
  const [result, setResult] = useState<any>(null)
  const [generating, setGenerating] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const generate = async () => {
    setGenerating(true)
    try { const r = await api.post('/innovation/radar/executive-brief', {}); setResult(r); setExpanded(true) }
    catch (e: any) { alert(e.message) } finally { setGenerating(false) }
  }

  return (
    <div style={{ ...S.card, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ fontWeight: 600, fontSize: 13, flex: 1, display: 'flex', alignItems: 'center' }}>
          📄 {isAR ? 'الموجز التنفيذي' : 'Executive Brief'}
          <HelpTip text={isAR
            ? 'موجز يكتبه الذكاء الاصطناعي استنادًا فقط إلى بياناتك الحقيقية المخزَّنة بالفعل - لا يُخترع أي رقم أو قرار.'
            : 'An AI-written summary grounded only in your real, already-stored data - no invented numbers or decisions.'} />
        </div>
        <button style={S.btn('primary')} onClick={generate} disabled={generating}>{generating ? (isAR ? 'جارٍ الإنشاء…' : 'Generating…') : (isAR ? 'أنشئ الموجز' : 'Generate Brief')}</button>
      </div>

      {result && expanded && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 12 }}>
            {isAR ? `مبني على: ${result.sourceStats.totalItems} عنصر، ${result.sourceStats.topAssessments.length} تقييمات، ${result.sourceStats.recentDecisions.length} قرارات حديثة، ${result.sourceStats.confirmedRelevanceCount} روابط صلة مؤكَّدة.`
              : `Grounded in: ${result.sourceStats.totalItems} items, ${result.sourceStats.topAssessments.length} assessments, ${result.sourceStats.recentDecisions.length} recent decisions, ${result.sourceStats.confirmedRelevanceCount} confirmed relevance links.`}
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{isAR ? 'الملخص التنفيذي' : 'Executive Summary'}</div>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>{result.brief.executiveSummary}</div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{isAR ? 'حركة المحفظة' : 'Portfolio Movement'}</div>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>{result.brief.portfolioMovement}</div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{isAR ? 'الأولويات الأهم' : 'Top Priorities'}</div>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>{result.brief.topPriorities}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{isAR ? 'الإجراءات الموصى بها' : 'Recommended Next Actions'}</div>
            <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 13, lineHeight: 1.7 }}>
              {(result.brief.recommendedNextActions || []).map((a: string, i: number) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

function RadarPortfolioDashboard({ api, isAR, t, onGoToDomain }: any) {
  const [items, setItems] = useState<any[] | null>(null)
  const [studies, setStudies] = useState<any[] | null>(null)
  const [viewDomain, setViewDomain] = useState('ALL')

  useEffect(() => { api.get('/innovation/radar').then((d: any) => setItems(Array.isArray(d) ? d : [])) }, [api])
  useEffect(() => { api.get('/innovation/studies').then((d: any) => setStudies(Array.isArray(d) ? d : [])) }, [api])

  const scopedItems = useMemo(() => (items || []).filter((i: any) => viewDomain === 'ALL' || i.radarDomain === viewDomain), [items, viewDomain])

  const stats = useMemo(() => {
    if (!items) return null
    const byDomain: Record<string, number> = {}
    DOMAINS.forEach(d => { byDomain[d] = 0 })
    const byMaturity: Record<string, number> = {}
    Object.keys(MATURITY_LABEL).forEach(m => { byMaturity[m] = 0 })
    const byMarketPosition: Record<string, number> = {}
    Object.keys(MARKET_POSITION_LABEL).forEach(p => { byMarketPosition[p] = 0 })
    const byTenantStatus: Record<string, number> = {}
    let notYetAssessed = 0, noOwner = 0, favorited = 0, watching = 0, beingAssessed = 0, beingPiloted = 0, adoptedOrScaled = 0
    for (const item of items) { if (byDomain[item.radarDomain] !== undefined) byDomain[item.radarDomain]++ }
    for (const item of scopedItems) {
      if (byMaturity[item.maturity] !== undefined) byMaturity[item.maturity]++
      if (byMarketPosition[item.marketPosition] !== undefined) byMarketPosition[item.marketPosition]++
      const interest = item.tenantInterest
      if (!interest || interest.tenantStatus === 'NOT_RELEVANT') { notYetAssessed++; continue }
      byTenantStatus[interest.tenantStatus] = (byTenantStatus[interest.tenantStatus] || 0) + 1
      if (!interest.ownerUserId) noOwner++
      if (interest.isFavorite) favorited++
      if (interest.isWatching) watching++
      if (interest.tenantStatus === 'ASSESS') beingAssessed++
      if (interest.tenantStatus === 'PILOT') beingPiloted++
      if (interest.tenantStatus === 'ADOPT' || interest.tenantStatus === 'SCALE') adoptedOrScaled++
    }
    return { total: items.length, byDomain, byMaturity, byMarketPosition, byTenantStatus, notYetAssessed, noOwner, favorited, watching, beingAssessed, beingPiloted, adoptedOrScaled }
  }, [items, scopedItems])

  const funnel = useMemo(() => {
    if (!studies) return null
    const scopedIds = new Set(scopedItems.map((i: any) => i.id))
    const signalsOnRadar = scopedItems.length
    const beingTracked = scopedItems.filter((i: any) => i.tenantInterest && i.tenantInterest.tenantStatus !== 'NOT_RELEVANT').length
    const studiesGenerated = new Set(studies.filter((s: any) => s.originType === 'RADAR_ITEM' && s.originRadarItemId && scopedIds.has(s.originRadarItemId)).map((s: any) => s.originRadarItemId)).size
    const adoptedOrScaled = scopedItems.filter((i: any) => i.tenantInterest && ['ADOPT', 'SCALE'].includes(i.tenantInterest.tenantStatus)).length
    return { signalsOnRadar, beingTracked, studiesGenerated, adoptedOrScaled }
  }, [studies, scopedItems])

  if (!stats) return <div style={{ color: 'var(--text-dim)' }}>{isAR ? 'جارٍ التحميل…' : 'Loading…'}</div>

  const Bar = ({ label, count, max, color, onClick, testId }: any) => (
    <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <div style={{ width: 170, fontSize: 12, textAlign: isAR ? 'left' : 'right', color: 'var(--text-dim)', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, background: 'var(--navy-mid)', borderRadius: 6, height: 18, position: 'relative', overflow: 'hidden' }}>
        <div style={{ width: `${max > 0 ? (count / max) * 100 : 0}%`, background: color, height: '100%', borderRadius: 6, transition: 'width 0.3s' }} />
      </div>
      <div style={{ width: 32, fontSize: 12, fontWeight: 700 }}>{count}</div>
    </div>
  )

  const FunnelStage = ({ label, count, max, color }: any) => (
    <div style={{ textAlign: 'center', flex: 1 }}>
      <div style={{ height: 60, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginBottom: 8 }}>
        <div style={{ width: '70%', height: `${max > 0 ? Math.max((count / max) * 100, 6) : 6}%`, background: color, borderRadius: '6px 6px 0 0' }} />
      </div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{count}</div>
      <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>{label}</div>
    </div>
  )

  const maxDomain = Math.max(1, ...Object.values(stats.byDomain))
  const maxMaturity = Math.max(1, ...Object.values(stats.byMaturity))
  const maxMarket = Math.max(1, ...Object.values(stats.byMarketPosition))
  const maxTenant = Math.max(1, ...Object.values(stats.byTenantStatus))
  const isSingleDomain = viewDomain !== 'ALL'

  return (
    <div>
      <RadarExecutiveBriefCard api={api} isAR={isAR} t={t} />

      {/* Domain-specific management views - spec sections 29-31: selecting one domain reframes this from a
          generic cross-domain portfolio into that domain's own management view (real data only, same
          underlying fields - no fabricated per-domain scores). */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' as const }}>
        <button onClick={() => setViewDomain('ALL')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10, border: viewDomain === 'ALL' ? '1px solid var(--accent)' : '1px solid var(--border)', background: viewDomain === 'ALL' ? 'var(--accent)22' : 'var(--navy-light)', color: viewDomain === 'ALL' ? 'var(--accent)' : 'var(--text)', fontSize: 13, fontWeight: viewDomain === 'ALL' ? 700 : 500, cursor: 'pointer' }}>
          🌐 {isAR ? 'كل الرادارات' : 'All Radars'}
        </button>
        {DOMAINS.map(d => (
          <button key={d} data-testid={`portfolio-view-domain-${d}`} onClick={() => setViewDomain(d)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10, border: viewDomain === d ? '1px solid var(--accent)' : '1px solid var(--border)', background: viewDomain === d ? 'var(--accent)22' : 'var(--navy-light)', color: viewDomain === d ? 'var(--accent)' : 'var(--text)', fontSize: 13, fontWeight: viewDomain === d ? 700 : 500, cursor: 'pointer' }}>
            {DOMAIN_INFO[d].icon} {domainLabel(d, isAR)}
          </button>
        ))}
      </div>

      {isSingleDomain && (
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
          {DOMAIN_INFO[viewDomain].icon} {domainLabel(viewDomain, isAR)} {isAR ? 'عرض الإدارة' : 'Management View'}
        </div>
      )}

      <div className="stat-grid-3" style={{ marginBottom: 20 }}>
        {isSingleDomain ? (
          <>
            <div style={S.card}><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'إجمالي الممارسات' : 'Total practices in this radar'}</div><div style={{ fontSize: 26, fontWeight: 700 }}>{scopedItems.length}</div></div>
            <div style={S.card}><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'قيد التقييم' : 'Being assessed'}</div><div style={{ fontSize: 26, fontWeight: 700 }}>{stats.beingAssessed}</div></div>
            <div style={S.card}><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'قيد التجريب' : 'Being piloted'}</div><div style={{ fontSize: 26, fontWeight: 700 }}>{stats.beingPiloted}</div></div>
            <div style={S.card}><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'مُتبنّاة أو مُوسّعة' : 'Adopted or scaled'}</div><div style={{ fontSize: 26, fontWeight: 700 }}>{stats.adoptedOrScaled}</div></div>
            <div style={S.card}><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'لم تُقيَّم بعد' : 'Not yet assessed'}</div><div style={{ fontSize: 26, fontWeight: 700 }}>{stats.notYetAssessed}</div></div>
            <div style={S.card}><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'قيد المتابعة' : 'Watching'}</div><div style={{ fontSize: 26, fontWeight: 700 }}>👁 {stats.watching}</div></div>
          </>
        ) : (
          <>
            <div style={S.card}><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'إجمالي العناصر عبر كل الرادارات' : 'Total items across all radars'}</div><div style={{ fontSize: 26, fontWeight: 700 }}>{stats.total}</div></div>
            <div style={S.card}><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'لم تُقيَّم بعد' : 'Not yet assessed'}</div><div style={{ fontSize: 26, fontWeight: 700 }}>{stats.notYetAssessed}</div></div>
            <div style={S.card}><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'بلا مالك (من بين ما تم تقييمه)' : 'Without an owner (of assessed)'}</div><div style={{ fontSize: 26, fontWeight: 700 }}>{stats.noOwner}</div></div>
            <div style={S.card}><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'مفضّلة' : 'Favorited'}</div><div style={{ fontSize: 26, fontWeight: 700 }}>⭐ {stats.favorited}</div></div>
            <div style={S.card}><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'قيد المتابعة' : 'Watching'}</div><div style={{ fontSize: 26, fontWeight: 700 }}>👁 {stats.watching}</div></div>
            <div style={S.card}><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'الرادارات المغطاة' : 'Radars covered'}</div><div style={{ fontSize: 26, fontWeight: 700 }}>{Object.values(stats.byDomain).filter((c: any) => c > 0).length} / {DOMAINS.length}</div></div>
          </>
        )}
      </div>

      {funnel && (
        <div style={{ ...S.card, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{isAR ? 'قمع الرادار إلى القيمة' : 'Radar \u2192 Value Funnel'}</div>
          <div style={{ fontSize: 10, color: 'var(--text-dim)', marginBottom: 12 }}>
            {isAR ? 'مبني من بيانات حقيقية فقط: حالتنا الخاصة والدراسات الاستشارية المرتبطة فعليًا بعنصر رادار.' : 'Built only from real data: our own tenant status and studies genuinely linked to a radar item.'}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <FunnelStage label={isAR ? 'إشارات على الرادار' : 'Signals on Radar'} count={funnel.signalsOnRadar} max={funnel.signalsOnRadar} color="#7f8c8d" />
            <FunnelStage label={isAR ? 'نتابعها فعليًا' : 'Being Tracked'} count={funnel.beingTracked} max={funnel.signalsOnRadar} color="#3498db" />
            <FunnelStage label={isAR ? 'ولّدت دراسة استشارية' : 'Studies Generated'} count={funnel.studiesGenerated} max={funnel.signalsOnRadar} color="#e67e22" />
            <FunnelStage label={isAR ? 'مُتبنّاة أو مُوسّعة' : 'Adopted or Scaled'} count={funnel.adoptedOrScaled} max={funnel.signalsOnRadar} color="#2ecc71" />
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {!isSingleDomain && (
          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{isAR ? 'المحفظة حسب الرادار' : 'Portfolio by Radar Domain'}</div>
            {DOMAINS.map(d => <Bar key={d} testId={`portfolio-domain-${d}`} label={`${DOMAIN_INFO[d].icon} ${domainLabel(d, isAR)}`} count={stats.byDomain[d]} max={maxDomain} color="#8e44ad" onClick={() => onGoToDomain(d)} />)}
            <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>{isAR ? 'انقر لعرض عناصر ذلك الرادار' : 'Click a bar to view that domain\u2019s items'}</div>
          </div>
        )}
        <div style={S.card}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{isAR ? 'المحفظة حسب النضج' : 'Portfolio by Maturity'}</div>
          {Object.keys(MATURITY_LABEL).map(m => <Bar key={m} label={isAR ? MATURITY_LABEL[m].ar : MATURITY_LABEL[m].en} count={stats.byMaturity[m]} max={maxMaturity} color={MATURITY_COLOR[m]} />)}
        </div>
        <div style={S.card}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{isAR ? 'المحفظة حسب موضع السوق' : 'Portfolio by Market Position'}</div>
          {Object.keys(MARKET_POSITION_LABEL).map(p => <Bar key={p} label={isAR ? MARKET_POSITION_LABEL[p].ar : MARKET_POSITION_LABEL[p].en} count={stats.byMarketPosition[p]} max={maxMarket} color={MARKET_POSITION_COLOR[p]} />)}
        </div>
        <div style={S.card}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{isAR ? 'قمع تبنّي مؤسستنا' : 'Our Organization\u2019s Adoption Funnel'}</div>
          {ORG_RINGS.map(s => <Bar key={s} label={isAR ? TENANT_STATUS_LABEL[s]?.ar : TENANT_STATUS_LABEL[s]?.en} count={stats.byTenantStatus[s] || 0} max={maxTenant} color={TENANT_STATUS_COLOR[s]} />)}
          <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>
            {isAR ? `بالإضافة إلى ${stats.notYetAssessed} عنصر لم يُقيَّم بعد أو غير ذي صلة` : `Plus ${stats.notYetAssessed} item(s) not yet assessed or marked not relevant`}
          </div>
        </div>
      </div>
    </div>
  )
}

function RadarChart({ items, isAR, t, ringBasis, onSelect }: any) {
  const [hovered, setHovered] = useState<any>(null)
  const rings: string[] = ringBasis === 'market' ? MARKET_RINGS : ringBasis === 'org' ? ORG_RINGS : ringBasis === 'maturity' ? MATURITY_RINGS : HORIZON_RINGS
  const size = 640
  const center = size / 2
  const maxRadius = center - 60
  const ringWidth = maxRadius / rings.length

  // Position each item: ring = its market/org standing; angle = grouped by
  // category (same-category items cluster into contiguous arcs, sorted so
  // the clustering is stable render-to-render) with deterministic jitter
  // inside its ring band so blips of the same category+ring don't stack
  // exactly on top of each other.
  const plotted = useMemo(() => {
    const getRing = (item: any): string | null => {
      if (ringBasis === 'market') return rings.includes(item.marketPosition) ? item.marketPosition : null
      if (ringBasis === 'maturity') return rings.includes(item.maturity) ? item.maturity : null
      if (ringBasis === 'horizon') return item.horizon && rings.includes(item.horizon) ? item.horizon : null
      const status = item.tenantInterest?.tenantStatus
      return status && rings.includes(status) ? status : null
    }
    const withRing = items.map((item: any) => ({ item, ring: getRing(item) })).filter((x: any) => x.ring)
    const sorted = [...withRing].sort((a, b) => (a.item.category || '').localeCompare(b.item.category || '') || a.item.code.localeCompare(b.item.code))
    const total = sorted.length || 1
    return sorted.map((x: any, i: number) => {
      const ringIndex = rings.indexOf(x.ring)
      const angle = (i / total) * 2 * Math.PI - Math.PI / 2
      const h = hashStr(x.item.code)
      const jitter = ((h % 100) / 100 - 0.5) * ringWidth * 0.7
      const r = ringIndex * ringWidth + ringWidth * 0.5 + jitter
      return { ...x, x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) }
    })
  }, [items, ringBasis, rings, ringWidth, center])

  const excluded = items.length - plotted.length

  return (
    <div>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' as const }}>
        <svg viewBox={`0 0 ${size} ${size}`} style={{ width: '100%', maxWidth: 640, flex: '1 1 480px', background: 'var(--navy-light)', borderRadius: 12, border: '1px solid var(--border)' }}>
          {rings.map((ringCode, i) => {
            const rOuter = (rings.length - i) * ringWidth
            return (
              <circle key={ringCode} cx={center} cy={center} r={rOuter}
                fill={i % 2 === 0 ? 'var(--navy)' : 'transparent'} fillOpacity={0.35}
                stroke="var(--border)" strokeWidth={1} />
            )
          })}
          {/* Ring labels, innermost ring's label nearest the center, along the top (12 o'clock) axis */}
          {rings.map((ringCode, i) => {
            const rMid = (rings.length - i - 0.5) * ringWidth
            return (
              <text key={ringCode} x={center} y={center - rMid} textAnchor="middle" dy={-4}
                fontSize={11} fontWeight={700} fill="var(--text-dim)">
                {ringLabel(ringBasis, ringCode, isAR)}
              </text>
            )
          })}
          {plotted.map(({ item, x, y }: any) => (
            <circle key={item.id} className="radar-blip" cx={x} cy={y} r={hovered?.item?.id === item.id ? 9 : 6.5}
              fill={MATURITY_COLOR[item.maturity] || '#7f8c8d'}
              stroke={item.tenantInterest?.isFavorite ? '#f1c40f' : 'var(--navy)'}
              strokeWidth={item.tenantInterest?.isFavorite ? 2.5 : 1.5}
              opacity={0.92} style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHovered({ item, x, y })}
              onMouseLeave={() => setHovered((h: any) => (h?.item?.id === item.id ? null : h))}
              onClick={() => onSelect(item.id)} />
          ))}
          {hovered && (
            <g style={{ pointerEvents: 'none' }}>
              <rect x={Math.min(Math.max(hovered.x - 90, 4), size - 184)} y={hovered.y > center ? hovered.y - 58 : hovered.y + 14}
                width={180} height={44} rx={8} fill="var(--navy)" stroke="var(--accent)" strokeWidth={1} />
              <text x={Math.min(Math.max(hovered.x - 90, 4), size - 184) + 10} y={(hovered.y > center ? hovered.y - 58 : hovered.y + 14) + 18}
                fontSize={12} fontWeight={700} fill="var(--text)">
                {(isAR && hovered.item.nameAr ? hovered.item.nameAr : hovered.item.name).slice(0, 26)}
              </text>
              <text x={Math.min(Math.max(hovered.x - 90, 4), size - 184) + 10} y={(hovered.y > center ? hovered.y - 58 : hovered.y + 14) + 34}
                fontSize={11} fill="var(--text-dim)">
                {allCategoryLabel(hovered.item.category, isAR)}
              </text>
            </g>
          )}
        </svg>
        <div style={{ minWidth: 180 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'var(--text-dim)' }}>{isAR ? 'لون النقطة = النضج' : 'Blip color = maturity'}</div>
          {Object.keys(MATURITY_LABEL).map(m => (
            <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: MATURITY_COLOR[m], display: 'inline-block' }} />
              <span style={{ fontSize: 12 }}>{isAR ? MATURITY_LABEL[m].ar : MATURITY_LABEL[m].en}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #f1c40f', display: 'inline-block' }} />
            <span style={{ fontSize: 12 }}>{t('innov.favorite')}</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 14, lineHeight: 1.5 }}>
            {isAR
              ? `الحلقات من المركز للخارج = درجة الالتزام. ${plotted.length} عنصر معروض على الرادار.`
              : `Rings run center-to-edge by commitment level. ${plotted.length} item(s) plotted.`}
            {excluded > 0 && (
              ringBasis === 'org' ? (isAR ? ` ${excluded} عنصر لم يُقيَّم بعد أو غير ذي صلة (غير معروض).` : ` ${excluded} item(s) not yet assessed or marked not relevant (not shown).`)
              : ringBasis === 'horizon' ? (isAR ? ` ${excluded} عنصر لم يُصنَّف بعد ضمن أفق زمني (غير معروض).` : ` ${excluded} item(s) not yet classified with a horizon (not shown).`)
              : (isAR ? ` ${excluded} عنصر بلا موضع سوق معروف.` : ` ${excluded} item(s) have no recognized market position.`)
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function RadarCard({ item, isAR, t, showDomain, compareMode, checked, onToggleCompare, onClick }: any) {
  const interest = item.tenantInterest
  return (
    <div style={{ ...S.card, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', borderColor: checked ? 'var(--accent)' : undefined }} onClick={onClick}
      onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--accent)')}
      onMouseLeave={e => (e.currentTarget.style.borderColor = checked ? 'var(--accent)' : 'var(--border)')}>
      {compareMode && (
        <input type="checkbox" checked={checked} onChange={() => onToggleCompare()} onClick={e => e.stopPropagation()}
          style={{ position: 'absolute', top: 12, [isAR ? 'left' : 'right']: 12, width: 16, height: 16, cursor: 'pointer' }} />
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ fontSize: 22 }}>{allCategoryIcon(item.category)}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
            {isAR && item.nameAr ? item.nameAr : item.name}
            {interest?.isFavorite && <span title={t('innov.favorite')}>⭐</span>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{allCategoryLabel(item.category, isAR)}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
        {showDomain && item.radarDomain && <span style={S.badge('#8e44ad')}>{DOMAIN_INFO[item.radarDomain]?.icon} {domainLabel(item.radarDomain, isAR)}</span>}
        <span style={S.badge(MATURITY_COLOR[item.maturity] || '#7f8c8d')}>{isAR ? MATURITY_LABEL[item.maturity]?.ar : MATURITY_LABEL[item.maturity]?.en}</span>
        <span style={S.badge(MARKET_POSITION_COLOR[item.marketPosition] || '#7f8c8d')}>{isAR ? MARKET_POSITION_LABEL[item.marketPosition]?.ar : MARKET_POSITION_LABEL[item.marketPosition]?.en}</span>
        {interest?.tenantStatus && interest.tenantStatus !== 'NOT_RELEVANT' && (
          <span style={S.badge(TENANT_STATUS_COLOR[interest.tenantStatus])}>{isAR ? TENANT_STATUS_LABEL[interest.tenantStatus]?.ar : TENANT_STATUS_LABEL[interest.tenantStatus]?.en}</span>
        )}
      </div>
    </div>
  )
}

function ComparisonView({ api, items, isAR, t, onBack, onDone, onSwitchToStudies }: any) {
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<any>(null)

  const rows: { label: string; render: (item: any) => any }[] = [
    { label: isAR ? 'الرادار' : 'Radar', render: (i: any) => `${DOMAIN_INFO[i.radarDomain]?.icon || ''} ${domainLabel(i.radarDomain, isAR)}` },
    { label: isAR ? 'الفئة' : 'Category', render: (i: any) => `${allCategoryIcon(i.category)} ${allCategoryLabel(i.category, isAR)}` },
    { label: t('innov.maturity'), render: (i: any) => isAR ? MATURITY_LABEL[i.maturity]?.ar : MATURITY_LABEL[i.maturity]?.en },
    { label: isAR ? 'موضع السوق' : 'Market Position', render: (i: any) => isAR ? MARKET_POSITION_LABEL[i.marketPosition]?.ar : MARKET_POSITION_LABEL[i.marketPosition]?.en },
    { label: isAR ? 'موقفنا' : 'Our Status', render: (i: any) => i.tenantInterest?.tenantStatus ? (isAR ? TENANT_STATUS_LABEL[i.tenantInterest.tenantStatus]?.ar : TENANT_STATUS_LABEL[i.tenantInterest.tenantStatus]?.en) : (isAR ? 'لم يُقيَّم' : 'Not yet assessed') },
    { label: t('innov.description'), render: (i: any) => isAR && i.descriptionAr ? i.descriptionAr : i.description },
    { label: t('innov.use_cases'), render: (i: any) => (i.typicalUseCases || []).join(' · ') || '—' },
    { label: t('innov.benefits'), render: (i: any) => (i.benefits || []).join(' · ') || '—' },
    { label: t('innov.risks'), render: (i: any) => (i.keyRisks || []).join(' · ') || '—' },
    { label: isAR ? 'القدرات المطلوبة' : 'Required Capabilities', render: (i: any) => (i.requiredCapabilities || []).join(' · ') || '—' },
  ]

  const createStudy = async () => {
    setCreating(true)
    try {
      const names = items.map((i: any) => isAR && i.nameAr ? i.nameAr : i.name)
      const title = (isAR ? 'مقارنة: ' : 'Comparison: ') + names.join(isAR ? ' مقابل ' : ' vs ')
      const objective = isAR
        ? `تقييم مقارن للخيارات التالية لدعم قرار مبني على الأدلة: ${names.join('، ')}.`
        : `A comparative assessment of the following options to support an evidence-based decision: ${names.join(', ')}.`
      const originDescription = items.map((i: any) => `${isAR && i.nameAr ? i.nameAr : i.name} (${allCategoryLabel(i.category, isAR)}, ${isAR ? MATURITY_LABEL[i.maturity]?.ar : MATURITY_LABEL[i.maturity]?.en})`).join('\n')
      const study = await api.post('/innovation/studies', { title, objective, originType: 'MANUAL', originDescription })
      setCreated(study)
    } catch (e: any) { alert(e.message) } finally { setCreating(false) }
  }

  return (
    <div>
      <div style={{ ...S.row, marginBottom: 16 }}>
        <button style={{ ...S.btn(), padding: '6px 12px' }} onClick={onBack}>{t('innov.back')}</button>
        <div style={{ flex: 1, fontSize: 18, fontWeight: 700 }}>⚖️ {isAR ? `مقارنة ${items.length} عناصر` : `Comparing ${items.length} items`}</div>
      </div>

      {created ? (
        <div style={{ ...S.card, textAlign: 'center', padding: 24 }}>
          <div style={{ fontSize: 14, marginBottom: 12 }}>
            {isAR ? 'تم إنشاء دراسة استشارية من هذه المقارنة.' : 'A consultation study was created from this comparison.'}
          </div>
          <div style={S.row}>
            <button style={S.btn('primary')} onClick={() => { onSwitchToStudies?.(); onDone() }}>{isAR ? 'فتح تبويب الدراسات' : 'Open Studies tab'}</button>
            <button style={S.btn()} onClick={onDone}>{t('innov.back')}</button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: isAR ? 'right' : 'left', padding: '10px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-dim)', width: 160 }} />
                  {items.map((i: any) => (
                    <th key={i.id} style={{ textAlign: isAR ? 'right' : 'left', padding: '10px 12px', borderBottom: '1px solid var(--border)', minWidth: 220 }}>
                      {isAR && i.nameAr ? i.nameAr : i.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.label}>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-dim)', fontWeight: 600, verticalAlign: 'top' }}>{row.label}</td>
                    {items.map((i: any) => (
                      <td key={i.id} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', lineHeight: 1.5 }}>{row.render(i)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button style={S.btn('primary')} onClick={createStudy} disabled={creating}>
            {creating ? t('innov.saving') : (isAR ? '📄 أنشئ دراسة استشارية من هذه المقارنة' : '📄 Create Consultation Study from Comparison')}
          </button>
        </>
      )}
    </div>
  )
}

function RadarCreateForm({ api, isAR, t, defaultDomain, onDone, onCancel }: any) {
  const initialDomain = defaultDomain || 'TECHNOLOGY'
  const [form, setForm] = useState({ code: '', name: '', nameAr: '', description: '', radarDomain: initialDomain, category: Object.keys(CATEGORIES_BY_DOMAIN[initialDomain])[0], maturity: 'EMERGING' })
  const [saving, setSaving] = useState(false)

  const create = async () => {
    if (!form.code || !form.name || !form.description) return alert(isAR ? 'الرمز والاسم والوصف مطلوبة' : 'Code, name, and description are required')
    setSaving(true)
    try { await api.post('/innovation/radar', form); onDone() } catch (e: any) { alert(e.message) } finally { setSaving(false) }
  }

  const domainCategories = CATEGORIES_BY_DOMAIN[form.radarDomain] || {}

  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div><div style={S.label}>{t('innov.code')} *</div><input style={S.input} value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase().replace(/\s+/g, '_') }))} /></div>
        <div>
          <div style={S.label}>{isAR ? 'رادار' : 'Radar'} *</div>
          <select style={S.input} value={form.radarDomain} onChange={e => { const nextDomain = e.target.value; const firstCat = Object.keys(CATEGORIES_BY_DOMAIN[nextDomain])[0]; setForm(f => ({ ...f, radarDomain: nextDomain, category: firstCat })) }}>
            {DOMAINS.map(d => <option key={d} value={d}>{DOMAIN_INFO[d].icon} {domainLabel(d, isAR)}</option>)}
          </select>
        </div>
        <div>
          <div style={S.label}>{t('innov.filter_category')} *</div>
          <select style={S.input} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
            {Object.keys(domainCategories).map(c => <option key={c} value={c}>{domainCategories[c].icon} {isAR ? domainCategories[c].ar : domainCategories[c].en}</option>)}
          </select>
        </div>
        <div><div style={S.label}>{t('innov.name_en')} *</div><input style={S.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
        <div><div style={S.label}>{t('innov.name_ar')}</div><input style={S.input} dir="rtl" value={form.nameAr} onChange={e => setForm(f => ({ ...f, nameAr: e.target.value }))} /></div>
        <div>
          <div style={S.label}>{t('innov.maturity')} *</div>
          <select style={S.input} value={form.maturity} onChange={e => setForm(f => ({ ...f, maturity: e.target.value }))}>
            {Object.keys(MATURITY_LABEL).map(m => <option key={m} value={m}>{isAR ? MATURITY_LABEL[m].ar : MATURITY_LABEL[m].en}</option>)}
          </select>
        </div>
      </div>
      <div style={S.label}>{t('innov.description')} *</div>
      <input style={S.input} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      <div style={S.row}>
        <button style={S.btn('primary')} onClick={create} disabled={saving}>{saving ? t('innov.saving') : t('innov.create')}</button>
        <button style={S.btn()} onClick={onCancel}>{t('innov.cancel')}</button>
      </div>
    </div>
  )
}

// ── Radar Detail ─────────────────────────────────────────────────────────────
function RadarDetail({ api, item, isAdmin, isAR, t, onBack, onRefresh, onOpenItem, onCreateIdeaFrom }: any) {
  const [editing, setEditing] = useState(false)
  const [status, setStatus] = useState(item.tenantInterest?.tenantStatus || 'NOT_RELEVANT')
  const [isFavorite, setIsFavorite] = useState(!!item.tenantInterest?.isFavorite)
  const [isWatching, setIsWatching] = useState(!!item.tenantInterest?.isWatching)
  const [notes, setNotes] = useState(item.tenantInterest?.notes || '')
  const [saving, setSaving] = useState(false)
  const [history, setHistory] = useState<any[] | null>(null)
  const [allItems, setAllItems] = useState<any[]>([])

  useEffect(() => { api.get(`/innovation/radar/${item.id}/status-history`).then((d: any) => setHistory(Array.isArray(d) ? d : [])) }, [api, item])
  useEffect(() => { api.get('/innovation/radar').then((d: any) => setAllItems(Array.isArray(d) ? d : [])) }, [api])

  const saveStatus = async () => {
    setSaving(true)
    try { await api.put(`/innovation/radar/${item.id}/my-status`, { tenantStatus: status, isFavorite, isWatching, notes }); onRefresh() }
    catch (e: any) { alert(e.message) } finally { setSaving(false) }
  }

  const deactivate = async () => { if (!window.confirm(t('innov.deactivate_confirm'))) return; await api.post(`/innovation/radar/${item.id}/deactivate`); onBack() }

  const name = isAR && item.nameAr ? item.nameAr : item.name
  const description = isAR && item.descriptionAr ? item.descriptionAr : item.description

  return (
    <div>
      <div style={{ ...S.row, marginBottom: 16 }}>
        <button style={{ ...S.btn(), padding: '6px 12px' }} onClick={onBack}>{t('innov.back')}</button>
        <div style={{ flex: 1, fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>{allCategoryIcon(item.category)} {name}</div>
        <button style={S.btn()} onClick={() => onCreateIdeaFrom(item)}>💡 {isAR ? 'أنشئ فكرة من هذا' : 'Create Idea from This'}</button>
        {isAdmin && (
          <>
            <button style={S.btn()} onClick={() => setEditing(e => !e)}>{t('innov.edit')}</button>
            <button style={S.btn('danger')} onClick={deactivate}>{t('innov.deactivate')}</button>
          </>
        )}
      </div>

      {editing ? (
        <RadarEditForm api={api} item={item} isAR={isAR} t={t} allItems={allItems} onDone={() => { setEditing(false); onRefresh() }} onCancel={() => setEditing(false)} />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' as const }}>
            {item.radarDomain && <span style={S.badge('#8e44ad')}>{DOMAIN_INFO[item.radarDomain]?.icon} {domainLabel(item.radarDomain, isAR)}</span>}
            <span style={S.badge(MATURITY_COLOR[item.maturity])}>{isAR ? MATURITY_LABEL[item.maturity]?.ar : MATURITY_LABEL[item.maturity]?.en}</span>
            <span style={S.badge(MARKET_POSITION_COLOR[item.marketPosition])}>{isAR ? MARKET_POSITION_LABEL[item.marketPosition]?.ar : MARKET_POSITION_LABEL[item.marketPosition]?.en}</span>
            {item.horizon && <span style={S.badge('#16a085')}>{isAR ? 'الأفق:' : 'Horizon:'} {isAR ? HORIZON_LABEL[item.horizon]?.ar : HORIZON_LABEL[item.horizon]?.en}</span>}
            <span style={S.badge('#7f8c8d')}>{allCategoryLabel(item.category, isAR)}</span>
          </div>

          <div style={{ ...S.card, marginBottom: 16 }}>
            <div style={S.label}>{t('innov.description')}</div>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>{description}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <InfoList title={t('innov.use_cases')} items={item.typicalUseCases} />
            <InfoList title={t('innov.benefits')} items={item.benefits} />
            <InfoList title={t('innov.risks')} items={item.keyRisks} />
          </div>

          {item.requiredCapabilities?.length > 0 && (
            <div style={{ ...S.card, marginBottom: 16 }}>
              <div style={S.label}>{t('innov.capabilities')}</div>
              <div style={{ fontSize: 13 }}>{item.requiredCapabilities.join(', ')}</div>
            </div>
          )}

          {item.evidenceSource && (
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 16 }}>
              {t('innov.evidence')}: {item.evidenceSourceUrl ? <a href={item.evidenceSourceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{item.evidenceSource}</a> : item.evidenceSource}
            </div>
          )}

          <div style={S.card}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center' }}>
              {t('innov.my_status')}
              <HelpTip text={isAR ? 'حالة السوق تصفها الجهة المشغّلة للمنصة، بينما هذه الحالة خاصة بمؤسستك فقط ولا يراها أحد غيرك.' : "The market position above is set by the platform; this status is specific to your organization and only visible to you."} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div style={S.label}>{t('innov.tenant_status')}</div>
                <select style={S.input} value={status} onChange={e => setStatus(e.target.value)}>
                  {TENANT_STATUS_OPTIONS.map(s => <option key={s} value={s}>{isAR ? TENANT_STATUS_LABEL[s].ar : TENANT_STATUS_LABEL[s].en}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', paddingTop: 18 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={isFavorite} onChange={e => setIsFavorite(e.target.checked)} /> ⭐ {t('innov.favorite')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={isWatching} onChange={e => setIsWatching(e.target.checked)} /> 👁 {t('innov.watching')}
                </label>
              </div>
            </div>
            <div style={S.label}>{t('innov.notes')}</div>
            <input style={S.input} value={notes} onChange={e => setNotes(e.target.value)} />
            <button style={S.btn('primary')} onClick={saveStatus} disabled={saving}>{saving ? t('innov.saving') : t('innov.save_status')}</button>
          </div>

          <RadarAssessmentCard api={api} item={item} isAR={isAR} t={t} />
          <RadarRelevanceCard api={api} item={item} isAR={isAR} t={t} />

          <div style={{ ...S.card, marginTop: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center' }}>
              🕘 {isAR ? 'سجل القرارات' : 'Decision History'}
              <HelpTip text={isAR
                ? 'سجل غير قابل للتعديل بكل مرة تغيّرت فيها حالة مؤسستك تجاه هذا العنصر.'
                : 'An immutable record of every time your organization\u2019s status on this item changed.'} />
            </div>
            {history === null ? (
              <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{isAR ? 'جارٍ التحميل…' : 'Loading…'}</div>
            ) : history.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                {isAR ? 'لا توجد تغييرات مسجلة بعد لحالة مؤسستك تجاه هذا العنصر.' : 'No status changes recorded yet for your organization on this item.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {history.map((h: any) => {
                  const from = h.previousState?.tenantStatus
                  const to = h.newState?.tenantStatus
                  return (
                    <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, flexWrap: 'wrap' as const }}>
                      <span style={{ color: 'var(--text-dim)', minWidth: 110 }}>{new Date(h.createdAt).toLocaleString(isAR ? 'ar' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      {from ? <span style={S.badge(TENANT_STATUS_COLOR[from] || '#7f8c8d')}>{isAR ? TENANT_STATUS_LABEL[from]?.ar : TENANT_STATUS_LABEL[from]?.en}</span> : <span style={{ color: 'var(--text-dim)' }}>{isAR ? '(بلا حالة)' : '(none)'}</span>}
                      <span>→</span>
                      <span style={S.badge(TENANT_STATUS_COLOR[to] || '#7f8c8d')}>{isAR ? TENANT_STATUS_LABEL[to]?.ar : TENANT_STATUS_LABEL[to]?.en}</span>
                      {h.newState?.notes && <span style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>— {h.newState.notes}</span>}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <RadarRelationshipGraphCard api={api} item={item} allItems={allItems} isAR={isAR} t={t} isAdmin={isAdmin} onOpenItem={onOpenItem} />
          <RadarAnalystCard api={api} item={item} isAR={isAR} t={t} />
          <RadarEvidenceCard api={api} item={item} isAR={isAR} t={t} isAdmin={isAdmin} />
          <RadarPilotsCard api={api} item={item} isAR={isAR} t={t} isAdmin={isAdmin} />
          <RadarValueRealizationCard api={api} item={item} isAR={isAR} t={t} isAdmin={isAdmin} />
        </>
      )}
    </div>
  )
}

function InfoList({ title, items }: { title: string; items?: string[] }) {
  return (
    <div style={S.card}>
      <div style={S.label}>{title}</div>
      {!items || items.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>—</div>
      ) : (
        <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 12, lineHeight: 1.7 }}>
          {items.map((i, idx) => <li key={idx}>{i}</li>)}
        </ul>
      )}
    </div>
  )
}

// ── Tenant Assessment Engine (spec section 12) ──────────────────────────────
const SCORE_COLOR = (score: number) => (score >= 4 ? '#2ecc71' : score >= 2.5 ? '#f1c40f' : '#e74c3c')
const BUCKET_LABEL: Record<string, { en: string; ar: string }> = {
  strategicFit: { en: 'Strategic Fit', ar: 'الملاءمة الاستراتيجية' },
  opportunity: { en: 'Opportunity', ar: 'الفرصة' },
  readiness: { en: 'Readiness', ar: 'الجاهزية' },
  risk: { en: 'Risk', ar: 'المخاطر' },
}

function RadarAssessmentCard({ api, item, isAR, t }: any) {
  const [assessment, setAssessment] = useState<any>(null)
  const [template, setTemplate] = useState<any[] | null>(null)
  const [criteria, setCriteria] = useState<any[] | null>(null)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => { api.get(`/innovation/radar/${item.id}/assessment`).then((d: any) => setAssessment(d && typeof d.compositeScore === 'number' ? d : null)) }, [api, item])
  useEffect(() => { api.get('/innovation/radar/assessment-criteria-template').then((d: any) => setTemplate(Array.isArray(d) ? d : [])) }, [api])

  const startEditing = () => {
    const base = assessment?.criteria || template || []
    setCriteria(base.map((c: any) => ({ key: c.key, bucket: c.bucket, weight: c.weight, invert: c.invert || false, label: c.label || (isAR ? c.ar : c.en) || c.key, score: c.score ?? 3 })))
    setEditing(true)
  }

  const save = async () => {
    if (!criteria) return
    setSaving(true)
    try {
      const result = await api.put(`/innovation/radar/${item.id}/assessment`, { criteria: criteria.map(c => ({ key: c.key, bucket: c.bucket, weight: c.weight, invert: c.invert, score: c.score })) })
      setAssessment(result)
      setEditing(false)
    } catch (e: any) { alert(e.message) } finally { setSaving(false) }
  }

  if (template === null) return null

  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center' }}>
        📊 {isAR ? 'تقييم مؤسستنا' : 'Our Organization\u2019s Assessment'}
        <HelpTip text={isAR
          ? 'تقييم مرجّح بمعايير تُدخلها أنت. النتائج المحسوبة (الفرصة/الجاهزية/المخاطر/الملاءمة الاستراتيجية/الأولوية الإجمالية) مبنية على أرقامك فقط - وليست تقديرًا من الذكاء الاصطناعي.'
          : 'A weighted assessment using criteria you enter. The computed scores (Opportunity/Readiness/Risk/Strategic Fit/Composite Priority) are derived only from your own numbers - not an AI guess.'} />
      </div>

      {!editing ? (
        assessment ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 12 }}>
              {(['strategicFit', 'opportunity', 'readiness', 'risk'] as const).map(b => (
                <div key={b} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: SCORE_COLOR(b === 'risk' ? 5 - assessment[`${b}Score`] : assessment[`${b}Score`]) }}>{assessment[`${b}Score`].toFixed(1)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>{isAR ? BUCKET_LABEL[b].ar : BUCKET_LABEL[b].en}</div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', padding: '10px 0', borderTop: '1px solid var(--border)', marginBottom: 12 }}>
              <div style={{ fontSize: 26, fontWeight: 700 }}>{assessment.compositeScore.toFixed(2)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? 'درجة الأولوية الإجمالية' : 'Composite Priority Score'}</div>
            </div>
            <button style={S.btn()} onClick={startEditing}>{isAR ? 'تحديث التقييم' : 'Update Assessment'}</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>{isAR ? 'لم يُقيَّم هذا العنصر بعد من قِبل مؤسستنا.' : 'This item has not been assessed by our organization yet.'}</div>
            <button style={S.btn('primary')} onClick={startEditing}>{isAR ? 'ابدأ التقييم' : 'Start Assessment'}</button>
          </>
        )
      ) : (
        <>
          {criteria?.map((c, idx) => (
            <div key={c.key} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <span>{c.label} <span style={{ color: 'var(--text-dim)' }}>({isAR ? BUCKET_LABEL[c.bucket]?.ar : BUCKET_LABEL[c.bucket]?.en})</span></span>
                <span style={{ fontWeight: 700 }}>{c.score}</span>
              </div>
              <input type="range" min={1} max={5} step={1} value={c.score}
                onChange={e => setCriteria(cs => cs!.map((x, i) => i === idx ? { ...x, score: Number(e.target.value) } : x))}
                style={{ width: '100%' }} />
            </div>
          ))}
          <div style={S.row}>
            <button style={S.btn('primary')} onClick={save} disabled={saving}>{saving ? t('innov.saving') : t('innov.save')}</button>
            <button style={S.btn()} onClick={() => setEditing(false)}>{t('innov.cancel')}</button>
          </div>
        </>
      )}
    </div>
  )
}

// ── Organizational Relevance Engine (spec section 13) ───────────────────────
function RadarRelevanceCard({ api, item, isAR, t }: any) {
  const [links, setLinks] = useState<any[] | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')

  const load = useCallback(() => { api.get(`/innovation/radar/${item.id}/relevance`).then((d: any) => setLinks(Array.isArray(d) ? d : [])) }, [api, item])
  useEffect(() => { load() }, [load])

  const analyze = async () => {
    setAnalyzing(true)
    try { await api.post(`/innovation/radar/${item.id}/analyze-relevance`); load() }
    catch (e: any) { alert(e.message) } finally { setAnalyzing(false) }
  }

  const review = async (linkId: string, decision: string, editedRationale?: string) => {
    try { await api.put(`/innovation/radar/relevance/${linkId}`, { decision, editedRationale }); setEditingId(null); load() }
    catch (e: any) { alert(e.message) }
  }

  if (links === null) return null

  const suggested = links.filter(l => l.status === 'AI_SUGGESTED')
  const decided = links.filter(l => l.status !== 'AI_SUGGESTED')

  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', flex: 1 }}>
          🎯 {isAR ? 'الصلة بمؤسستنا' : 'Relevance to Our Organization'}
          <HelpTip text={isAR
            ? 'يحلّل الذكاء الاصطناعي هذا العنصر مقابل القدرات والتطبيقات الحقيقية في مستودعك، ويقترح روابط تحتاج لمراجعتك (تأكيد/رفض/تعديل).'
            : 'AI analyzes this item against the real capabilities and applications in your repository and suggests links for you to review (confirm/reject/edit).'} />
        </div>
        <button style={S.btn()} onClick={analyze} disabled={analyzing}>{analyzing ? (isAR ? 'جارٍ التحليل…' : 'Analyzing…') : (isAR ? '🔍 حلّل الصلة' : '🔍 Analyze Relevance')}</button>
      </div>

      {links.length === 0 && !analyzing && (
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{isAR ? 'لم يُجرَ تحليل للصلة بعد.' : 'No relevance analysis has been run yet.'}</div>
      )}

      {suggested.length > 0 && (
        <div style={{ marginBottom: decided.length > 0 ? 16 : 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', marginBottom: 8 }}>{isAR ? 'مقترح من الذكاء الاصطناعي - بحاجة لمراجعتك' : 'AI Suggested - needs your review'}</div>
          {suggested.map((l: any) => (
            <div key={l.id} style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={S.badge('#3498db')}>{l.asset ? l.asset.assetType : l.assetType}</span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{l.asset ? (isAR && l.asset.nameAr ? l.asset.nameAr : l.asset.name) : (isAR ? '(الأصل محذوف)' : '(asset no longer exists)')}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 8 }}>{isAR && l.rationaleAr ? l.rationaleAr : l.rationale}</div>
              {editingId === l.id ? (
                <div>
                  <input style={S.input} value={editText} onChange={e => setEditText(e.target.value)} placeholder={isAR ? 'اكتب التبرير المعدَّل…' : 'Write your edited rationale…'} />
                  <div style={S.row}>
                    <button style={S.btn('primary')} onClick={() => review(l.id, 'EDITED', editText)} disabled={!editText.trim()}>{t('innov.save')}</button>
                    <button style={S.btn()} onClick={() => setEditingId(null)}>{t('innov.cancel')}</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{ ...S.btn('primary'), padding: '4px 10px', fontSize: 12 }} onClick={() => review(l.id, 'CONFIRMED')}>✓ {isAR ? 'تأكيد' : 'Confirm'}</button>
                  <button style={{ ...S.btn(), padding: '4px 10px', fontSize: 12 }} onClick={() => { setEditingId(l.id); setEditText(l.rationale) }}>✎ {isAR ? 'تعديل' : 'Edit'}</button>
                  <button style={{ ...S.btn('danger'), padding: '4px 10px', fontSize: 12 }} onClick={() => review(l.id, 'REJECTED')}>✕ {isAR ? 'رفض' : 'Reject'}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {decided.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', marginBottom: 8 }}>{isAR ? 'مُراجَع' : 'Reviewed'}</div>
          {decided.map((l: any) => (
            <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 12 }}>
              <span style={S.badge(l.status === 'CONFIRMED' ? '#2ecc71' : l.status === 'REJECTED' ? '#e74c3c' : '#f1c40f')}>{l.status}</span>
              <span>{l.asset ? (isAR && l.asset.nameAr ? l.asset.nameAr : l.asset.name) : (isAR ? '(الأصل محذوف)' : '(asset no longer exists)')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Relationship Graph (spec section 25) ────────────────────────────────────
const RELATIONSHIP_TYPE_COLOR: Record<string, string> = {
  DEPENDS_ON: '#e74c3c', ENABLES: '#2ecc71', COMPLEMENTS: '#3498db', COMPETES_WITH: '#e67e22',
  REPLACES: '#9b59b6', CONVERGES_WITH: '#1abc9c', SUPPORTS: '#f1c40f', REQUIRES: '#e74c3c', RELATED: '#7f8c8d',
}
const RELATIONSHIP_TYPE_LABEL: Record<string, { en: string; ar: string }> = {
  DEPENDS_ON: { en: 'Depends On', ar: 'يعتمد على' },
  ENABLES: { en: 'Enables', ar: 'يُمكّن' },
  COMPLEMENTS: { en: 'Complements', ar: 'يُكمّل' },
  COMPETES_WITH: { en: 'Competes With', ar: 'ينافس' },
  REPLACES: { en: 'Replaces', ar: 'يحل محل' },
  CONVERGES_WITH: { en: 'Converges With', ar: 'يتقارب مع' },
  SUPPORTS: { en: 'Supports', ar: 'يدعم' },
  REQUIRES: { en: 'Requires', ar: 'يتطلب' },
  RELATED: { en: 'Related', ar: 'ذو صلة' },
}

function RadarRelationshipGraphCard({ api, item, allItems, isAR, t, isAdmin, onOpenItem }: any) {
  const [data, setData] = useState<{ outgoing: any[]; incoming: any[] } | null>(null)
  const [adding, setAdding] = useState(false)
  const [search, setSearch] = useState('')
  const [targetId, setTargetId] = useState('')
  const [relType, setRelType] = useState('ENABLES')
  const [types, setTypes] = useState<string[]>(Object.keys(RELATIONSHIP_TYPE_LABEL).filter(t2 => t2 !== 'RELATED'))
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => { api.get(`/innovation/radar/${item.id}/relationships`).then((d: any) => setData(d && d.outgoing ? d : { outgoing: [], incoming: [] })) }, [api, item])
  useEffect(() => { load() }, [load])
  useEffect(() => { api.get('/innovation/radar/relationship-types').then((d: any) => { if (Array.isArray(d) && d.length > 0) setTypes(d) }) }, [api])

  const addRel = async () => {
    if (!targetId) return
    setSaving(true)
    try { await api.post(`/innovation/radar/${item.id}/relationships`, { toTechnologyId: targetId, relationshipType: relType }); setAdding(false); setSearch(''); setTargetId(''); load() }
    catch (e: any) { alert(e.message) } finally { setSaving(false) }
  }
  const removeRel = async (relId: string) => {
    try { await api.post(`/innovation/radar/relationships/${relId}/delete`, {}); load() } catch (e: any) { alert(e.message) }
  }

  if (!data) return null
  const total = data.outgoing.length + data.incoming.length
  const candidates = (allItems || []).filter((x: any) => x.id !== item.id && (isAR && x.nameAr ? x.nameAr : x.name).toLowerCase().includes(search.toLowerCase()))

  // Simple radial ego-graph: this item at center, every related item placed
  // evenly around a circle, outgoing edges pointing out and incoming edges
  // pointing in - deliberately just the current item's own neighborhood
  // (spec's own example is a walkable chain, one hop at a time), not an
  // attempt at a whole-catalog force-directed graph.
  const size = 460
  const center = size / 2
  const radius = 165
  const nodes = [...data.outgoing.map((r: any) => ({ ...r, direction: 'out' as const })), ...data.incoming.map((r: any) => ({ ...r, direction: 'in' as const }))]
  const positioned = nodes.map((n, i) => {
    const angle = (i / Math.max(nodes.length, 1)) * 2 * Math.PI - Math.PI / 2
    return { ...n, x: center + radius * Math.cos(angle), y: center + radius * Math.sin(angle) }
  })

  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>🕸️ {isAR ? 'رسم العلاقات' : 'Relationship Graph'}</div>
        {isAdmin && <button style={{ ...S.btn(), padding: '4px 10px', fontSize: 12 }} onClick={() => setAdding(a => !a)}>+ {isAR ? 'أضف علاقة' : 'Add Relationship'}</button>}
      </div>

      {adding && (
        <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12 }}>
          <input style={S.input} placeholder={isAR ? 'ابحث عن عنصر…' : 'Search for an item…'} value={search} onChange={e => { setSearch(e.target.value); setTargetId('') }} />
          {search && !targetId && (
            <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8 }}>
              {candidates.slice(0, 15).map((c: any) => (
                <div key={c.id} onClick={() => { setTargetId(c.id); setSearch(isAR && c.nameAr ? c.nameAr : c.name) }}
                  style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--navy-mid)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  {allCategoryIcon(c.category)} {isAR && c.nameAr ? c.nameAr : c.name}
                </div>
              ))}
              {candidates.length === 0 && <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text-dim)' }}>{isAR ? 'لا توجد نتائج' : 'No matches'}</div>}
            </div>
          )}
          <select style={S.input} value={relType} onChange={e => setRelType(e.target.value)}>
            {types.map(ty => <option key={ty} value={ty}>{isAR ? RELATIONSHIP_TYPE_LABEL[ty]?.ar || ty : RELATIONSHIP_TYPE_LABEL[ty]?.en || ty}</option>)}
          </select>
          <div style={S.row}>
            <button style={S.btn('primary')} onClick={addRel} disabled={!targetId || saving}>{saving ? t('innov.saving') : t('innov.save')}</button>
            <button style={S.btn()} onClick={() => setAdding(false)}>{t('innov.cancel')}</button>
          </div>
        </div>
      )}

      {total === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{isAR ? 'لا توجد علاقات مسجَّلة لهذا العنصر بعد.' : 'No relationships recorded for this item yet.'}</div>
      ) : (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' as const }}>
          <svg viewBox={`0 0 ${size} ${size}`} style={{ width: '100%', maxWidth: 460, flex: '1 1 320px' }}>
            {positioned.map((n: any) => (
              <line key={`edge-${n.id}`}
                x1={n.direction === 'out' ? center : n.x} y1={n.direction === 'out' ? center : n.y}
                x2={n.direction === 'out' ? n.x : center} y2={n.direction === 'out' ? n.y : center}
                stroke={RELATIONSHIP_TYPE_COLOR[n.relationshipType] || '#7f8c8d'} strokeWidth={2} markerEnd="url(#arrow)" />
            ))}
            <defs>
              <marker id="arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill="var(--text-dim)" /></marker>
            </defs>
            {positioned.map((n: any) => (
              <text key={`label-${n.id}`} x={(center + n.x) / 2} y={(center + n.y) / 2 - 6} textAnchor="middle" fontSize={9} fill={RELATIONSHIP_TYPE_COLOR[n.relationshipType]}>
                {isAR ? RELATIONSHIP_TYPE_LABEL[n.relationshipType]?.ar || n.relationshipType : RELATIONSHIP_TYPE_LABEL[n.relationshipType]?.en || n.relationshipType}
              </text>
            ))}
            <circle cx={center} cy={center} r={26} fill="var(--accent)" opacity={0.25} stroke="var(--accent)" strokeWidth={2} />
            <text x={center} y={center + 4} textAnchor="middle" fontSize={10} fontWeight={700} fill="var(--text)">{isAR ? 'هذا العنصر' : 'This item'}</text>
            {positioned.map((n: any) => n.otherItem && (
              <g key={`node-${n.id}`} style={{ cursor: 'pointer' }} onClick={() => onOpenItem(n.otherItem.id)}>
                <circle cx={n.x} cy={n.y} r={20} fill="var(--navy-mid)" stroke={RELATIONSHIP_TYPE_COLOR[n.relationshipType]} strokeWidth={2} />
                <text x={n.x} y={n.y + 32} textAnchor="middle" fontSize={10} fill="var(--text)">{(isAR && n.otherItem.nameAr ? n.otherItem.nameAr : n.otherItem.name).slice(0, 16)}</text>
              </g>
            ))}
          </svg>
          {isAdmin && (
            <div style={{ minWidth: 180, fontSize: 11 }}>
              <div style={{ color: 'var(--text-dim)', marginBottom: 6, fontWeight: 700 }}>{isAR ? 'إدارة العلاقات' : 'Manage'}</div>
              {[...data.outgoing, ...data.incoming].map((r: any) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ flex: 1 }}>{r.otherItem ? (isAR && r.otherItem.nameAr ? r.otherItem.nameAr : r.otherItem.name) : (isAR ? '(محذوف)' : '(deleted)')}</span>
                  <button onClick={() => removeRel(r.id)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 12 }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── AI Innovation Analyst (spec section 15) ─────────────────────────────────
const IMPACT_FRAMING_LABEL: Record<string, { en: string; ar: string }> = {
  'Architecture Impact': { en: 'Architecture Impact', ar: 'الأثر المعماري' },
  'Operating Model Impact': { en: 'Operating Model Impact', ar: 'أثر نموذج التشغيل' },
  'Quality Impact': { en: 'Quality Impact', ar: 'أثر الجودة' },
  'Delivery Impact': { en: 'Delivery Impact', ar: 'أثر التسليم' },
}

function RadarAnalystCard({ api, item, isAR, t }: any) {
  const [result, setResult] = useState<any>(null)
  const [analyzing, setAnalyzing] = useState(false)

  const analyze = async () => {
    setAnalyzing(true)
    try { const r = await api.post(`/innovation/radar/${item.id}/analyze`, {}); setResult(r) }
    catch (e: any) { alert(e.message) } finally { setAnalyzing(false) }
  }

  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ fontWeight: 600, fontSize: 13, flex: 1, display: 'flex', alignItems: 'center' }}>
          🤖 {isAR ? 'محلل الابتكار بالذكاء الاصطناعي' : 'AI Innovation Analyst'}
          <HelpTip text={isAR ? 'تحليل مبني فقط على بيانات هذا العنصر الحقيقية وتقييم/صلة مؤسستك إن وُجدت.' : 'Analysis grounded only in this item\u2019s own real fields and your organization\u2019s real assessment/relevance data, if any.'} />
        </div>
        <button style={S.btn('primary')} onClick={analyze} disabled={analyzing}>{analyzing ? (isAR ? 'جارٍ التحليل…' : 'Analyzing…') : (isAR ? 'حلّل هذا العنصر' : 'Analyze This Item')}</button>
      </div>
      {result && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 10 }}>
            {isAR
              ? `مبني على: موقفنا${result.groundedIn.tenantStatus ? ` (${result.groundedIn.tenantStatus})` : ' (غير محدد)'}${result.groundedIn.hasAssessment ? `، تقييم بدرجة ${result.groundedIn.compositeScore.toFixed(2)}` : '، بلا تقييم بعد'}، ${result.groundedIn.confirmedRelevanceCount} روابط صلة مؤكَّدة.`
              : `Grounded in: our status${result.groundedIn.tenantStatus ? ` (${result.groundedIn.tenantStatus})` : ' (unset)'}${result.groundedIn.hasAssessment ? `, an assessment scoring ${result.groundedIn.compositeScore.toFixed(2)}` : ', no assessment yet'}, ${result.groundedIn.confirmedRelevanceCount} confirmed relevance link(s).`}
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{isAR ? IMPACT_FRAMING_LABEL[result.impactFraming]?.ar : IMPACT_FRAMING_LABEL[result.impactFraming]?.en}</div>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>{result.analysis.impactSummary}</div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{isAR ? 'اعتبارات رئيسية' : 'Key Considerations'}</div>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>{result.analysis.keyConsiderations}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={S.badge(TENANT_STATUS_COLOR[result.analysis.recommendedNextStep] || '#3498db')}>{isAR ? 'الخطوة التالية الموصى بها:' : 'Recommended Next Step:'} {isAR ? TENANT_STATUS_LABEL[result.analysis.recommendedNextStep]?.ar : TENANT_STATUS_LABEL[result.analysis.recommendedNextStep]?.en}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 6 }}>{result.analysis.recommendedNextStepRationale}</div>
        </div>
      )}
    </div>
  )
}

// ── Evidence & Signal Model + Trend Intelligence (spec sections 10-11) ──────
const SIGNAL_TYPES = ['RESEARCH', 'MARKET', 'ADOPTION', 'REGULATORY', 'STANDARD', 'VENDOR', 'ACADEMIC', 'GOVERNMENT', 'IMPLEMENTATION', 'INTERNAL']
const TREND_COLOR: Record<string, string> = { TRENDING: '#e74c3c', RISING: '#2ecc71', STABLE: '#3498db', DECLINING: '#e67e22', INSUFFICIENT_EVIDENCE: '#7f8c8d' }
const TREND_LABEL: Record<string, { en: string; ar: string }> = {
  TRENDING: { en: 'Trending', ar: 'رائج' }, RISING: { en: 'Rising', ar: 'صاعد' }, STABLE: { en: 'Stable', ar: 'مستقر' },
  DECLINING: { en: 'Declining', ar: 'متراجع' }, INSUFFICIENT_EVIDENCE: { en: 'Insufficient Evidence', ar: 'أدلة غير كافية' },
}

function RadarEvidenceCard({ api, item, isAR, t, isAdmin }: any) {
  const [signals, setSignals] = useState<any[] | null>(null)
  const [trend, setTrend] = useState<any>(null)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ signalType: 'RESEARCH', source: '', sourceUrl: '', publisher: '', summary: '', confidence: 'MEDIUM' })
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    api.get(`/innovation/radar/${item.id}/evidence`).then((d: any) => setSignals(Array.isArray(d) ? d : []))
    api.get(`/innovation/radar/${item.id}/trend`).then((d: any) => setTrend(d && d.trendState ? d : null))
  }, [api, item])
  useEffect(() => { load() }, [load])

  const addSignal = async () => {
    if (!form.source || !form.summary) return alert(isAR ? 'المصدر والملخص مطلوبان' : 'Source and summary are required')
    setSaving(true)
    try { await api.post(`/innovation/radar/${item.id}/evidence`, form); setAdding(false); setForm({ signalType: 'RESEARCH', source: '', sourceUrl: '', publisher: '', summary: '', confidence: 'MEDIUM' }); load() }
    catch (e: any) { alert(e.message) } finally { setSaving(false) }
  }
  const removeSignal = async (id: string) => { try { await api.post(`/innovation/radar/evidence/${id}/delete`, {}); load() } catch (e: any) { alert(e.message) } }

  if (signals === null) return null

  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>📰 {isAR ? 'الأدلة والاتجاه' : 'Evidence & Trend'}</div>
        {trend && (
          <span style={{ ...S.badge(TREND_COLOR[trend.trendState]), marginRight: 8 }} title={trend.reason}>
            {isAR ? TREND_LABEL[trend.trendState]?.ar : TREND_LABEL[trend.trendState]?.en}
          </span>
        )}
        {isAdmin && <button style={{ ...S.btn(), padding: '4px 10px', fontSize: 12 }} onClick={() => setAdding(a => !a)}>+ {isAR ? 'أضف دليلًا' : 'Add Evidence'}</button>}
      </div>
      {trend && <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 12 }}>{trend.reason}</div>}

      {adding && (
        <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <select style={S.input} value={form.signalType} onChange={e => setForm(f => ({ ...f, signalType: e.target.value }))}>
              {SIGNAL_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select style={S.input} value={form.confidence} onChange={e => setForm(f => ({ ...f, confidence: e.target.value }))}>
              {['HIGH', 'MEDIUM', 'LOW'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <input style={S.input} placeholder={isAR ? 'المصدر (مثال: تقرير Gartner)' : 'Source (e.g. Gartner report)'} value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} />
          <input style={S.input} placeholder={isAR ? 'رابط المصدر (اختياري)' : 'Source URL (optional)'} value={form.sourceUrl} onChange={e => setForm(f => ({ ...f, sourceUrl: e.target.value }))} />
          <input style={S.input} placeholder={isAR ? 'الناشر (اختياري)' : 'Publisher (optional)'} value={form.publisher} onChange={e => setForm(f => ({ ...f, publisher: e.target.value }))} />
          <textarea style={{ ...S.input, minHeight: 60, fontFamily: 'inherit' }} placeholder={isAR ? 'ملخص الدليل' : 'Evidence summary'} value={form.summary} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} />
          <div style={S.row}>
            <button style={S.btn('primary')} onClick={addSignal} disabled={saving}>{saving ? t('innov.saving') : t('innov.save')}</button>
            <button style={S.btn()} onClick={() => setAdding(false)}>{t('innov.cancel')}</button>
          </div>
        </div>
      )}

      {signals.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{isAR ? 'لا توجد أدلة مسجَّلة بعد.' : 'No evidence recorded yet.'}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {signals.map((s: any) => (
            <div key={s.id} style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={S.badge('#3498db')}>{s.signalType}</span>
                <span style={{ fontWeight: 600 }}>{s.sourceUrl ? <a href={s.sourceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{s.source}</a> : s.source}</span>
                {s.publisher && <span style={{ color: 'var(--text-dim)' }}>({s.publisher})</span>}
                <span style={{ marginLeft: 'auto', color: 'var(--text-dim)' }}>{new Date(s.observedDate).toLocaleDateString()}</span>
                {isAdmin && <button onClick={() => removeSignal(s.id)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer' }}>✕</button>}
              </div>
              <div>{s.summary}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Pilot / Experiment Management (spec section 22) ─────────────────────────
const PILOT_OUTCOME_COLOR: Record<string, string> = { ADOPT: '#2ecc71', SCALE: '#27ae60', REASSESS: '#f1c40f', HOLD: '#e67e22', STOP: '#e74c3c' }

function RadarPilotsCard({ api, item, isAR, t, isAdmin }: any) {
  const [pilots, setPilots] = useState<any[] | null>(null)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ title: '', hypothesis: '', problemStatement: '', expectedBenefit: '' })
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => { api.get(`/innovation/radar/${item.id}/pilots`).then((d: any) => setPilots(Array.isArray(d) ? d : [])) }, [api, item])
  useEffect(() => { load() }, [load])

  const create = async () => {
    if (!form.title || !form.hypothesis) return alert(isAR ? 'العنوان والفرضية مطلوبان' : 'Title and hypothesis are required')
    setSaving(true)
    try { await api.post(`/innovation/radar/${item.id}/pilots`, form); setAdding(false); setForm({ title: '', hypothesis: '', problemStatement: '', expectedBenefit: '' }); load() }
    catch (e: any) { alert(e.message) } finally { setSaving(false) }
  }
  const setStatus = async (id: string, status: string) => { try { await api.put(`/innovation/radar/pilots/${id}`, { status }); load() } catch (e: any) { alert(e.message) } }
  const setOutcome = async (id: string, outcome: string) => { try { await api.put(`/innovation/radar/pilots/${id}`, { status: 'COMPLETED', outcome }); load() } catch (e: any) { alert(e.message) } }

  if (pilots === null) return null

  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>🧪 {isAR ? 'التجارب والمشاريع الاستطلاعية' : 'Pilots & Experiments'}</div>
        {isAdmin && <button style={{ ...S.btn(), padding: '4px 10px', fontSize: 12 }} onClick={() => setAdding(a => !a)}>+ {isAR ? 'ابدأ تجربة' : 'Start a Pilot'}</button>}
      </div>

      {adding && (
        <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12 }}>
          <input style={S.input} placeholder={isAR ? 'العنوان' : 'Title'} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          <textarea style={{ ...S.input, minHeight: 50, fontFamily: 'inherit' }} placeholder={isAR ? 'الفرضية' : 'Hypothesis'} value={form.hypothesis} onChange={e => setForm(f => ({ ...f, hypothesis: e.target.value }))} />
          <textarea style={{ ...S.input, minHeight: 50, fontFamily: 'inherit' }} placeholder={isAR ? 'بيان المشكلة (اختياري)' : 'Problem statement (optional)'} value={form.problemStatement} onChange={e => setForm(f => ({ ...f, problemStatement: e.target.value }))} />
          <input style={S.input} placeholder={isAR ? 'الفائدة المتوقعة (اختياري)' : 'Expected benefit (optional)'} value={form.expectedBenefit} onChange={e => setForm(f => ({ ...f, expectedBenefit: e.target.value }))} />
          <div style={S.row}>
            <button style={S.btn('primary')} onClick={create} disabled={saving}>{saving ? t('innov.saving') : t('innov.save')}</button>
            <button style={S.btn()} onClick={() => setAdding(false)}>{t('innov.cancel')}</button>
          </div>
        </div>
      )}

      {pilots.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{isAR ? 'لا توجد تجارب مسجَّلة بعد.' : 'No pilots recorded yet.'}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pilots.map((p: any) => (
            <div key={p.id} style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>{p.title}</span>
                <span style={S.badge(p.status === 'COMPLETED' ? '#2ecc71' : p.status === 'IN_PROGRESS' ? '#3498db' : '#7f8c8d')}>{p.status}</span>
                {p.outcome && <span style={S.badge(PILOT_OUTCOME_COLOR[p.outcome])}>{p.outcome}</span>}
              </div>
              <div style={{ color: 'var(--text-dim)', marginBottom: 6 }}>{p.hypothesis}</div>
              {isAdmin && p.status !== 'COMPLETED' && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
                  {p.status === 'PLANNED' && <button style={{ ...S.btn('primary'), padding: '3px 8px', fontSize: 11 }} onClick={() => setStatus(p.id, 'IN_PROGRESS')}>{isAR ? 'ابدأ' : 'Start'}</button>}
                  {['ADOPT', 'SCALE', 'REASSESS', 'HOLD', 'STOP'].map(o => (
                    <button key={o} style={{ ...S.btn(), padding: '3px 8px', fontSize: 11 }} onClick={() => setOutcome(p.id, o)}>{isAR ? 'أنهِ:' : 'Conclude:'} {o}</button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Value Realization (spec section 24) ─────────────────────────────────────
function RadarValueRealizationCard({ api, item, isAR, t, isAdmin }: any) {
  const [records, setRecords] = useState<any[] | null>(null)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ kpiName: '', baseline: '', target: '', actual: '' })
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => { api.get(`/innovation/radar/${item.id}/value-realization`).then((d: any) => setRecords(Array.isArray(d) ? d : [])) }, [api, item])
  useEffect(() => { load() }, [load])

  const create = async () => {
    if (!form.kpiName) return alert(isAR ? 'اسم المؤشر مطلوب' : 'KPI name is required')
    setSaving(true)
    try { await api.post(`/innovation/radar/${item.id}/value-realization`, form); setAdding(false); setForm({ kpiName: '', baseline: '', target: '', actual: '' }); load() }
    catch (e: any) { alert(e.message) } finally { setSaving(false) }
  }
  const updateActual = async (id: string, actual: string) => { try { await api.put(`/innovation/radar/value-realization/${id}`, { actual }); load() } catch (e: any) { alert(e.message) } }

  if (records === null) return null

  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>📈 {isAR ? 'تحقيق القيمة' : 'Value Realization'}</div>
        {isAdmin && <button style={{ ...S.btn(), padding: '4px 10px', fontSize: 12 }} onClick={() => setAdding(a => !a)}>+ {isAR ? 'تتبَّع مؤشرًا' : 'Track a KPI'}</button>}
      </div>

      {adding && (
        <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12 }}>
          <input style={S.input} placeholder={isAR ? 'اسم المؤشر' : 'KPI name'} value={form.kpiName} onChange={e => setForm(f => ({ ...f, kpiName: e.target.value }))} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <input style={S.input} placeholder={isAR ? 'خط الأساس' : 'Baseline'} value={form.baseline} onChange={e => setForm(f => ({ ...f, baseline: e.target.value }))} />
            <input style={S.input} placeholder={isAR ? 'الهدف' : 'Target'} value={form.target} onChange={e => setForm(f => ({ ...f, target: e.target.value }))} />
          </div>
          <div style={S.row}>
            <button style={S.btn('primary')} onClick={create} disabled={saving}>{saving ? t('innov.saving') : t('innov.save')}</button>
            <button style={S.btn()} onClick={() => setAdding(false)}>{t('innov.cancel')}</button>
          </div>
        </div>
      )}

      {records.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{isAR ? 'لا توجد مؤشرات مُتابَعة بعد.' : 'No KPIs being tracked yet.'}</div>
      ) : (
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[isAR ? 'المؤشر' : 'KPI', isAR ? 'خط الأساس' : 'Baseline', isAR ? 'الهدف' : 'Target', isAR ? 'الفعلي' : 'Actual'].map(h => (
                <th key={h} style={{ textAlign: isAR ? 'right' : 'left', padding: '6px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text-dim)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {records.map((r: any) => (
              <tr key={r.id}>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>{r.kpiName}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>{r.baseline || '—'}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>{r.target || '—'}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>
                  {isAdmin ? (
                    <input style={{ ...S.input, marginBottom: 0, padding: '3px 6px', width: 100 }} placeholder={isAR ? 'أدخل القيمة الفعلية' : 'Enter actual'} defaultValue={r.actual || ''} onBlur={e => e.target.value !== (r.actual || '') && updateActual(r.id, e.target.value)} />
                  ) : (r.actual || '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function RadarEditForm({ api, item, isAR, t, allItems, onDone, onCancel }: any) {
  const [form, setForm] = useState({
    name: item.name || '', nameAr: item.nameAr || '', description: item.description || '', descriptionAr: item.descriptionAr || '',
    category: item.category, maturity: item.maturity, marketPosition: item.marketPosition, horizon: item.horizon || '',
    typicalUseCases: (item.typicalUseCases || []).join(', '), benefits: (item.benefits || []).join(', '), keyRisks: (item.keyRisks || []).join(', '),
    relatedTechnologyIds: item.relatedTechnologyIds || [],
  })
  const [relatedSearch, setRelatedSearch] = useState('')
  const [saving, setSaving] = useState(false)

  const toggleRelated = (id: string) => {
    setForm(f => ({ ...f, relatedTechnologyIds: f.relatedTechnologyIds.includes(id) ? f.relatedTechnologyIds.filter((x: string) => x !== id) : [...f.relatedTechnologyIds, id] }))
  }

  const candidates = (allItems || []).filter((x: any) => x.id !== item.id && (isAR && x.nameAr ? x.nameAr : x.name).toLowerCase().includes(relatedSearch.toLowerCase()))

  const save = async () => {
    setSaving(true)
    try {
      await api.put(`/innovation/radar/${item.id}`, {
        ...form,
        horizon: form.horizon || null,
        typicalUseCases: form.typicalUseCases.split(',').map((s: string) => s.trim()).filter(Boolean),
        benefits: form.benefits.split(',').map((s: string) => s.trim()).filter(Boolean),
        keyRisks: form.keyRisks.split(',').map((s: string) => s.trim()).filter(Boolean),
      })
      onDone()
    } catch (e: any) { alert(e.message) } finally { setSaving(false) }
  }

  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div><div style={S.label}>{t('innov.name_en')}</div><input style={S.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
        <div><div style={S.label}>{t('innov.name_ar')}</div><input style={S.input} dir="rtl" value={form.nameAr} onChange={e => setForm(f => ({ ...f, nameAr: e.target.value }))} /></div>
        <div>
          <div style={S.label}>{t('innov.maturity')}</div>
          <select style={S.input} value={form.maturity} onChange={e => setForm(f => ({ ...f, maturity: e.target.value }))}>
            {Object.keys(MATURITY_LABEL).map(m => <option key={m} value={m}>{isAR ? MATURITY_LABEL[m].ar : MATURITY_LABEL[m].en}</option>)}
          </select>
        </div>
        <div>
          <div style={S.label}>{t('innov.market_position')}</div>
          <select style={S.input} value={form.marketPosition} onChange={e => setForm(f => ({ ...f, marketPosition: e.target.value }))}>
            {Object.keys(MARKET_POSITION_LABEL).map(p => <option key={p} value={p}>{isAR ? MARKET_POSITION_LABEL[p].ar : MARKET_POSITION_LABEL[p].en}</option>)}
          </select>
        </div>
        <div>
          <div style={S.label}>{isAR ? 'الأفق الزمني' : 'Horizon'}</div>
          <select style={S.input} value={form.horizon} onChange={e => setForm(f => ({ ...f, horizon: e.target.value }))}>
            <option value="">{isAR ? 'غير مصنَّف' : 'Not yet classified'}</option>
            {HORIZON_RINGS.map(h => <option key={h} value={h}>{isAR ? HORIZON_LABEL[h].ar : HORIZON_LABEL[h].en}</option>)}
          </select>
        </div>
      </div>
      <div style={S.label}>{t('innov.description')}</div>
      <input style={S.input} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      <div style={S.label}>{t('innov.use_cases')}</div>
      <input style={S.input} value={form.typicalUseCases} onChange={e => setForm(f => ({ ...f, typicalUseCases: e.target.value }))} />
      <div style={S.label}>{t('innov.benefits')}</div>
      <input style={S.input} value={form.benefits} onChange={e => setForm(f => ({ ...f, benefits: e.target.value }))} />
      <div style={S.label}>{t('innov.risks')}</div>
      <input style={S.input} value={form.keyRisks} onChange={e => setForm(f => ({ ...f, keyRisks: e.target.value }))} />

      <div style={S.label}>{isAR ? 'عناصر رادار ذات صلة' : 'Related Radar Items'}</div>
      {form.relatedTechnologyIds.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginBottom: 8 }}>
          {form.relatedTechnologyIds.map((id: string) => {
            const rel = (allItems || []).find((x: any) => x.id === id)
            return (
              <span key={id} style={{ ...S.badge('#8e44ad'), display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }} onClick={() => toggleRelated(id)}>
                {rel ? (isAR && rel.nameAr ? rel.nameAr : rel.name) : id} ✕
              </span>
            )
          })}
        </div>
      )}
      <input style={S.input} placeholder={isAR ? 'ابحث لإضافة عنصر ذي صلة…' : 'Search to add a related item…'} value={relatedSearch} onChange={e => setRelatedSearch(e.target.value)} />
      {relatedSearch && (
        <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 10 }}>
          {candidates.slice(0, 20).map((c: any) => (
            <div key={c.id} onClick={() => { toggleRelated(c.id); setRelatedSearch('') }}
              style={{ padding: '8px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--navy-mid)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              {allCategoryIcon(c.category)} {isAR && c.nameAr ? c.nameAr : c.name}
              {form.relatedTechnologyIds.includes(c.id) && <span style={{ marginLeft: 'auto', color: 'var(--accent)' }}>✓</span>}
            </div>
          ))}
          {candidates.length === 0 && <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-dim)' }}>{isAR ? 'لا توجد نتائج' : 'No matches'}</div>}
        </div>
      )}

      <div style={S.row}>
        <button style={S.btn('primary')} onClick={save} disabled={saving}>{saving ? t('innov.saving') : t('innov.save')}</button>
        <button style={S.btn()} onClick={onCancel}>{t('innov.cancel')}</button>
      </div>
    </div>
  )
}

// ── Favorites Tab ────────────────────────────────────────────────────────────
function FavoritesTab({ api, isAdmin, isAR, t, selected, setSelected, onCreateIdeaFrom }: any) {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/innovation/radar/favorites').then((d: any) => setItems(Array.isArray(d) ? d : [])).finally(() => setLoading(false))
  }, [api])
  useEffect(() => { load() }, [load])

  const openItem = async (id: string) => { const full = await api.get(`/innovation/radar/${id}`); setSelected(full) }
  const refreshSelected = async () => { if (selected) await openItem(selected.id) }

  if (selected) return <RadarDetail api={api} item={selected} isAdmin={isAdmin} isAR={isAR} t={t} onBack={() => { setSelected(null); load() }} onRefresh={refreshSelected} onOpenItem={openItem} onCreateIdeaFrom={onCreateIdeaFrom} />

  if (loading) return <div style={{ color: 'var(--text-dim)' }}>{isAR ? 'جارٍ التحميل…' : 'Loading…'}</div>
  if (items.length === 0) return <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-dim)', padding: 40 }}>{t('innov.no_favorites')}</div>

  return (
    <div className="stat-grid-3" style={{ alignItems: 'start' }}>
      {items.map((item: any) => <RadarCard key={item.id} item={item} isAR={isAR} t={t} onClick={() => openItem(item.id)} />)}
    </div>
  )
}

// ── Watchlist Tab (spec section 20: "make watching operationally useful") ──
function WatchlistTab({ api, isAdmin, isAR, t, userId, selected, setSelected, onCreateIdeaFrom }: any) {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/innovation/radar/watchlist').then((d: any) => setItems(Array.isArray(d) ? d : [])).finally(() => setLoading(false))
  }, [api])
  useEffect(() => { load() }, [load])

  const openItem = async (id: string) => { const full = await api.get(`/innovation/radar/${id}`); setSelected(full) }
  const refreshSelected = async () => { if (selected) await openItem(selected.id) }

  if (selected) return <RadarDetail api={api} item={selected} isAdmin={isAdmin} isAR={isAR} t={t} onBack={() => { setSelected(null); load() }} onRefresh={refreshSelected} onOpenItem={openItem} onCreateIdeaFrom={onCreateIdeaFrom} />

  if (loading) return <div style={{ color: 'var(--text-dim)' }}>{isAR ? 'جارٍ التحميل…' : 'Loading…'}</div>
  if (items.length === 0) return (
    <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-dim)', padding: 40 }}>
      {isAR ? 'لا توجد عناصر قيد المتابعة. من تفاصيل أي عنصر، فعّل "قيد المتابعة" لإضافته هنا.' : 'Nothing on your watchlist yet. From any item\u2019s detail view, check \u201cWatching\u201d to add it here.'}
    </div>
  )

  const ownerLabel = (ownerUserId: string | null) => {
    if (!ownerUserId) return isAR ? 'بلا مالك' : 'Unassigned'
    if (ownerUserId === userId) return isAR ? 'أنت' : 'You'
    return ownerUserId.slice(0, 8)
  }
  const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString(isAR ? 'ar' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {[isAR ? 'العنصر' : 'Item', isAR ? 'الرادار' : 'Radar', isAR ? 'الفئة' : 'Category', isAR ? 'موقفنا الحالي' : 'Current Position', t('innov.maturity'), isAR ? 'آخر تحديث لموقفنا' : 'Our Last Update', isAR ? 'المالك' : 'Owner'].map(h => (
              <th key={h} style={{ textAlign: isAR ? 'right' : 'left', padding: '10px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-dim)', fontWeight: 600, fontSize: 11 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item: any) => (
            <tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => openItem(item.id)}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--navy-light)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>
                {allCategoryIcon(item.category)} {isAR && item.nameAr ? item.nameAr : item.name}
                {item.tenantInterest?.isFavorite && <span style={{ marginLeft: 6 }} title={t('innov.favorite')}>⭐</span>}
              </td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>{DOMAIN_INFO[item.radarDomain]?.icon} {domainLabel(item.radarDomain, isAR)}</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>{allCategoryLabel(item.category, isAR)}</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
                <span style={S.badge(TENANT_STATUS_COLOR[item.tenantInterest?.tenantStatus] || '#7f8c8d')}>
                  {item.tenantInterest?.tenantStatus ? (isAR ? TENANT_STATUS_LABEL[item.tenantInterest.tenantStatus]?.ar : TENANT_STATUS_LABEL[item.tenantInterest.tenantStatus]?.en) : (isAR ? 'غير محدد' : 'Unset')}
                </span>
              </td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
                <span style={S.badge(MATURITY_COLOR[item.maturity] || '#7f8c8d')}>{isAR ? MATURITY_LABEL[item.maturity]?.ar : MATURITY_LABEL[item.maturity]?.en}</span>
              </td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-dim)' }}>{fmtDate(item.tenantInterest?.updatedAt)}</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-dim)' }}>{ownerLabel(item.tenantInterest?.ownerUserId)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Organization Profile Tab ─────────────────────────────────────────────────
// Read-only display only, as of this consolidation. Editing moved to
// Settings > Organization (OrganizationSettingsPage.tsx's "Organizational
// Context" card) - this was a second, disconnected UI for the exact same
// tenant-level SetupProfile record, which belongs in one place. It also
// carried a real data-corruption risk: this tab's "Domains in Scope" was a
// free-text comma list writing to the SAME `domainsInScope` column that
// Settings > Organization's Framework card manages as validated domain
// codes (used to scope Repository filters/Meta-Model) - saving here could
// silently overwrite that structured value with arbitrary text. Dropped
// entirely rather than ported; the Framework card is the one real editor.
function ProfileTab({ api, isAR, t }: any) {
  const navigate = useNavigate()
  const [profile, setProfile] = useState<any>(null)

  useEffect(() => {
    api.get('/innovation/context-profile').then((p: any) => setProfile(p))
  }, [api])

  if (!profile) return <div style={{ color: 'var(--text-dim)' }}>{isAR ? 'جارٍ التحميل…' : 'Loading…'}</div>

  const constraintsList = profile.constraints && typeof profile.constraints === 'object' ? Object.entries(profile.constraints) : []

  return (
    <div style={{ maxWidth: 700 }}>
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{t('innov.profile_title')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.6 }}>{t('innov.profile_desc')}</div>
      </div>

      <div style={S.card}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
          <div><div style={S.label}>{t('innov.industry')}</div><div>{profile.industry || '—'}</div></div>
          <div><div style={S.label}>{t('innov.org_size')}</div><div>{profile.organizationSize || '—'}</div></div>
        </div>
        <div style={S.label}>{t('innov.mandate')}</div>
        <div style={{ marginBottom: 12 }}>{profile.primaryMandate || '—'}</div>
        <div style={S.label}>{t('innov.short_desc')}</div>
        <div style={{ marginBottom: 12 }}>{profile.orgDescriptionShort || '—'}</div>
        <div style={S.label}>{t('innov.constraints')}</div>
        <div style={{ marginBottom: 16 }}>
          {constraintsList.length ? constraintsList.map(([k, v]) => <div key={k}>{k}: {String(v)}</div>) : '—'}
        </div>
        <button style={S.btn('primary')} onClick={() => navigate('/settings/organization')}>
          {isAR ? '✎ تعديل في إعدادات المنظمة' : '✎ Edit in Organization Settings'}
        </button>
      </div>
    </div>
  )
}

// ── Ideas Tab (Innovation-P2) ─────────────────────────────────────────────────
const DECISION_ROLES = ['TENANT_ADMIN', 'REVIEWER']

const STUDY_STATUS_COLOR: Record<string, string> = {
  DRAFT: '#7f8c8d', AI_RESEARCH: '#f39c12', UNDER_REVIEW: '#3498db', REWORK: '#e67e22',
  APPROVED: '#27ae60', RECOMMENDED: '#2ecc71', PILOT_INITIATIVE: '#9b59b6', IMPLEMENTED: '#16a085', CLOSED_ARCHIVED: '#7f8c8d',
}
// Mirrors apps/api/src/innovation/study.service.ts's STUDY_SECTION_DEFS (story order: summary, why, what it enables,
// how, cost and value, risks, recommendation) - keep in sync if that list changes.
const STUDY_SECTIONS: { key: string; en: string; ar: string; shape: 'text' | 'list' | 'recommendation' | 'impact' }[] = [
  { key: 'EXECUTIVE_SUMMARY', en: 'Executive Summary', ar: 'الملخص التنفيذي', shape: 'text' },
  { key: 'BUSINESS_PROBLEM', en: 'Business Problem / Opportunity', ar: 'المشكلة/الفرصة التجارية', shape: 'text' },
  { key: 'STRATEGIC_ALIGNMENT', en: 'Strategic Alignment', ar: 'التوافق الاستراتيجي', shape: 'text' },
  { key: 'CAPABILITY_IMPACT', en: 'Business Capability Impact', ar: 'أثر القدرات المؤسسية', shape: 'list' },
  { key: 'USE_CASES', en: 'Potential Use Cases', ar: 'حالات الاستخدام المحتملة', shape: 'list' },
  { key: 'ARCHITECTURE_FIT', en: 'Architecture Fit Assessment', ar: 'تقييم توافق البنية', shape: 'text' },
  // Optional - only on studies that opted in to the EA impact analysis.
  { key: 'EA_IMPACT_ANALYSIS', en: 'EA Impact Analysis', ar: 'تحليل الأثر على البنية المؤسسية', shape: 'impact' },
  { key: 'TECHNOLOGY_OPTIONS', en: 'Recommended Technology Options', ar: 'خيارات التقنية الموصى بها', shape: 'list' },
  { key: 'BENCHMARK', en: 'Product Benchmark', ar: 'المقارنة المعيارية للمنتجات', shape: 'list' },
  { key: 'COST_ESTIMATE', en: 'Cost Estimate (CAPEX / OPEX)', ar: 'تقدير التكاليف (الرأسمالية / التشغيلية)', shape: 'list' },
  { key: 'FINANCIAL_ASSESSMENT', en: 'Financial Assessment', ar: 'التقييم المالي', shape: 'text' },
  { key: 'RISKS_MITIGATION', en: 'Risks & Mitigation', ar: 'المخاطر والتخفيف', shape: 'list' },
  { key: 'RECOMMENDATION', en: 'Recommendation', ar: 'التوصية', shape: 'recommendation' },
]

// Column headings for study register sections (options, benchmark, risks, use cases, capabilities).
const STUDY_FIELD_LABELS: Record<string, { en: string; ar: string }> = {
  capability: { en: 'Capability', ar: 'القدرة' }, currentMaturity: { en: 'Current Maturity', ar: 'مستوى النضج الحالي' }, expectedImpact: { en: 'Expected Impact', ar: 'الأثر المتوقع' }, priority: { en: 'Priority', ar: 'الأولوية' },
  useCase: { en: 'Use Case', ar: 'حالة الاستخدام' }, businessProblem: { en: 'Business Problem', ar: 'المشكلة' }, targetUsers: { en: 'Target Users', ar: 'المستخدمون المستهدفون' }, expectedBenefit: { en: 'Expected Benefit', ar: 'الفائدة المتوقعة' }, complexity: { en: 'Complexity', ar: 'التعقيد' },
  option: { en: 'Option', ar: 'الخيار' }, type: { en: 'Approach', ar: 'نوع الخيار' }, products: { en: 'Named Products', ar: 'المنتجات المقترحة' }, marketRecognition: { en: 'Market Standing', ar: 'المكانة في السوق' }, rationale: { en: 'Rationale', ar: 'المبررات' }, fitScore: { en: 'Fit', ar: 'درجة التوافق' },
  costItem: { en: 'Cost Item', ar: 'بند التكلفة' }, low: { en: 'Low Estimate', ar: 'التقدير الأدنى' }, high: { en: 'High Estimate', ar: 'التقدير الأعلى' }, calculation: { en: 'Calculation', ar: 'طريقة الحساب' },
  product: { en: 'Product', ar: 'المنتج' }, functionalFit: { en: 'Functional Fit', ar: 'الملاءمة الوظيفية' }, integrationFit: { en: 'Integration', ar: 'التكامل' }, securityCompliance: { en: 'Security & Compliance', ar: 'الأمن والامتثال' }, scalability: { en: 'Scalability', ar: 'قابلية التوسع' }, costEfficiency: { en: 'Cost Efficiency', ar: 'كفاءة التكلفة' }, basis: { en: 'Basis', ar: 'الأساس' }, evidence: { en: 'Evidence', ar: 'الدليل' },
  risk: { en: 'Risk', ar: 'الخطر' }, category: { en: 'Category', ar: 'الفئة' }, likelihood: { en: 'Likelihood', ar: 'الاحتمالية' }, impact: { en: 'Impact', ar: 'الأثر' }, mitigation: { en: 'Mitigation', ar: 'إجراءات التخفيف' },
}
const STUDY_VALUE_LABELS: Record<string, { en: string; ar: string }> = {
  HIGH: { en: 'High', ar: 'عالٍ' }, MEDIUM: { en: 'Medium', ar: 'متوسط' }, LOW: { en: 'Low', ar: 'منخفض' },
  REUSE: { en: 'Reuse', ar: 'إعادة الاستخدام' }, EXTEND: { en: 'Extend', ar: 'التوسعة' }, BUILD: { en: 'Build', ar: 'البناء' }, BUY: { en: 'Buy', ar: 'الشراء' }, SAAS: { en: 'SaaS', ar: 'خدمة سحابية' }, OPEN_SOURCE: { en: 'Open Source', ar: 'مفتوح المصدر' },
  CAPEX: { en: 'CAPEX (one-time)', ar: 'رأسمالية (لمرة واحدة)' }, OPEX: { en: 'OPEX (per year)', ar: 'تشغيلية (سنوياً)' },
  EVIDENCE: { en: 'Recorded evidence', ar: 'أدلة موثقة' }, AI_QUALITATIVE: { en: 'AI qualitative', ar: 'تقييم نوعي آلي' }, MANUAL: { en: 'Reviewer assessment', ar: 'تقييم المراجع' },
}
// On these ratings High is the good outcome (green); on risk-style ratings High is the concern (red).
const STUDY_BENEFIT_FIELDS = new Set(['fitScore', 'functionalFit', 'integrationFit', 'securityCompliance', 'scalability', 'costEfficiency'])
const RATING_COLOR = { good: '#2ecc71', mid: '#f39c12', bad: '#e74c3c' }
const fieldLabel = (k: string, isAR: boolean) => STUDY_FIELD_LABELS[k]?.[isAR ? 'ar' : 'en'] || k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase())

/** Where the options' products and the benchmark's ratings come from — shown with the section so nobody reads a qualitative rating as a measurement. */
export function studySourceNote(sectionKey: string, content: any, isAR: boolean): string | null {
  if (sectionKey === 'COST_ESTIMATE') return isAR
    ? 'نطاقات تقديرية مبنية على معرفة الذكاء الاصطناعي بالسوق وافتراضات الدراسة وليست عروض أسعار — يلزم التأكيد بعروض الموردين. الإجماليات محسوبة من البنود.'
    : 'Indicative ranges from the AI’s market knowledge and the study assumptions, not quotations — confirm with vendor quotes. Totals are computed from the lines.'
  if (sectionKey === 'TECHNOLOGY_OPTIONS') return isAR
    ? 'المنتجات وتصنيفات المحللين مبنية على المعرفة العامة للذكاء الاصطناعي بالسوق — يلزم التحقق منها في أحدث تقارير المحللين قبل الشراء.'
    : 'Products and analyst standing reflect the AI’s general market knowledge — verify against current analyst reports before procurement.'
  if (sectionKey !== 'BENCHMARK' || !Array.isArray(content) || !content.length) return null
  const backed = content.filter((r: any) => r?.basis === 'EVIDENCE').length
  if (backed === content.length) return isAR ? 'جميع التقييمات مستندة إلى أدلة موثقة في رادار التقنية.' : 'Every rating cites recorded Tech Radar evidence.'
  if (backed) return isAR ? 'الصفوف الموسومة «أدلة موثقة» مستندة إلى أدلة رادار التقنية؛ والبقية تقييمات نوعية وليست قياسات.' : 'Rows marked “Recorded evidence” cite Tech Radar evidence; the rest are qualitative assessments, not measurements.'
  return isAR ? 'تقييم نوعي وليس مقارنة معيارية مقاسة: لا توجد أدلة موثقة لهذه المنتجات.' : 'Qualitative assessment, not a measured benchmark: no recorded evidence exists for these products.'
}

// Mirrors apps/api/src/innovation/study-cost-estimate.ts: totals are computed from the saved lines, never stored.
export const COST_TCO_YEARS = 3
const formatSar = (n: number, currency = 'SAR') => `${currency}\u00A0${Math.round(n).toLocaleString('en-US')}`
const costRange = (low: number, high: number, currency?: string) => (low === high ? formatSar(low, currency) : `${formatSar(low, currency)} – ${formatSar(high, currency)}`)
export function studyCostTotals(lines: any[]) {
  const valid = (lines || []).filter(l => l && (l.category === 'CAPEX' || l.category === 'OPEX') && Number.isFinite(l.low) && Number.isFinite(l.high))
  if (!valid.length) return null
  const sum = (cat: string, k: 'low' | 'high') => valid.filter(l => l.category === cat).reduce((a, l) => a + l[k], 0)
  const capex = { low: sum('CAPEX', 'low'), high: sum('CAPEX', 'high') }
  const opex = { low: sum('OPEX', 'low'), high: sum('OPEX', 'high') }
  return { currency: valid[0].currency || 'SAR', capex, opex, tco: { low: capex.low + COST_TCO_YEARS * opex.low, high: capex.high + COST_TCO_YEARS * opex.high } }
}

/** CAPEX / OPEX estimate: headline totals, then every line with its calculation and rationale. */
export function StudyCostEstimate({ lines, isAR }: { lines: any[]; isAR: boolean }) {
  const totals = studyCostTotals(lines)
  const tiles = totals ? [
    { key: 'capex', label: isAR ? 'إجمالي التكاليف الرأسمالية (لمرة واحدة)' : 'Total CAPEX (one-time)', r: totals.capex, color: '#3498db' },
    { key: 'opex', label: isAR ? 'إجمالي التكاليف التشغيلية (سنوياً)' : 'Total OPEX (per year)', r: totals.opex, color: '#9b59b6' },
    { key: 'tco', label: isAR ? `إجمالي تكلفة الملكية لمدة ${COST_TCO_YEARS} سنوات` : `${COST_TCO_YEARS}-year total cost of ownership`, r: totals.tco, color: '#f39c12' },
  ] : []
  const rows = (lines || []).map(l => ({ costItem: l.costItem, category: l.category, low: Number.isFinite(l.low) ? formatSar(l.low, l.currency) : l.low, high: Number.isFinite(l.high) ? formatSar(l.high, l.currency) : l.high, calculation: l.calculation, rationale: l.rationale }))
  return (
    <div>
      {totals && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
          {tiles.map(t => (
            <div key={t.key} data-testid={`cost-total-${t.key}`} style={{ background: 'var(--navy)', border: '1px solid var(--border)', borderTop: `3px solid ${t.color}`, borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>{t.label}</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{costRange(t.r.low, t.r.high, totals.currency)}</div>
            </div>
          ))}
        </div>
      )}
      <StudyRegisterTable rows={rows} isAR={isAR} numeric={['low', 'high']} />
    </div>
  )
}

const EA_IMPACT_HELP = {
  en: 'Optional. The AI also assesses the study against every domain of your EA Repository (Current architecture) and lists, per domain, only the objects that are or might be impacted, with a description of the impact. Where one of your EA Views (saved or from the View Library) illustrates a domain’s impact, the AI chooses it and embeds its picture with the impacted objects highlighted. It takes longer and uses more AI credits. You can switch it on or off later from the study.',
  ar: 'اختياري. يقيّم الذكاء الاصطناعي الدراسة أيضًا مقابل كل مجال في مستودع البنية المؤسسية (البنية الحالية)، ويسرد لكل مجال العناصر المتأثرة أو المحتمل تأثرها فقط مع وصف الأثر. وإذا وُجد عرض للبنية المؤسسية (محفوظ أو من مكتبة العروض) يوضح أثر المجال، يختاره الذكاء الاصطناعي ويضمّن صورته مع تمييز العناصر المتأثرة. يستغرق وقتًا أطول ويستهلك أرصدة ذكاء اصطناعي إضافية. يمكنك تفعيله أو إيقافه لاحقًا من الدراسة.',
}
// Mirrors apps/api/src/innovation/study-ea-impact.ts (domains, impact types) - keep in sync if that list changes.
const EA_IMPACT_DOMAIN_LABEL: Record<string, { en: string; ar: string }> = {
  BUSINESS_ARCHITECTURE: { en: 'Business Architecture', ar: 'معمارية الأعمال' },
  BENEFICIARY_EXPERIENCE: { en: 'Beneficiary Experience', ar: 'تجربة المستفيد' },
  APPLICATION_INTEGRATION: { en: 'Applications & Integration', ar: 'التطبيقات والتكامل' },
  DATA_ARCHITECTURE: { en: 'Data Architecture', ar: 'معمارية البيانات' },
  INFRASTRUCTURE: { en: 'Infrastructure & Technology', ar: 'البنية التحتية والتقنية' },
  SECURITY_ARCHITECTURE: { en: 'Security Architecture', ar: 'معمارية الأمن' },
  STRATEGY_LAYER: { en: 'Strategy Layer', ar: 'الطبقة الاستراتيجية' },
  UNCLASSIFIED: { en: 'Other / Unclassified', ar: 'أخرى / غير مصنفة' },
}
const EA_IMPACT_TYPE_LABEL: Record<string, { en: string; ar: string }> = {
  ENHANCE: { en: 'Enhance', ar: 'تعزيز' }, MODIFY: { en: 'Modify', ar: 'تعديل' }, INTEGRATE: { en: 'Integrate', ar: 'تكامل' },
  REPLACE: { en: 'Replace', ar: 'استبدال' }, RETIRE: { en: 'Retire', ar: 'إيقاف' }, DEPENDENCY: { en: 'Dependency', ar: 'اعتمادية' },
}
const EA_IMPACT_LEVEL_COLOR: Record<string, string> = { HIGH: RATING_COLOR.bad, MEDIUM: RATING_COLOR.mid, LOW: '#3498db', NONE: '#64748B' }

/** One domain's EA View picture, chosen by the AI to illustrate the impact; impacted objects are highlighted in it. */
function StudyImpactViewFigure({ view, isAR }: { view: any; isAR: boolean }) {
  // An <img> never runs markup from the picture; the SVG is generated server-side with every name escaped.
  const src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(view.image.svg)}`
  const source = view.source === 'VIEW_LIBRARY' ? (isAR ? 'مكتبة العروض' : 'View Library') : (isAR ? 'عرض محفوظ' : 'Saved view')
  return (
    <figure data-testid="study-impact-view" style={{ margin: '0 0 10px', border: '1px solid var(--border)', borderRadius: 8, padding: 10, background: 'var(--navy)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const, marginBottom: 8, fontSize: 12 }}>
        <span style={{ fontWeight: 700 }}>🗺 {view.title}</span>
        <span style={{ color: 'var(--text-dim)' }}>{source} · {view.visualization}</span>
      </div>
      <div style={{ overflowX: 'auto' as const, background: '#ffffff', borderRadius: 6 }}>
        <img src={src} alt={isAR ? `عرض البنية المؤسسية: ${view.title}` : `EA View: ${view.title}`} style={{ display: 'block', maxWidth: '100%', height: 'auto' }} />
      </div>
      <figcaption style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6, lineHeight: 1.5 }}>
        {view.reason ? `${view.reason} ` : ''}{isAR ? `تم تمييز ${view.impactedShown} من العناصر المتأثرة.` : `${view.impactedShown} impacted object(s) highlighted.`}
      </figcaption>
    </figure>
  )
}

/** EA impact analysis: headline counts, then every domain - impacted ones with their objects, the rest in one line each. */
export function StudyEaImpact({ content, isAR }: { content: any; isAR: boolean }) {
  const L = (en: string, ar: string) => (isAR ? ar : en)
  const domains: any[] = Array.isArray(content?.domains) ? content.domains : []
  const totals = content?.totals || {}
  const domainName = (d: any) => EA_IMPACT_DOMAIN_LABEL[d.domain]?.[isAR ? 'ar' : 'en'] || d.domainName || d.domain
  const levelBadge = (level: string) => {
    const label = level === 'NONE' ? L('No impact', 'لا أثر') : STUDY_VALUE_LABELS[level]?.[isAR ? 'ar' : 'en'] || level
    return <span data-rating={level} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, color: EA_IMPACT_LEVEL_COLOR[level] || '#64748B', background: `${EA_IMPACT_LEVEL_COLOR[level] || '#64748B'}22`, whiteSpace: 'nowrap' as const }}>{label}</span>
  }
  const impacted = domains.filter(d => d.status === 'ASSESSED' && (d.impactedObjects || []).length > 0)
  const quiet = domains.filter(d => !impacted.includes(d))
  const th = { textAlign: isAR ? 'right' as const : 'left' as const, padding: '8px 10px', background: 'var(--navy)', color: 'var(--text-dim)', fontWeight: 700, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' as const }
  const td = { padding: '8px 10px', verticalAlign: 'top' as const, lineHeight: 1.5 }
  const tiles = [
    { key: 'objects', label: L('Impacted objects', 'العناصر المتأثرة'), value: totals.impactedObjects ?? 0, color: RATING_COLOR.bad },
    { key: 'domains', label: L('Impacted domains', 'المجالات المتأثرة'), value: `${totals.impactedDomains ?? 0} / ${domains.filter(d => d.status !== 'NO_OBJECTS').length}`, color: RATING_COLOR.mid },
    { key: 'repository', label: L('Objects assessed (Current architecture)', 'العناصر المقيَّمة (البنية الحالية)'), value: totals.assessedObjects ?? 0, color: '#3498db' },
  ]
  return (
    <div data-testid="study-ea-impact">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 12 }}>
        {tiles.map(tile => (
          <div key={tile.key} data-testid={`ea-impact-total-${tile.key}`} style={{ background: 'var(--navy)', border: '1px solid var(--border)', borderTop: `3px solid ${tile.color}`, borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>{tile.label}</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{tile.value}</div>
          </div>
        ))}
      </div>
      {impacted.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>{L('No Repository object was identified as impacted.', 'لم يُحدَّد أي عنصر متأثر في المستودع.')}</div>}
      {impacted.map(d => (
        <div key={d.domain} data-testid={`ea-impact-domain-${d.domain}`} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const, marginBottom: 6 }}>
            <div style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>{domainName(d)}</div>
            {levelBadge(d.impactLevel)}
            <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{isAR ? `${d.impactedObjects.length} من ${d.objectCount} عنصر متأثر` : `${d.impactedObjects.length} of ${d.objectCount} objects impacted`}</span>
          </div>
          {d.summary && <div style={{ fontSize: 12, lineHeight: 1.6, marginBottom: 8 }}>{d.summary}</div>}
          {d.view?.image?.svg && <StudyImpactViewFigure view={d.view} isAR={isAR} />}
          {d.assessedCount < d.objectCount && <div style={{ fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic' as const, marginBottom: 8 }}>{isAR ? `قُيّم ${d.assessedCount} من ${d.objectCount} عنصرًا (الأكثر صلة أولًا).` : `${d.assessedCount} of ${d.objectCount} objects assessed (most relevant first).`}</div>}
          <div style={{ overflowX: 'auto' as const, WebkitOverflowScrolling: 'touch' as any }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 12, minWidth: 640 }}>
              <thead><tr>
                <th scope="col" style={th}>{L('Object', 'العنصر')}</th>
                <th scope="col" style={th}>{L('Type', 'النوع')}</th>
                <th scope="col" style={th}>{L('Impact Type', 'نوع الأثر')}</th>
                <th scope="col" style={th}>{L('Level', 'المستوى')}</th>
                <th scope="col" style={th}>{L('Impact Description', 'وصف الأثر')}</th>
              </tr></thead>
              <tbody>
                {d.impactedObjects.map((o: any) => (
                  <tr key={o.assetId} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ ...td, fontWeight: 600 }}>{o.name}</td>
                    <td style={td}>{o.typeLabel || o.assetType}</td>
                    <td style={td}>
                      {EA_IMPACT_TYPE_LABEL[o.impactType]?.[isAR ? 'ar' : 'en'] || o.impactType}
                      {o.nature === 'INDIRECT' && <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>{L('Indirect (possible)', 'غير مباشر (محتمل)')}</div>}
                    </td>
                    <td style={td}>{levelBadge(o.impactLevel)}</td>
                    <td style={td}>{o.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {quiet.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>{L('Other domains', 'المجالات الأخرى')}</div>
          {quiet.map(d => (
            <div key={d.domain} data-testid={`ea-impact-domain-${d.domain}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' as const, fontSize: 12, padding: '4px 0' }}>
              <span style={{ fontWeight: 600, minWidth: 180 }}>{domainName(d)}</span>
              <span style={{ color: d.status === 'FAILED' ? RATING_COLOR.bad : 'var(--text-dim)', flex: 1, minWidth: 200 }}>
                {d.status === 'NO_OBJECTS' ? L('No objects recorded in this domain.', 'لا توجد عناصر مسجلة في هذا المجال.')
                  : d.status === 'FAILED' ? L('Not assessed — run the analysis again.', 'لم يُقيَّم — أعد تشغيل التحليل.')
                  : (d.summary || L('No impact identified.', 'لم يُحدَّد أثر.'))}
              </span>
            </div>
          ))}
        </div>
      )}
      <div data-testid="study-source-note" style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8, fontStyle: 'italic' as const }}>
        {L('Assessed by AI against your EA Repository (Current architecture). Only objects that are or might be impacted are listed; every object is a real Repository object. Review before relying on it.',
          'تقييم آلي مقابل مستودع البنية المؤسسية (البنية الحالية). تُدرج فقط العناصر المتأثرة أو المحتمل تأثرها، وجميعها عناصر حقيقية في المستودع. يُرجى المراجعة قبل الاعتماد عليه.')}
      </div>
    </div>
  )
}

/** A study register (a list of flat records) as one labelled table; scrolls sideways on narrow screens instead of squeezing. */
export function StudyRegisterTable({ rows, isAR, numeric = [] }: { rows: any[]; isAR: boolean; numeric?: string[] }) {
  const records = rows.filter(r => r && typeof r === 'object' && !Array.isArray(r))
  const columns: string[] = []
  for (const r of records) for (const [k, v] of Object.entries(r)) if (v !== null && v !== undefined && String(v).trim() && !columns.includes(k)) columns.push(k)
  const cell = (k: string, v: any) => {
    if (v === null || v === undefined || !String(v).trim()) return null
    const text = typeof v === 'object' ? JSON.stringify(v) : String(v)
    if (/^(HIGH|MEDIUM|LOW)$/.test(text)) {
      const good = STUDY_BENEFIT_FIELDS.has(k)
      const color = text === 'MEDIUM' ? RATING_COLOR.mid : (text === 'HIGH') === good ? RATING_COLOR.good : RATING_COLOR.bad
      return <span data-rating={text} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, color, background: `${color}22`, whiteSpace: 'nowrap' as const }}>{STUDY_VALUE_LABELS[text][isAR ? 'ar' : 'en']}</span>
    }
    if (k === 'products') return <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 4 }}>{text.split(/;\s*/).filter(Boolean).map(p => <span key={p} style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: 'var(--navy)', border: '1px solid var(--border)', whiteSpace: 'nowrap' as const }}>{p}</span>)}</div>
    return STUDY_VALUE_LABELS[text]?.[isAR ? 'ar' : 'en'] || text
  }
  return (
    <div style={{ overflowX: 'auto' as const, WebkitOverflowScrolling: 'touch' as any }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 12, minWidth: Math.min(columns.length * 120, 900) }}>
        <thead>
          <tr>{columns.map(k => <th key={k} scope="col" style={{ textAlign: isAR ? 'right' as const : 'left' as const, padding: '8px 10px', background: 'var(--navy)', color: 'var(--text-dim)', fontWeight: 700, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' as const }}>{fieldLabel(k, isAR)}</th>)}</tr>
        </thead>
        <tbody>
          {records.map((r, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
              {columns.map((k, c) => <td key={k} style={{ padding: '8px 10px', verticalAlign: 'top' as const, lineHeight: 1.5, fontWeight: c === 0 ? 600 : 400, ...(numeric.includes(k) ? { textAlign: isAR ? 'left' as const : 'right' as const, whiteSpace: 'nowrap' as const } : {}) }}>{cell(k, r[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ScoreRing({ score, label }: { score: number | null; label: string }) {
  const color = score == null ? '#7f8c8d' : score >= 70 ? '#2ecc71' : score >= 50 ? '#f39c12' : '#e74c3c'
  return (
    <div style={{ textAlign: 'center' as const }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', border: `3px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, margin: '0 auto' }}>
        {score == null ? '—' : score}
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>{label}</div>
    </div>
  )
}

const IDEA_POLL_MS = 5000
type IdeaView = 'list' | 'matrix'

/** Query string for the ideas list (only the filters that are set). */
export function ideasQuery(f: { status?: string; category?: string; quadrant?: string; recommendation?: string; search?: string; sort?: string; mine?: boolean }) {
  const p = new URLSearchParams()
  if (f.status) p.set('status', f.status)
  if (f.category) p.set('category', f.category)
  if (f.quadrant) p.set('quadrant', f.quadrant)
  if (f.recommendation) p.set('recommendation', f.recommendation)
  if (f.search && f.search.trim()) p.set('search', f.search.trim())
  if (f.sort && f.sort !== 'score') p.set('sort', f.sort)
  if (f.mine) p.set('mine', 'true')
  const q = p.toString()
  return `/innovation/ideas${q ? `?${q}` : ''}`
}

function IdeasTab({ api, isAR, t, userRole, seed, onSeedConsumed, onStartStudy }: any) {
  const [ideas, setIdeas] = useState<any[]>([])
  const [radarItems, setRadarItems] = useState<any[]>([])
  const [filters, setFilters] = useState({ status: '', category: '', quadrant: '', recommendation: '', search: '', sort: 'score', mine: false })
  const [searchDraft, setSearchDraft] = useState('')
  const [view, setView] = useState<IdeaView>('list')
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    api.get(ideasQuery(filters)).then((d: any) => setIdeas(Array.isArray(d) ? d : [])).catch(() => setIdeas([])).finally(() => setLoading(false))
  }, [api, filters])
  useEffect(() => { load() }, [load])
  useEffect(() => { api.get('/innovation/radar').then((d: any) => setRadarItems(Array.isArray(d) ? d : [])).catch(() => {}) }, [api])
  // Search applies after a short pause in typing, not on every keystroke.
  useEffect(() => {
    const h = setTimeout(() => setFilters(f => (f.search === searchDraft ? f : { ...f, search: searchDraft })), 350)
    return () => clearTimeout(h)
  }, [searchDraft])
  // Opportunity Discovery (spec section 16): "Create Idea from This" on a
  // radar item's detail view lands here already carrying that context.
  useEffect(() => { if (seed) { setCreating(true) } }, [seed])

  const endorse = async (idea: any) => {
    try {
      const res = idea.endorsedByMe ? await api.del(`/innovation/ideas/${idea.id}/endorse`) : await api.post(`/innovation/ideas/${idea.id}/endorse`)
      setIdeas(list => list.map(i => (i.id === idea.id ? { ...i, ...res } : i)))
    } catch (e: any) { alert(e.message) }
  }

  if (selectedId) return <IdeaDetail api={api} ideaId={selectedId} isAR={isAR} t={t} userRole={userRole} radarItems={radarItems} onOpenIdea={setSelectedId} onBack={() => { setSelectedId(null); load() }} onStartStudy={onStartStudy} />

  const stats = {
    total: ideas.length,
    awaiting: ideas.filter(i => i.status === 'SUBMITTED' && !i.qualifiedAt).length,
    quickWins: ideas.filter(i => i.priorityQuadrant === 'QUICK_WIN').length,
    inReview: ideas.filter(i => i.status === 'QUALIFIED' || i.status === 'IN_REVIEW').length,
    approved: ideas.filter(i => i.status === 'APPROVED').length,
  }
  const set = (k: string, v: any) => setFilters(f => ({ ...f, [k]: v }))
  const matrixPoints = ideas.filter(i => i.valueScore != null && i.easeScore != null).map(i => ({ id: i.id, title: isAR && i.titleAr ? i.titleAr : i.title, value: i.valueScore, ease: i.easeScore }))

  return (
    <div>
      <div className="stat-grid-5" style={{ marginBottom: 16 }}>
        {[
          ['total', stats.total, 'var(--text)'], ['awaiting', stats.awaiting, '#7f8c8d'], ['quick_wins', stats.quickWins, '#27ae60'],
          ['in_pipeline', stats.inReview, '#3498db'], ['approved', stats.approved, '#2ecc71'],
        ].map(([k, n, c]: any) => (
          <div key={k} style={{ ...S.card, padding: 14 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: c }}>{n}</div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{t(`innov.ideas_stat_${k}`)}</div>
          </div>
        ))}
      </div>

      <div className="idea-filters" style={{ marginBottom: 10 }}>
        <input aria-label={t('innov.ideas_search')} placeholder={t('innov.ideas_search')} style={{ ...S.input, marginBottom: 0 }} value={searchDraft} onChange={e => setSearchDraft(e.target.value)} />
        <select aria-label={t('innov.all_statuses')} style={{ ...S.input, marginBottom: 0 }} value={filters.status} onChange={e => set('status', e.target.value)}>
          <option value="">{t('innov.all_statuses')}</option>
          {Object.keys(IDEA_STATUS_LABEL).map(s => <option key={s} value={s}>{label(IDEA_STATUS_LABEL, s, isAR)}</option>)}
        </select>
        <select aria-label={t('innov.filter_category')} style={{ ...S.input, marginBottom: 0 }} value={filters.category} onChange={e => set('category', e.target.value)}>
          <option value="">{t('innov.ideas_all_categories')}</option>
          {Object.keys(CATEGORIES).map(c => <option key={c} value={c}>{categoryLabel(c, isAR)}</option>)}
        </select>
        <select aria-label={t('innov.ideas_priority')} style={{ ...S.input, marginBottom: 0 }} value={filters.quadrant} onChange={e => set('quadrant', e.target.value)}>
          <option value="">{t('innov.ideas_all_priorities')}</option>
          {Object.keys(QUADRANT).map(q => <option key={q} value={q}>{label(QUADRANT, q, isAR)}</option>)}
        </select>
        <select aria-label={t('innov.ideas_recommendation')} style={{ ...S.input, marginBottom: 0 }} value={filters.recommendation} onChange={e => set('recommendation', e.target.value)}>
          <option value="">{t('innov.ideas_all_recommendations')}</option>
          {Object.keys(RECOMMENDATION).map(r => <option key={r} value={r}>{label(RECOMMENDATION, r, isAR)}</option>)}
        </select>
        <select aria-label={t('innov.ideas_sort')} style={{ ...S.input, marginBottom: 0 }} value={filters.sort} onChange={e => set('sort', e.target.value)}>
          {['score', 'value', 'ease', 'endorsed', 'newest'].map(s => <option key={s} value={s}>{t(`innov.ideas_sort_${s}`)}</option>)}
        </select>
      </div>
      <div style={{ ...S.row, marginBottom: 16, flexWrap: 'wrap' as const }}>
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={filters.mine} onChange={e => set('mine', e.target.checked)} /> {t('innov.ideas_mine')}
        </label>
        <div role="group" aria-label={t('innov.ideas_view')} style={{ display: 'flex', gap: 4 }}>
          <button style={{ ...S.btn(view === 'list' ? 'primary' : 'secondary'), padding: '5px 12px' }} aria-pressed={view === 'list'} onClick={() => setView('list')}>☰ {t('innov.ideas_view_list')}</button>
          <button style={{ ...S.btn(view === 'matrix' ? 'primary' : 'secondary'), padding: '5px 12px' }} aria-pressed={view === 'matrix'} onClick={() => setView('matrix')}>▦ {t('innov.ideas_view_matrix')}</button>
        </div>
        <HelpTip text={t('innov.ideas_help')} />
        <div style={{ flex: 1 }} />
        <button style={S.btn('primary')} onClick={() => setCreating(true)}>{t('innov.submit_idea')}</button>
      </div>

      {creating && <IdeaForm api={api} isAR={isAR} t={t} radarItems={radarItems} initial={seed} onDone={(created: any) => { setCreating(false); onSeedConsumed?.(); if (created?.id) setSelectedId(created.id); else load() }} onCancel={() => { setCreating(false); onSeedConsumed?.() }} />}

      {loading ? (
        <div style={{ color: 'var(--text-dim)' }}>{isAR ? 'جارٍ التحميل…' : 'Loading…'}</div>
      ) : ideas.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-dim)', padding: 40 }}>{t('innov.no_ideas')}</div>
      ) : view === 'matrix' ? (
        <div style={S.card}>
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>{t('innov.ideas_matrix_hint')}</div>
          {matrixPoints.length ? <IdeaMatrix points={matrixPoints} isAR={isAR} onSelect={setSelectedId} /> : <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>{t('innov.ideas_matrix_empty')}</div>}
          {ideas.length > matrixPoints.length && <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8 }}>{ideas.length - matrixPoints.length} {t('innov.ideas_matrix_unassessed')}</div>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ideas.map((idea: any) => {
            const rec = RECOMMENDATION[idea.aiRecommendation]
            const quad = QUADRANT[idea.priorityQuadrant]
            return (
              <div key={idea.id} data-testid="idea-row" style={{ ...S.card, padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' as const }} onClick={() => setSelectedId(idea.id)}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{isAR && idea.titleAr ? idea.titleAr : idea.title}</div>
                  {idea.assessmentSummary && <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }}>{pickBi(idea.assessmentSummary, isAR)}</div>}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginTop: 6, alignItems: 'center' }}>
                    {idea.category && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{categoryLabel(idea.category, isAR)}</span>}
                    {quad && <span style={S.badge(quad.color)}>{isAR ? quad.ar : quad.en}</span>}
                    {rec && <span style={S.badge(rec.color)}>{rec.icon} {isAR ? rec.ar : rec.en}</span>}
                    {idea.commentCount > 0 && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>💬 {idea.commentCount}</span>}
                  </div>
                </div>
                <button type="button" aria-pressed={!!idea.endorsedByMe} aria-label={idea.endorsedByMe ? t('innov.idea_withdraw_endorse') : t('innov.idea_endorse')}
                  onClick={e => { e.stopPropagation(); endorse(idea) }}
                  style={{ ...S.btn(idea.endorsedByMe ? 'primary' : 'secondary'), padding: '4px 10px', fontSize: 12 }}>👍 {idea.endorsementCount ?? 0}</button>
                {idea.overallScore != null && <div style={{ fontSize: 18, fontWeight: 700, color: scoreColor(idea.overallScore), minWidth: 30, textAlign: 'center' as const }} title={t('innov.overall')}>{idea.overallScore}</div>}
                <span style={S.badge(IDEA_STATUS_COLOR[idea.status])}>{label(IDEA_STATUS_LABEL, idea.status, isAR)}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/** Create or edit an idea. Problem-first intake: what problem, for whom, what benefit. */
function IdeaForm({ api, isAR, t, radarItems, initial, idea, onDone, onCancel }: any) {
  const editing = !!idea
  const [form, setForm] = useState({
    title: idea?.title || initial?.title || '', titleAr: idea?.titleAr || '',
    description: idea?.description || initial?.description || '', descriptionAr: idea?.descriptionAr || '',
    problemStatement: idea?.problemStatement || '', expectedBenefits: idea?.expectedBenefits || '', targetBeneficiaries: idea?.targetBeneficiaries || '',
    category: idea?.category || '', tags: (idea?.tags || []).join(', '), relatedRadarItemId: idea?.relatedRadarItemId || initial?.relatedRadarItemId || '',
  })
  const [saving, setSaving] = useState(false)
  const f = (k: string) => (e: any) => setForm(prev => ({ ...prev, [k]: e.target.value }))
  const id = (k: string) => `idea-form-${k}`

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) return alert(isAR ? 'العنوان والوصف مطلوبان' : 'Title and description are required')
    setSaving(true)
    try {
      const body = { ...form, tags: form.tags.split(',').map((s: string) => s.trim()).filter(Boolean), relatedRadarItemId: form.relatedRadarItemId || undefined, category: form.category || undefined }
      const saved = editing ? await api.put(`/innovation/ideas/${idea.id}`, body) : await api.post('/innovation/ideas', body)
      onDone(saved)
    } catch (e: any) { alert(e.message) } finally { setSaving(false) }
  }

  const area = { ...S.input, minHeight: 64, resize: 'vertical' as const, fontFamily: 'inherit' }
  return (
    <div style={{ ...S.card, marginBottom: 16 }} data-testid="idea-form">
      <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 12, display: 'flex', alignItems: 'center' }}>
        {t('innov.idea_form_hint')}<HelpTip text={t('innov.idea_form_help')} />
      </div>
      <div className="grid-2">
        <div><label htmlFor={id('title')} style={S.label}>{t('innov.idea_title')} *</label><input id={id('title')} style={S.input} value={form.title} onChange={f('title')} /></div>
        <div><label htmlFor={id('titleAr')} style={S.label}>{t('innov.idea_title_ar')}</label><input id={id('titleAr')} style={S.input} dir="rtl" value={form.titleAr} onChange={f('titleAr')} /></div>
      </div>
      <label htmlFor={id('problem')} style={S.label}>{t('innov.idea_problem')}</label>
      <textarea id={id('problem')} style={area} value={form.problemStatement} onChange={f('problemStatement')} placeholder={t('innov.idea_problem_ph')} />
      <label htmlFor={id('description')} style={S.label}>{t('innov.idea_description')} *</label>
      <textarea id={id('description')} style={{ ...area, minHeight: 80 }} value={form.description} onChange={f('description')} placeholder={t('innov.idea_description_ph')} />
      <div className="grid-2">
        <div><label htmlFor={id('benefits')} style={S.label}>{t('innov.idea_benefits')}</label><textarea id={id('benefits')} style={area} value={form.expectedBenefits} onChange={f('expectedBenefits')} placeholder={t('innov.idea_benefits_ph')} /></div>
        <div><label htmlFor={id('beneficiaries')} style={S.label}>{t('innov.idea_beneficiaries')}</label><textarea id={id('beneficiaries')} style={area} value={form.targetBeneficiaries} onChange={f('targetBeneficiaries')} placeholder={t('innov.idea_beneficiaries_ph')} /></div>
      </div>
      <label htmlFor={id('descriptionAr')} style={S.label}>{t('innov.idea_description_ar')}</label>
      <textarea id={id('descriptionAr')} style={area} dir="rtl" value={form.descriptionAr} onChange={f('descriptionAr')} />
      <div className="grid-2">
        <div>
          <label htmlFor={id('category')} style={S.label}>{t('innov.filter_category')}</label>
          <select id={id('category')} style={S.input} value={form.category} onChange={f('category')}>
            <option value="">—</option>
            {Object.keys(CATEGORIES).map(c => <option key={c} value={c}>{categoryLabel(c, isAR)}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={id('related')} style={S.label}>{t('innov.related_tech')}</label>
          <select id={id('related')} style={S.input} value={form.relatedRadarItemId} onChange={f('relatedRadarItemId')}>
            <option value="">{t('innov.none')}</option>
            {radarItems.map((r: any) => <option key={r.id} value={r.id}>{isAR && r.nameAr ? r.nameAr : r.name}</option>)}
          </select>
        </div>
      </div>
      <label htmlFor={id('tags')} style={S.label}>{t('innov.tags')}</label>
      <input id={id('tags')} style={S.input} value={form.tags} onChange={f('tags')} />
      <div style={S.row}>
        <button style={S.btn('primary')} onClick={save} disabled={saving}>{saving ? t('innov.saving') : editing ? t('innov.idea_save_changes') : t('innov.submit')}</button>
        <button style={S.btn()} onClick={onCancel}>{t('innov.cancel')}</button>
      </div>
    </div>
  )
}

function IdeaDetail({ api, ideaId, isAR, t, userRole, radarItems, onBack, onOpenIdea, onStartStudy }: any) {
  const [idea, setIdea] = useState<any>(null)
  const [loadError, setLoadError] = useState(false)
  const [starting, setStarting] = useState(false)
  const [decisionNotes, setDecisionNotes] = useState('')
  const [transitioning, setTransitioning] = useState(false)
  const [editing, setEditing] = useState(false)
  const [comment, setComment] = useState('')
  const [posting, setPosting] = useState(false)
  const canDecide = DECISION_ROLES.includes(userRole)

  const load = useCallback(async () => {
    try { const d = await api.get(`/innovation/ideas/${ideaId}`); if (d && d.id) { setIdea(d); setLoadError(false) } else setLoadError(true) }
    catch { setLoadError(true) }
  }, [api, ideaId])
  useEffect(() => { setIdea(null); load() }, [load])

  // The assessment runs in the background; follow it until it completes.
  const assessing = idea?.status === 'QUALIFYING'
  useEffect(() => {
    if (!assessing) return
    const timer = setTimeout(load, IDEA_POLL_MS)
    return () => clearTimeout(timer)
  }, [assessing, idea, load])

  if (loadError) return <div><button style={{ ...S.btn(), padding: '6px 12px' }} onClick={onBack}>{t('innov.back_to_ideas')}</button><div style={{ ...S.card, marginTop: 16, color: 'var(--text-dim)' }}>{t('innov.idea_not_found')}</div></div>
  if (!idea) return <div style={{ color: 'var(--text-dim)' }}>{isAR ? 'جارٍ التحميل…' : 'Loading…'}</div>

  const assess = async () => {
    setStarting(true)
    try { const marked = await api.post(`/innovation/ideas/${idea.id}/qualify`); setIdea((i: any) => ({ ...i, ...marked })) }
    catch (e: any) { alert(e.message); await load() }
    finally { setStarting(false) }
  }
  const moveTo = async (status: string) => {
    setTransitioning(true)
    try { await api.put(`/innovation/ideas/${idea.id}/status`, { status, decisionNotes: decisionNotes || undefined }); await load() }
    catch (e: any) { alert(e.message) } finally { setTransitioning(false) }
  }
  const endorse = async () => {
    try { const res = idea.endorsedByMe ? await api.del(`/innovation/ideas/${idea.id}/endorse`) : await api.post(`/innovation/ideas/${idea.id}/endorse`); setIdea((i: any) => ({ ...i, ...res })) }
    catch (e: any) { alert(e.message) }
  }
  const adjust = async (key: string, score: number | null, note?: string) => {
    await api.put(`/innovation/ideas/${idea.id}/assessment/criteria/${key}`, { score, note })
    await load()
  }
  const postComment = async () => {
    if (!comment.trim()) return
    setPosting(true)
    try { await api.post(`/innovation/ideas/${idea.id}/comments`, { body: comment.trim() }); setComment(''); await load() }
    catch (e: any) { alert(e.message) } finally { setPosting(false) }
  }
  const deleteComment = async (commentId: string) => {
    try { await api.del(`/innovation/ideas/${idea.id}/comments/${commentId}`); await load() } catch (e: any) { alert(e.message) }
  }

  const title = isAR && idea.titleAr ? idea.titleAr : idea.title
  const description = isAR && idea.descriptionAr ? idea.descriptionAr : idea.description
  const relatedTech = idea.relatedRadarItemId ? radarItems.find((r: any) => r.id === idea.relatedRadarItemId) : null
  const hasAssessment = idea.assessment && Array.isArray(idea.assessment.criteria)
  const rec = RECOMMENDATION[idea.aiRecommendation]
  const person = (p: any) => (p ? (isAR && p.nameAr ? p.nameAr : p.name) : '—')

  if (editing) return <IdeaForm api={api} isAR={isAR} t={t} radarItems={radarItems} idea={idea} onDone={async () => { setEditing(false); await load() }} onCancel={() => setEditing(false)} />

  return (
    <div data-testid="idea-detail">
      <div style={{ ...S.row, marginBottom: 16, flexWrap: 'wrap' as const }}>
        <button style={{ ...S.btn(), padding: '6px 12px' }} onClick={onBack}>{t('innov.back_to_ideas')}</button>
        <div style={{ flex: 1, fontSize: 18, fontWeight: 700, minWidth: 200 }}>{title}</div>
        <button type="button" aria-pressed={!!idea.endorsedByMe} style={{ ...S.btn(idea.endorsedByMe ? 'primary' : 'secondary'), padding: '5px 12px' }} onClick={endorse}>
          👍 {idea.endorsedByMe ? t('innov.idea_endorsed') : t('innov.idea_endorse')} · {idea.endorsementCount ?? 0}
        </button>
        <button style={{ ...S.btn(), padding: '5px 12px' }} onClick={() => setEditing(true)}>✏️ {t('innov.idea_edit')}</button>
        <span style={S.badge(IDEA_STATUS_COLOR[idea.status])}>{label(IDEA_STATUS_LABEL, idea.status, isAR)}</span>
      </div>

      <div style={{ ...S.card, marginBottom: 16 }}>
        {idea.problemStatement && (<><div style={S.label}>{t('innov.idea_problem')}</div><div style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>{idea.problemStatement}</div></>)}
        <div style={S.label}>{t('innov.idea_description')}</div>
        <div style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 12, whiteSpace: 'pre-wrap' as const }}>{description}</div>
        {(idea.expectedBenefits || idea.targetBeneficiaries) && (
          <div className="grid-2" style={{ marginBottom: 12 }}>
            <div><div style={S.label}>{t('innov.idea_benefits')}</div><div style={{ fontSize: 13, lineHeight: 1.6 }}>{idea.expectedBenefits || '—'}</div></div>
            <div><div style={S.label}>{t('innov.idea_beneficiaries')}</div><div style={{ fontSize: 13, lineHeight: 1.6 }}>{idea.targetBeneficiaries || '—'}</div></div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const, alignItems: 'center' }}>
          {idea.category && <span style={S.badge('#7f8c8d')}>{categoryLabel(idea.category, isAR)}</span>}
          {(idea.tags || []).map((tag: string) => <span key={tag} style={S.badge('#3498db')}>{tag}</span>)}
          {relatedTech && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{t('innov.related_tech')}: {isAR && relatedTech.nameAr ? relatedTech.nameAr : relatedTech.name}</span>}
          <span style={{ fontSize: 11, color: 'var(--text-dim)', marginInlineStart: 'auto' }}>{t('innov.idea_submitted_by')}: {person(idea.submittedBy)} · {new Date(idea.createdAt).toLocaleDateString(isAR ? 'ar-SA' : 'en-GB')}</span>
        </div>
      </div>

      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' as const }}>
          <div style={{ flex: 1, fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center' }}>
            {t('innov.ai_qualification')}
            <HelpTip text={t('innov.idea_assessment_help')} />
          </div>
          {!assessing && <button style={S.btn('primary')} onClick={assess} disabled={starting}>{starting ? t('innov.qualifying') : idea.qualifiedAt ? t('innov.requalify') : t('innov.qualify')}</button>}
        </div>
        {assessing || starting ? (
          <div role="status" style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>⏳ {t('innov.idea_assessing')}</div>
        ) : idea.qualificationError ? (
          <div role="alert" style={{ fontSize: 12.5, color: '#e74c3c', marginBottom: 8 }}>{t('innov.idea_assessment_failed')}</div>
        ) : null}
        {!assessing && (hasAssessment ? (
          <IdeaAssessmentView assessment={idea.assessment} isAR={isAR} canAdjust={canDecide} onAdjust={adjust} onOpenIdea={onOpenIdea} title={title} />
        ) : idea.qualifiedAt ? (
          <>
            <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginBottom: 16, flexWrap: 'wrap' as const }}>
              <ScoreRing score={idea.feasibilityScore} label={t('innov.feasibility')} />
              <ScoreRing score={idea.impactScore} label={t('innov.impact')} />
              <ScoreRing score={idea.alignmentScore} label={t('innov.alignment')} />
              <ScoreRing score={idea.overallScore} label={t('innov.overall')} />
            </div>
            <div style={S.label}>{t('innov.rationale')}</div>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>{isAR && idea.qualificationRationaleAr ? idea.qualificationRationaleAr : idea.qualificationRationale}</div>
            <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 10 }}>{t('innov.idea_legacy_assessment')}</div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{t('innov.not_qualified_yet')}</div>
        ))}
      </div>

      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
          <span style={{ flex: 1 }}>{t('innov.idea_studies')}</span>
          {onStartStudy && <button style={S.btn(idea.aiRecommendation === 'PROCEED_TO_STUDY' ? 'primary' : 'secondary')} onClick={() => onStartStudy(idea)}>📑 {t('innov.idea_start_study')}</button>}
        </div>
        {idea.studies?.length ? idea.studies.map((s: any) => (
          <div key={s.id} style={{ fontSize: 12.5, marginBottom: 6 }}>
            <a href={`/innovation?study=${encodeURIComponent(s.id)}`} style={{ color: 'var(--accent)' }}>{isAR && s.titleAr ? s.titleAr : s.title}</a>
            <span style={{ color: 'var(--text-dim)' }}> · {isAR ? STUDY_STATUS_LABEL[s.status]?.ar || s.status : STUDY_STATUS_LABEL[s.status]?.en || s.status}</span>
          </div>
        )) : <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{t('innov.idea_no_studies')}</div>}
      </div>

      {idea.status !== 'APPROVED' && idea.status !== 'REJECTED' && idea.status !== 'ARCHIVED' && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}>{t('innov.decision')}</div>
          {rec && <div style={{ fontSize: 12, marginBottom: 10, color: 'var(--text-dim)' }}>{t('innov.idea_ai_suggests')}: <span style={S.badge(rec.color)}>{rec.icon} {isAR ? rec.ar : rec.en}</span></div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const, marginBottom: 12 }}>
            {idea.status !== 'IN_REVIEW' && <button style={S.btn()} onClick={() => moveTo('IN_REVIEW')} disabled={transitioning || assessing}>{t('innov.move_in_review')}</button>}
          </div>
          {canDecide ? (
            <>
              <label htmlFor="idea-decision-notes" style={S.label}>{t('innov.decision_notes')}</label>
              <input id="idea-decision-notes" style={S.input} value={decisionNotes} onChange={e => setDecisionNotes(e.target.value)} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                <button style={S.btn('primary')} onClick={() => moveTo('APPROVED')} disabled={transitioning || assessing}>{t('innov.approve')}</button>
                <button style={S.btn('danger')} onClick={() => moveTo('REJECTED')} disabled={transitioning || assessing}>{t('innov.reject')}</button>
                <button style={S.btn()} onClick={() => moveTo('ARCHIVED')} disabled={transitioning || assessing}>{t('innov.archive')}</button>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{t('innov.decision_restricted')}</div>
          )}
        </div>
      )}
      {idea.decidedAt && (
        <div style={{ ...S.card, marginBottom: 16, fontSize: 12.5 }}>
          <span style={{ fontWeight: 600 }}>{t('innov.decision')}:</span> {label(IDEA_STATUS_LABEL, idea.status, isAR)} · {person(idea.decidedBy)} · {new Date(idea.decidedAt).toLocaleDateString(isAR ? 'ar-SA' : 'en-GB')}
          {idea.decisionNotes && <div style={{ marginTop: 6 }}>{idea.decisionNotes}</div>}
        </div>
      )}

      <div style={S.card}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>💬 {t('innov.idea_discussion')} ({idea.comments?.length || 0})</div>
        {(idea.comments || []).map((c: any) => (
          <div key={c.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', display: 'flex', gap: 8 }}>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>{person(c.author)}</span>
              <span>{new Date(c.createdAt).toLocaleString(isAR ? 'ar-SA' : 'en-GB')}</span>
              {(c.mine || userRole === 'TENANT_ADMIN') && <button type="button" onClick={() => deleteComment(c.id)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 11 }}>{t('innov.idea_delete_comment')}</button>}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' as const, marginTop: 2 }}>{c.body}</div>
          </div>
        ))}
        <label htmlFor="idea-comment" style={{ ...S.label, marginTop: 10 }}>{t('innov.idea_add_comment')}</label>
        <textarea id="idea-comment" style={{ ...S.input, minHeight: 56, resize: 'vertical' as const, fontFamily: 'inherit' }} value={comment} onChange={e => setComment(e.target.value)} maxLength={4000} />
        <button style={S.btn('primary')} onClick={postComment} disabled={posting || !comment.trim()}>{posting ? t('innov.saving') : t('innov.idea_post_comment')}</button>
      </div>
    </div>
  )
}

function PortfolioSummary({ api, isAR, t }: any) {
  const [portfolio, setPortfolio] = useState<any>(null)

  useEffect(() => { api.get('/innovation/portfolio').then(setPortfolio) }, [api])

  if (!portfolio || !portfolio.funnel) return null

  return (
    <div className="stat-grid-5" style={{ marginBottom: 20 }}>
      <div style={S.card}>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>{t('innov.funnel_submitted')}</div>
        <div style={{ fontSize: 22, fontWeight: 700 }}>{portfolio.funnel.ideasSubmitted}</div>
      </div>
      <div style={S.card}>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>{t('innov.funnel_qualified')}</div>
        <div style={{ fontSize: 22, fontWeight: 700 }}>{portfolio.funnel.ideasQualified}</div>
      </div>
      <div style={S.card}>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>{t('innov.funnel_studies')}</div>
        <div style={{ fontSize: 22, fontWeight: 700 }}>{portfolio.funnel.studiesGenerated}</div>
      </div>
      <div style={S.card}>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>{t('innov.funnel_approved')}</div>
        <div style={{ fontSize: 22, fontWeight: 700 }}>{portfolio.funnel.studiesApproved}</div>
      </div>
      <div style={S.card}>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>{t('innov.funnel_initiatives')}</div>
        <div style={{ fontSize: 22, fontWeight: 700 }}>{portfolio.funnel.convertedToInitiative}</div>
      </div>
    </div>
  )
}

// Who prepared a study: platform users in this module (the default view), or
// the Chief Architect in Copilot.
export type StudyAuthorFilter = 'USER' | 'COPILOT' | 'ALL'
export function studiesQuery(status: string, author: StudyAuthorFilter): string {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  params.set('author', author)
  return `/innovation/studies?${params.toString()}`
}

function StudiesTab({ api, isAR, t, initialStudyId, seed, onSeedConsumed }: any) {
  const [studies, setStudies] = useState<any[]>([])
  const [statusFilter, setStatusFilter] = useState('')
  const [authorFilter, setAuthorFilter] = useState<StudyAuthorFilter>('USER')
  const [creating, setCreating] = useState(!!seed)
  const [selectedId, setSelectedId] = useState<string | null>(initialStudyId || null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    api.get(studiesQuery(statusFilter, authorFilter)).then((d: any) => setStudies(Array.isArray(d) ? d : [])).finally(() => setLoading(false))
  }, [api, statusFilter, authorFilter])
  useEffect(() => { load() }, [load])

  if (selectedId) return <StudyDetail api={api} studyId={selectedId} isAR={isAR} t={t} onBack={() => { setSelectedId(null); load() }} />

  return (
    <div>
      <PortfolioSummary api={api} isAR={isAR} t={t} />
      <div style={{ ...S.row, marginBottom: 16, flexWrap: 'wrap' as const }}>
        <select style={{ ...S.input, marginBottom: 0, width: 200 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">{t('innov.all_statuses')}</option>
          {Object.keys(STUDY_STATUS_LABEL).map(s => <option key={s} value={s}>{isAR ? STUDY_STATUS_LABEL[s].ar : STUDY_STATUS_LABEL[s].en}</option>)}
        </select>
        <label htmlFor="study-author-filter" style={{ fontSize: 12, color: 'var(--text-dim)' }}>{t('innov.author_filter')}</label>
        <select id="study-author-filter" style={{ ...S.input, marginBottom: 0, width: 220 }} value={authorFilter} onChange={e => setAuthorFilter(e.target.value as StudyAuthorFilter)}>
          <option value="USER">{t('innov.author_users')}</option>
          <option value="COPILOT">{t('innov.author_copilot')}</option>
          <option value="ALL">{t('innov.author_all')}</option>
        </select>
        <HelpTip text={t('innov.author_help')} />
        <div style={{ flex: 1 }} />
        <button style={S.btn('primary')} onClick={() => setCreating(true)}>{t('innov.new_study')}</button>
      </div>

      {creating && <StudyCreateForm api={api} isAR={isAR} t={t} initial={seed} onDone={(id: string) => { setCreating(false); onSeedConsumed?.(); setSelectedId(id) }} onCancel={() => { setCreating(false); onSeedConsumed?.() }} />}

      {loading ? (
        <div style={{ color: 'var(--text-dim)' }}>{isAR ? 'جارٍ التحميل…' : 'Loading…'}</div>
      ) : studies.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-dim)', padding: 40 }}>{t('innov.no_studies')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {studies.map((s: any) => (
            <div key={s.id} style={{ ...S.card, padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16 }} onClick={() => setSelectedId(s.id)}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {isAR && s.titleAr ? s.titleAr : s.title}
                  {s.authorType === 'COPILOT' && <span style={{ ...S.badge('#38bdf8'), marginInlineStart: 8 }}>{t('copilot.study.by_copilot')}</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>{s.objective}</div>
              </div>
              {s.qualityScore != null && <div style={{ fontSize: 15, fontWeight: 700 }}>{s.qualityScore}</div>}
              {s.recommendation && <span style={S.badge('#3498db')}>{isAR ? RECOMMENDATION_LABEL[s.recommendation]?.ar : RECOMMENDATION_LABEL[s.recommendation]?.en}</span>}
              <span style={S.badge(STUDY_STATUS_COLOR[s.status])}>{isAR ? STUDY_STATUS_LABEL[s.status]?.ar : STUDY_STATUS_LABEL[s.status]?.en}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function StudyCreateForm({ api, isAR, t, initial, onDone, onCancel }: any) {
  const [form, setForm] = useState<{ title: string; titleAr: string; objective: string; originType: string; originRadarItemId: string; originIdeaId: string; originDescription: string; scope: string; includeImpactAnalysis: boolean }>(
    { title: '', titleAr: '', objective: '', originType: 'MANUAL', originRadarItemId: '', originIdeaId: '', originDescription: '', scope: 'STANDARD', includeImpactAnalysis: false, ...(initial || {}) })
  const [radarItems, setRadarItems] = useState<any[]>([])
  const [ideas, setIdeas] = useState<any[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (form.originType === 'RADAR_ITEM' && radarItems.length === 0) api.get('/innovation/radar').then((d: any) => setRadarItems(Array.isArray(d) ? d : []))
    if (form.originType === 'IDEA' && ideas.length === 0) api.get('/innovation/ideas').then((d: any) => setIdeas(Array.isArray(d) ? d : []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.originType])

  const create = async () => {
    if (!form.title || !form.objective) return alert(isAR ? 'العنوان والهدف مطلوبان' : 'Title and objective are required')
    if (form.originType === 'RADAR_ITEM' && !form.originRadarItemId) return alert(isAR ? 'يرجى اختيار تقنية' : 'Please select a technology')
    if (form.originType === 'IDEA' && !form.originIdeaId) return alert(isAR ? 'يرجى اختيار فكرة' : 'Please select an idea')
    setSaving(true)
    try {
      const created = await api.post('/innovation/studies', {
        title: form.title, titleAr: form.titleAr || undefined, objective: form.objective, originType: form.originType, scope: form.scope,
        includeImpactAnalysis: form.includeImpactAnalysis,
        originRadarItemId: form.originType === 'RADAR_ITEM' ? form.originRadarItemId : undefined,
        originIdeaId: form.originType === 'IDEA' ? form.originIdeaId : undefined,
        originDescription: form.originType !== 'RADAR_ITEM' && form.originType !== 'IDEA' ? form.originDescription : undefined,
      })
      onDone(created.id)
    } catch (e: any) { alert(e.message) } finally { setSaving(false) }
  }

  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div><div style={S.label}>{t('innov.study_title')} *</div><input style={S.input} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
        <div><div style={S.label}>{t('innov.study_title_ar')}</div><input style={S.input} dir="rtl" value={form.titleAr} onChange={e => setForm(f => ({ ...f, titleAr: e.target.value }))} /></div>
      </div>
      <div style={S.label}>{t('innov.objective')} *</div>
      <textarea style={{ ...S.input, minHeight: 60, resize: 'vertical' as const, fontFamily: 'inherit' }} value={form.objective} onChange={e => setForm(f => ({ ...f, objective: e.target.value }))} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <div style={S.label}>{t('innov.origin_type')}</div>
          <select style={S.input} value={form.originType} onChange={e => setForm(f => ({ ...f, originType: e.target.value }))}>
            <option value="MANUAL">{t('innov.origin_manual')}</option>
            <option value="RADAR_ITEM">{t('innov.origin_radar')}</option>
            <option value="IDEA">{t('innov.origin_idea')}</option>
            <option value="BUSINESS_PROBLEM">{t('innov.origin_business_problem')}</option>
            <option value="EA_RECOMMENDATION">{t('innov.origin_ea_recommendation')}</option>
            <option value="ARCHITECTURE_GAP">{t('innov.origin_architecture_gap')}</option>
          </select>
        </div>
        <div>
          <div style={S.label}>{t('innov.scope')}</div>
          <select style={S.input} value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}>
            <option value="STANDARD">{t('innov.scope_standard')}</option>
            <option value="EXECUTIVE">{t('innov.scope_executive')}</option>
            <option value="DETAILED">{t('innov.scope_detailed')}</option>
          </select>
        </div>
      </div>
      {form.originType === 'RADAR_ITEM' && (
        <><div style={S.label}>{t('innov.origin_radar')}</div>
        <select style={S.input} value={form.originRadarItemId} onChange={e => setForm(f => ({ ...f, originRadarItemId: e.target.value }))}>
          <option value="">{t('innov.select_radar_item')}</option>
          {radarItems.map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select></>
      )}
      {form.originType === 'IDEA' && (
        <><div style={S.label}>{t('innov.origin_idea')}</div>
        <select style={S.input} value={form.originIdeaId} onChange={e => setForm(f => ({ ...f, originIdeaId: e.target.value }))}>
          <option value="">{t('innov.select_idea')}</option>
          {ideas.map((i: any) => <option key={i.id} value={i.id}>{i.title}</option>)}
        </select></>
      )}
      {form.originType !== 'RADAR_ITEM' && form.originType !== 'IDEA' && (
        <><div style={S.label}>{t('innov.origin_description')}</div>
        <textarea style={{ ...S.input, minHeight: 60, resize: 'vertical' as const, fontFamily: 'inherit' }} value={form.originDescription} onChange={e => setForm(f => ({ ...f, originDescription: e.target.value }))} /></>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 12px', flexWrap: 'wrap' as const }}>
        <input id="study-include-impact" type="checkbox" checked={form.includeImpactAnalysis} onChange={e => setForm(f => ({ ...f, includeImpactAnalysis: e.target.checked }))} />
        <label htmlFor="study-include-impact" style={{ fontSize: 13, cursor: 'pointer' }}>{isAR ? 'تضمين تحليل الأثر على البنية المؤسسية' : 'Include EA impact analysis'}</label>
        <HelpTip text={EA_IMPACT_HELP[isAR ? 'ar' : 'en']} />
      </div>
      <div style={S.row}>
        <button style={S.btn('primary')} onClick={create} disabled={saving}>{saving ? t('innov.saving') : t('innov.create_study')}</button>
        <button style={S.btn()} onClick={onCancel}>{t('innov.cancel')}</button>
      </div>
    </div>
  )
}

function StudySectionCard({ section, isAR, api, onUpdated }: any) {
  const def = STUDY_SECTIONS.find(d => d.key === section.sectionKey)
  const title = def ? (isAR ? def.ar : def.en) : section.title
  const content = section.content
  const sourceNote = studySourceNote(section.sectionKey, content, isAR)

  // ── Innovation-P4: section-level edit / regenerate / approve / lock /
  // comment, version history, cross-section impact preview ────────────────
  const [editing, setEditing] = useState(false)
  const [editDraft, setEditDraft] = useState<any>(null)
  const [saving, setSaving] = useState(false)
  const [revisiting, setRevisiting] = useState(false)
  const [guidance, setGuidance] = useState('')
  const [showGuidanceBox, setShowGuidanceBox] = useState(false)
  const [showComments, setShowComments] = useState(false)
  const [comments, setComments] = useState<any[]>([])
  const [newComment, setNewComment] = useState('')
  const [showVersions, setShowVersions] = useState(false)
  const [versions, setVersions] = useState<any[]>([])
  const [impactWarning, setImpactWarning] = useState<string[] | null>(null)
  const [actionError, setActionError] = useState('')

  const locked = !!section.locked
  const approved = section.status === 'APPROVED'

  const loadImpactPreview = async () => {
    try {
      const preview = await api.get(`/innovation/study-sections/impact-preview/${section.sectionKey}`)
      setImpactWarning(preview?.potentiallyStaleSections?.length ? preview.potentiallyStaleSections : null)
    } catch { setImpactWarning(null) }
  }

  const startEdit = async () => {
    setActionError('')
    await loadImpactPreview()
    // The impact analysis' view pictures are generated and kept by the server; the editor shows which view, not the image data.
    const editable = def?.shape === 'impact' && Array.isArray(content?.domains)
      ? { ...content, domains: content.domains.map((d: any) => d.view ? { ...d, view: { title: d.view.title, source: d.view.source } } : d) }
      : content
    setEditDraft(def?.shape === 'recommendation' ? { ...content } : def?.shape === 'list' || def?.shape === 'impact' ? JSON.stringify(editable, null, 2) : (content || ''))
    setEditing(true)
  }

  const saveEdit = async () => {
    setSaving(true); setActionError('')
    try {
      let payload = editDraft
      if (def?.shape === 'list' || def?.shape === 'impact') {
        try { payload = JSON.parse(editDraft) } catch { setActionError(isAR ? 'صيغة JSON غير صالحة' : 'Invalid JSON — check the structure before saving.'); setSaving(false); return }
      }
      await api.put(`/innovation/study-sections/${section.id}/content`, { content: payload })
      setEditing(false)
      onUpdated()
    } catch (e: any) { setActionError(e.message || String(e)) } finally { setSaving(false) }
  }

  const revisitWithAI = async () => {
    if (!guidance || guidance.trim().length < 5) { setActionError(isAR ? 'يرجى تقديم توجيه محدد' : 'Please provide specific guidance on what should change.'); return }
    setRevisiting(true); setActionError('')
    try {
      await api.post(`/innovation/study-sections/${section.id}/revisit`, { guidance })
      setShowGuidanceBox(false); setGuidance('')
      onUpdated()
    } catch (e: any) { setActionError(e.message || String(e)) } finally { setRevisiting(false) }
  }

  const approve = async () => { try { await api.post(`/innovation/study-sections/${section.id}/approve`); onUpdated() } catch (e: any) { setActionError(e.message || String(e)) } }
  const toggleLock = async () => {
    try { await api.post(`/innovation/study-sections/${section.id}/${locked ? 'unlock' : 'lock'}`); onUpdated() }
    catch (e: any) { setActionError(e.message || String(e)) }
  }

  const loadComments = async () => {
    const list = await api.get(`/innovation/study-sections/${section.id}/comments`)
    setComments(Array.isArray(list) ? list : [])
  }
  const toggleComments = async () => { if (!showComments) await loadComments(); setShowComments(!showComments) }
  const submitComment = async () => {
    if (!newComment.trim()) return
    await api.post(`/innovation/study-sections/${section.id}/comments`, { body: newComment })
    setNewComment(''); await loadComments()
  }
  const resolveComment = async (commentId: string) => { await api.post(`/innovation/study-section-comments/${commentId}/resolve`); await loadComments() }

  const loadVersions = async () => {
    const list = await api.get(`/innovation/study-sections/${section.id}/versions`)
    setVersions(Array.isArray(list) ? list : [])
  }
  const toggleVersions = async () => { if (!showVersions) await loadVersions(); setShowVersions(!showVersions) }

  const STATUS_COLOR: Record<string, string> = { PENDING: '#64748B', GENERATING: '#f39c12', AI_DRAFT: '#3498db', UNDER_REVIEW: '#f39c12', APPROVED: '#2ecc71' }

  return (
    <div style={{ ...S.card, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' as const }}>
        <div style={{ fontWeight: 700, fontSize: 14, flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
          {title}
          {section.sectionKey === 'TECHNOLOGY_OPTIONS' && <HelpTip text={isAR
            ? 'كل خيار يسمّي منتجات حقيقية من السوق ومكانتها لدى المحللين (مثل Gartner Magic Quadrant) وفق معرفة الذكاء الاصطناعي، مرتبة: إعادة الاستخدام ثم التوسعة ثم البناء ثم الشراء. تحقق من أحدث التقارير قبل الشراء.'
            : 'Each option names real market products and their analyst standing (e.g. Gartner Magic Quadrant) from the AI’s market knowledge, ordered reuse → extend → build → buy. Verify against current reports before procurement.'} />}
          {section.sectionKey === 'COST_ESTIMATE' && <HelpTip text={isAR
            ? 'تكاليف تنفيذ الخيار الأنسب: الرأسمالية لمرة واحدة والتشغيلية سنوياً، لكل بند نطاق وطريقة حساب ومبررات. أضف افتراضات الدراسة (مثل عدد المستخدمين) قبل التوليد لتحسين الدقة. الإجماليات وتكلفة الملكية لثلاث سنوات تُحسب تلقائياً من البنود.'
            : 'Cost of implementing the best-fit option: one-time CAPEX and yearly OPEX, each line with a range, its calculation and rationale. Add study assumptions (e.g. user numbers) before generating for sharper figures. Totals and the 3-year cost of ownership are computed from the lines.'} />}
          {section.sectionKey === 'EA_IMPACT_ANALYSIS' && <HelpTip text={isAR
            ? 'أثر الدراسة على كل مجال في مستودع البنية المؤسسية: العناصر المتأثرة أو المحتمل تأثرها فقط، مع نوع الأثر ومستواه ووصفه. «غير مباشر» يعني أن العنصر قد يتأثر عبر علاقاته. التعديل اليدوي لا يمكنه إضافة عنصر غير موجود في المستودع، و«إعادة النظر بالذكاء الاصطناعي» تعيد التحليل مقابل المستودع وفق توجيهك.'
            : 'The study’s impact on each domain of your EA Repository: only objects that are or might be impacted, with the impact type, level and description. “Indirect” means the object might be affected through its relationships. Manual edits cannot add an object that is not in the Repository; Revisit with AI re-runs the analysis against the Repository with your guidance.'} />}
          {section.sectionKey === 'BENCHMARK' && <HelpTip text={isAR
            ? 'يقارن المنتجات المسماة في خيارات التقنية على خمسة معايير. يُعد الصف مستندًا إلى أدلة فقط عندما يستشهد بدليل موثق في رادار التقنية للتقنية محل الدراسة؛ وإلا فهو تقييم نوعي وليس قياسًا. التعديل اليدوي لا يمكنه إضافة دليل.'
            : 'Compares the products named in the technology options on five criteria. A row counts as evidence-backed only when it cites recorded Tech Radar evidence for the study’s technology; otherwise it is a qualitative assessment, not a measurement. Manual edits cannot add evidence.'} />}
        </div>
        <span style={S.badge(STATUS_COLOR[section.status] || '#64748B')}>{section.status?.replace(/_/g, ' ')}</span>
        {locked && <span style={S.badge('#64748B')}>🔒 {isAR ? 'مقفل' : 'Locked'}</span>}
        {content != null && !editing && (
          <>
            {!locked && <button style={{ ...S.btn(), padding: '4px 10px', fontSize: 11 }} onClick={startEdit}>✏ {isAR ? 'تعديل' : 'Edit'}</button>}
            {!locked && <button style={{ ...S.btn(), padding: '4px 10px', fontSize: 11 }} onClick={async () => { await loadImpactPreview(); setShowGuidanceBox(true) }}>✨ {isAR ? 'إعادة النظر بالذكاء الاصطناعي' : 'Revisit with AI'}</button>}
            {!approved && <button style={{ ...S.btn('primary'), padding: '4px 10px', fontSize: 11 }} onClick={approve}>✓ {isAR ? 'اعتماد' : 'Approve'}</button>}
            <button style={{ ...S.btn(), padding: '4px 10px', fontSize: 11 }} onClick={toggleLock}>{locked ? (isAR ? '🔓 فتح' : '🔓 Unlock') : (isAR ? '🔒 قفل' : '🔒 Lock')}</button>
            <button style={{ ...S.btn(), padding: '4px 10px', fontSize: 11 }} onClick={toggleComments}>💬 {isAR ? 'تعليقات' : 'Comments'}</button>
            <button style={{ ...S.btn(), padding: '4px 10px', fontSize: 11 }} onClick={toggleVersions}>🕘 {isAR ? 'السجل' : 'History'}</button>
          </>
        )}
      </div>

      {actionError && <div style={{ fontSize: 12, color: '#e74c3c', marginBottom: 10 }}>{actionError}</div>}

      {showGuidanceBox && (
        <div style={{ background: 'var(--navy)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={S.label}>{isAR ? 'ما الذي يجب أن يتغير؟ (مطلوب)' : 'What should change? (required)'}</div>
          {impactWarning && (
            <div style={{ fontSize: 11, color: '#f39c12', marginBottom: 8 }}>
              ⚠ {isAR ? 'قد تحتاج هذه الأقسام لمراجعة بعد هذا التغيير: ' : 'These sections may need re-review after this change: '}
              {impactWarning.map(k => STUDY_SECTIONS.find(d => d.key === k)?.[isAR ? 'ar' : 'en'] || k).join(', ')}
            </div>
          )}
          <textarea style={{ ...S.input, minHeight: 60, resize: 'vertical' as const, fontFamily: 'inherit' }} value={guidance} onChange={e => setGuidance(e.target.value)} placeholder={isAR ? 'مثال: اجعل تقدير التكلفة أكثر تحفظًا' : 'e.g. Make the cost estimate more conservative'} />
          <div style={S.row}>
            <button style={S.btn('primary')} onClick={revisitWithAI} disabled={revisiting}>{revisiting ? (isAR ? 'جارٍ...' : 'Working...') : (isAR ? 'إرسال' : 'Submit')}</button>
            <button style={S.btn()} onClick={() => { setShowGuidanceBox(false); setGuidance('') }}>{isAR ? 'إلغاء' : 'Cancel'}</button>
          </div>
        </div>
      )}

      {editing ? (
        <div>
          {impactWarning && (
            <div style={{ fontSize: 11, color: '#f39c12', marginBottom: 8 }}>
              ⚠ {isAR ? 'قد تحتاج هذه الأقسام لمراجعة بعد هذا التغيير: ' : 'These sections may need re-review after this change: '}
              {impactWarning.map(k => STUDY_SECTIONS.find(d => d.key === k)?.[isAR ? 'ar' : 'en'] || k).join(', ')}
            </div>
          )}
          {def?.shape === 'recommendation' ? (
            <div>
              <div style={S.label}>{isAR ? 'التوصية' : 'Recommendation'}</div>
              <select style={S.input} value={editDraft?.recommendation || ''} onChange={e => setEditDraft((d: any) => ({ ...d, recommendation: e.target.value }))}>
                {Object.keys(RECOMMENDATION_LABEL).map(k => <option key={k} value={k}>{isAR ? RECOMMENDATION_LABEL[k].ar : RECOMMENDATION_LABEL[k].en}</option>)}
              </select>
              <div style={S.label}>{isAR ? 'المبررات' : 'Rationale'}</div>
              <textarea style={{ ...S.input, minHeight: 100, resize: 'vertical' as const, fontFamily: 'inherit' }} value={editDraft?.rationale || ''} onChange={e => setEditDraft((d: any) => ({ ...d, rationale: e.target.value }))} />
            </div>
          ) : def?.shape === 'list' || def?.shape === 'impact' ? (
            <textarea style={{ ...S.input, minHeight: 160, resize: 'vertical' as const, fontFamily: 'monospace', fontSize: 12 }} value={editDraft || ''} onChange={e => setEditDraft(e.target.value)} />
          ) : (
            <textarea style={{ ...S.input, minHeight: 120, resize: 'vertical' as const, fontFamily: 'inherit' }} value={editDraft || ''} onChange={e => setEditDraft(e.target.value)} />
          )}
          <div style={S.row}>
            <button style={S.btn('primary')} onClick={saveEdit} disabled={saving}>{saving ? (isAR ? 'جارٍ الحفظ...' : 'Saving...') : (isAR ? 'حفظ' : 'Save')}</button>
            <button style={S.btn()} onClick={() => setEditing(false)}>{isAR ? 'إلغاء' : 'Cancel'}</button>
          </div>
        </div>
      ) : content == null ? (
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{isAR ? 'لم يتم إنشاء هذا القسم بعد' : 'Not yet generated'}</div>
      ) : def?.shape === 'text' ? (
        <div style={{ fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' as const }}>{content}</div>
      ) : def?.shape === 'impact' ? (
        <StudyEaImpact content={content} isAR={isAR} />
      ) : def?.shape === 'recommendation' ? (
        <div>
          <span style={S.badge('#2ecc71')}>{isAR ? RECOMMENDATION_LABEL[content.recommendation]?.ar : RECOMMENDATION_LABEL[content.recommendation]?.en}</span>
          <div style={{ fontSize: 13, lineHeight: 1.7, marginTop: 10 }}>{content.rationale}</div>
        </div>
      ) : Array.isArray(content) ? (
        <div>
          {content.length ? (section.sectionKey === 'COST_ESTIMATE' ? <StudyCostEstimate lines={content} isAR={isAR} /> : <StudyRegisterTable rows={content} isAR={isAR} />) : <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{isAR ? 'لا توجد عناصر' : 'No items'}</div>}
          {sourceNote && <div data-testid="study-source-note" style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8, fontStyle: 'italic' as const }}>{sourceNote}</div>}
        </div>
      ) : (
        <div style={{ fontSize: 13 }}>{JSON.stringify(content)}</div>
      )}

      {showComments && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          {comments.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 8 }}>{isAR ? 'لا توجد تعليقات بعد' : 'No comments yet'}</div>}
          {comments.map((c: any) => (
            <div key={c.id} style={{ background: 'var(--navy)', borderRadius: 8, padding: 10, marginBottom: 8, opacity: c.resolved ? 0.6 : 1 }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>{c.body}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{new Date(c.createdAt).toLocaleString()}</span>
                {c.resolved ? <span style={S.badge('#2ecc71')}>{isAR ? 'تم الحل' : 'Resolved'}</span> : (
                  <button style={{ ...S.btn(), padding: '2px 8px', fontSize: 10 }} onClick={() => resolveComment(c.id)}>{isAR ? 'وضع علامة كمحلول' : 'Mark resolved'}</button>
                )}
              </div>
            </div>
          ))}
          <div style={S.row}>
            <input style={{ ...S.input, marginBottom: 0 }} value={newComment} onChange={e => setNewComment(e.target.value)} placeholder={isAR ? 'أضف تعليقًا...' : 'Add a comment...'} />
            <button style={S.btn('primary')} onClick={submitComment}>{isAR ? 'إرسال' : 'Post'}</button>
          </div>
        </div>
      )}

      {showVersions && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          {versions.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{isAR ? 'لا يوجد سجل تعديلات بعد' : 'No edit history yet'}</div>}
          {versions.map((v: any) => (
            <div key={v.id} style={{ background: 'var(--navy)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={S.badge(v.changeType === 'AI_REVISIT' ? '#9b59b6' : '#3498db')}>{v.changeType === 'AI_REVISIT' ? (isAR ? 'مراجعة بالذكاء الاصطناعي' : 'AI Revisit') : (isAR ? 'تعديل يدوي' : 'Manual Edit')}</span>
                <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{new Date(v.createdAt).toLocaleString()}</span>
              </div>
              {v.changeReason && <div style={{ fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic' }}>{v.changeReason}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function StudyDetail({ api, studyId, isAR, t, onBack }: any) {
  const [study, setStudy] = useState<any>(null)
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError] = useState('')
  const [exporting, setExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null)
  const [showExport, setShowExport] = useState(false)
  const [converting, setConverting] = useState(false)
  const [addingAssumption, setAddingAssumption] = useState(false)
  const [assumptionForm, setAssumptionForm] = useState({ label: '', value: '' })
  const [impactBusy, setImpactBusy] = useState<'' | 'toggle' | 'run'>('')
  const [impactError, setImpactError] = useState('')

  const load = useCallback(() => { api.get(`/innovation/studies/${studyId}`).then(setStudy) }, [api, studyId])
  useEffect(() => { load() }, [load])

  const generate = async () => {
    setGenerating(true); setGenError('')
    try { await api.post(`/innovation/studies/${studyId}/generate`); await load() }
    catch (e: any) { setGenError(e.message); await load() }
    finally { setGenerating(false) }
  }

  const toggleImpactAnalysis = async (enabled: boolean) => {
    setImpactBusy('toggle'); setImpactError('')
    try { await api.put(`/innovation/studies/${studyId}/impact-analysis`, { enabled }); await load() }
    catch (e: any) { setImpactError(e.message || String(e)) }
    finally { setImpactBusy('') }
  }
  const runImpactAnalysis = async () => {
    setImpactBusy('run'); setImpactError('')
    try { await api.post(`/innovation/studies/${studyId}/impact-analysis/run`); await load() }
    catch (e: any) { setImpactError(e.message || String(e)) }
    finally { setImpactBusy('') }
  }

  const moveTo = async (status: string) => { await api.put(`/innovation/studies/${studyId}/status`, { status }); await load() }

  const deleteStudy = async () => {
    if (!window.confirm(t('innov.confirm_delete_study'))) return
    await api.post(`/innovation/studies/${studyId}/delete`); onBack()
  }

  const addAssumption = async () => {
    if (!assumptionForm.label || !assumptionForm.value) return
    await api.post(`/innovation/studies/${studyId}/assumptions`, assumptionForm)
    setAssumptionForm({ label: '', value: '' }); setAddingAssumption(false); await load()
  }

  const exportStudy = async (language: 'en'|'ar', format: GovernanceExportFormat) => {
    setShowExport(false)
    setExporting(true)
    try {
      const blob = await fetchStudyExport(`${API}/innovation/studies/${studyId}/export/${format === 'word' ? 'docx' : 'pptx'}?lang=${language}`, localStorage.getItem('ea_token'), {
        onPreparing: p => setExportProgress(p), fallbackMessage: t('innov.export_failed'),
      })
      saveBlob(blob, exportFileName(language === 'ar' && study?.titleAr ? study.titleAr : study?.title, 'Innovation_Study', language, format === 'word' ? 'docx' : 'pptx'))
    } catch (e: any) {
      alert(e?.message || t('innov.export_failed'))
    } finally {
      setExporting(false); setExportProgress(null)
    }
  }

  const convertToInitiative = async () => {
    if (!window.confirm(t('innov.convert_confirm'))) return
    setConverting(true)
    try {
      await api.post(`/innovation/studies/${studyId}/convert-to-initiative`, {})
      alert(t('innov.convert_success'))
      await load()
    } catch (e: any) {
      alert(`${t('innov.convert_failed')} ${e.message || ''}`)
    } finally {
      setConverting(false)
    }
  }

  if (!study) return <div style={{ color: 'var(--text-dim)' }}>{isAR ? 'جارٍ التحميل…' : 'Loading…'}</div>

  const title = isAR && study.titleAr ? study.titleAr : study.title
  const hasGeneratedContent = (study.sections || []).some((s: any) => s.content != null)
  // Study order comes from the section definitions: a section added later (the benchmark) must not tie with older sections' stored orderIndex.
  const sectionRank = (sec: any) => { const i = STUDY_SECTIONS.findIndex(d => d.key === sec.sectionKey); return i < 0 ? 1000 + (sec.orderIndex ?? 0) : i }
  const sortedSections = [...(study.sections || [])].sort((a: any, b: any) => sectionRank(a) - sectionRank(b))
  const impactSection = (study.sections || []).find((s: any) => s.sectionKey === 'EA_IMPACT_ANALYSIS')

  return (
    <div>
      {showExport && <GovernanceExportDialog subject='study' onClose={() => setShowExport(false)} onExport={exportStudy} />}
      <div style={{ ...S.row, marginBottom: 16, flexWrap: 'wrap' as const }}>
        <button style={{ ...S.btn(), padding: '6px 12px' }} onClick={onBack}>{t('innov.back_to_studies')}</button>
        <div style={{ flex: 1, fontSize: 18, fontWeight: 700 }}>{title}</div>
        <span style={S.badge(STUDY_STATUS_COLOR[study.status])}>{isAR ? STUDY_STATUS_LABEL[study.status]?.ar : STUDY_STATUS_LABEL[study.status]?.en}</span>
        {hasGeneratedContent && (
          <><button style={S.btn()} onClick={() => setShowExport(true)} disabled={exporting}>{exporting ? t('innov.exporting') : (isAR ? 'تصدير' : 'Export')}</button>
          {exportProgress && <span role="status" style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{t('innov.export_preparing')}{exportProgress.total ? ` (${Math.min(exportProgress.translated, exportProgress.total)}/${exportProgress.total})` : ''}</span>}</>
        )}
        {hasGeneratedContent && study.status !== 'PILOT_INITIATIVE' && study.status !== 'IMPLEMENTED' && (
          <button style={S.btn('primary')} onClick={convertToInitiative} disabled={converting}>{converting ? t('innov.converting') : t('innov.convert_to_initiative')}</button>
        )}
      </div>

      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={S.label}>{t('innov.objective')}</div>
        <div style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>{study.objective}</div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' as const, alignItems: 'center' }}>
          {study.qualityScore != null && <div style={{ fontSize: 13 }}><span style={{ color: 'var(--text-dim)' }}>{t('innov.quality_score')}: </span><b>{study.qualityScore}</b></div>}
          {study.recommendation && <div style={{ fontSize: 13 }}><span style={{ color: 'var(--text-dim)' }}>{t('innov.recommendation')}: </span><span style={S.badge('#2ecc71')}>{isAR ? RECOMMENDATION_LABEL[study.recommendation]?.ar : RECOMMENDATION_LABEL[study.recommendation]?.en}</span></div>}
          <div style={{ flex: 1 }} />
          <button style={S.btn('primary')} onClick={generate} disabled={generating || study.status === 'AI_RESEARCH'}>
            {generating || study.status === 'AI_RESEARCH' ? t('innov.generating') : hasGeneratedContent ? t('innov.regenerate_study') : t('innov.generate_study')}
          </button>
        </div>
        {genError && <div style={{ fontSize: 12, color: '#e74c3c', marginTop: 10 }}>{t('innov.generation_failed')}: {genError}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <input id="study-impact-toggle" type="checkbox" checked={!!study.includeImpactAnalysis} disabled={!!impactBusy || study.status === 'AI_RESEARCH'} onChange={e => toggleImpactAnalysis(e.target.checked)} />
          <label htmlFor="study-impact-toggle" style={{ fontSize: 13, cursor: 'pointer' }}>{isAR ? 'تضمين تحليل الأثر على البنية المؤسسية' : 'Include EA impact analysis'}</label>
          <HelpTip text={EA_IMPACT_HELP[isAR ? 'ar' : 'en']} />
          <div style={{ flex: 1 }} />
          {study.includeImpactAnalysis && hasGeneratedContent && (
            <button style={S.btn()} onClick={runImpactAnalysis} disabled={!!impactBusy || generating || study.status === 'AI_RESEARCH'}>
              {impactBusy === 'run' ? (isAR ? 'جارٍ تحليل الأثر…' : 'Analyzing impact…') : impactSection?.content != null ? (isAR ? 'إعادة تشغيل تحليل الأثر' : 'Re-run impact analysis') : (isAR ? 'تشغيل تحليل الأثر' : 'Run impact analysis')}
            </button>
          )}
        </div>
        {study.includeImpactAnalysis && !hasGeneratedContent && <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>{isAR ? 'سيُنفَّذ تحليل الأثر ضمن توليد الدراسة.' : 'The impact analysis runs as part of generating the study.'}</div>}
        {impactError && <div style={{ fontSize: 12, color: '#e74c3c', marginTop: 8 }}>{impactError}</div>}
      </div>

      {!hasGeneratedContent && !generating && study.status !== 'AI_RESEARCH' && (
        <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-dim)', padding: 30, marginBottom: 16 }}>{t('innov.not_generated_yet')}</div>
      )}

      {hasGeneratedContent && sortedSections.map((s: any) => <StudySectionCard key={s.id} section={s} isAR={isAR} api={api} onUpdated={load} />)}

      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ flex: 1, fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center' }}>
            {t('innov.assumptions')}
            <HelpTip text={isAR
              ? 'سجّل الافتراضات الأساسية التي استندت إليها الدراسة (مثل عدد المستخدمين المتوقع أو مدة التنفيذ)، حتى يمكن مراجعتها لاحقًا إذا تغيّرت.'
              : 'Record the key assumptions this study relies on (e.g. expected user count, implementation duration), so they can be revisited later if they change.'} />
          </div>
          <button style={S.btn()} onClick={() => setAddingAssumption(true)}>{t('innov.add_assumption')}</button>
        </div>
        {addingAssumption && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' as const }}>
            <input style={{ ...S.input, marginBottom: 0, flex: 1 }} placeholder={t('innov.assumption_label')} value={assumptionForm.label} onChange={e => setAssumptionForm(f => ({ ...f, label: e.target.value }))} />
            <input style={{ ...S.input, marginBottom: 0, flex: 1 }} placeholder={t('innov.assumption_value')} value={assumptionForm.value} onChange={e => setAssumptionForm(f => ({ ...f, value: e.target.value }))} />
            <button style={S.btn('primary')} onClick={addAssumption}>{t('innov.submit')}</button>
            <button style={S.btn()} onClick={() => setAddingAssumption(false)}>{t('innov.cancel')}</button>
          </div>
        )}
        {(study.assumptions || []).length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{t('innov.no_assumptions')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {study.assumptions.map((a: any) => (
              <div key={a.id} style={{ fontSize: 12, display: 'flex', gap: 8 }}>
                <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>{a.label}:</span><span>{a.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <RelatedObjectsPanel api={api} studyId={studyId} isAR={isAR} t={t} />

      <div style={S.card}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}>{t('innov.study_status')}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
          {study.status === 'UNDER_REVIEW' && <button style={S.btn('primary')} onClick={() => moveTo('APPROVED')}>{t('innov.approve_study')}</button>}
          {study.status === 'UNDER_REVIEW' && <button style={S.btn()} onClick={() => moveTo('REWORK')}>{t('innov.request_rework')}</button>}
          {study.status === 'DRAFT' && hasGeneratedContent && <button style={S.btn('primary')} onClick={() => moveTo('UNDER_REVIEW')}>{t('innov.move_to_review')}</button>}
          <div style={{ flex: 1 }} />
          <button style={S.btn('danger')} onClick={deleteStudy}>{t('innov.delete_study')}</button>
        </div>
      </div>
    </div>
  )
}

// ── Related EA Objects (Innovation-P5 traceability) ─────────────────────────

const RELATED_OBJECT_TYPE_LABEL: Record<string, { en: string; ar: string }> = {
  EA_ASSET: { en: 'EA Asset', ar: 'أصل بنية مؤسسية' },
  ADM_CYCLE: { en: 'ADM Cycle', ar: 'دورة تطوير البنية' },
  GOVERNANCE_REVIEW: { en: 'Governance Review', ar: 'مراجعة الحوكمة' },
  EA_PLAN: { en: 'EA Plan', ar: 'خطة البنية المؤسسية' },
  STRATEGY: { en: 'Strategy', ar: 'استراتيجية' },
}

function RelatedObjectsPanel({ api, studyId, isAR, t }: any) {
  const [relationships, setRelationships] = useState<any[]>([])
  const [linking, setLinking] = useState(false)
  const [form, setForm] = useState({ relatedObjectType: 'EA_ASSET', relatedObjectId: '', notes: '' })
  const [assets, setAssets] = useState<any[]>([])
  const [error, setError] = useState('')

  const load = useCallback(() => { api.get(`/innovation/studies/${studyId}/relationships`).then((d: any) => setRelationships(Array.isArray(d) ? d : [])) }, [api, studyId])
  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (form.relatedObjectType === 'EA_ASSET' && assets.length === 0) api.get('/ea-repository/assets').then((d: any) => setAssets(Array.isArray(d) ? d : []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.relatedObjectType])

  const link = async () => {
    if (!form.relatedObjectId) return
    setError('')
    try {
      await api.post(`/innovation/studies/${studyId}/relationships`, form)
      setForm({ relatedObjectType: 'EA_ASSET', relatedObjectId: '', notes: '' })
      setLinking(false); await load()
    } catch (e: any) { setError(e.message) }
  }

  const unlink = async (relationshipId: string) => {
    if (!window.confirm(t('innov.confirm_unlink'))) return
    await api.post(`/innovation/studies/${studyId}/relationships/${relationshipId}/delete`); await load()
  }

  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ flex: 1, fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center' }}>
          {t('innov.related_objects')}
          <HelpTip text={isAR
            ? 'اربط هذه الدراسة بكائنات حقيقية في البنية المؤسسية (قدرات، تطبيقات، تقنيات، دورات، مراجعات، خطط، استراتيجيات) لمعرفة أي الدراسات تؤثر على أي جزء من البنية.'
            : 'Link this study to real EA repository objects (capabilities, applications, technologies, ADM cycles, reviews, plans, strategies) so it\'s traceable which studies affect which part of the architecture.'} />
        </div>
        <button style={S.btn()} onClick={() => setLinking(true)}>{t('innov.link_object')}</button>
      </div>

      {linking && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12, padding: 12, background: 'var(--navy)', borderRadius: 8 }}>
          <select style={S.input} value={form.relatedObjectType} onChange={e => setForm(f => ({ ...f, relatedObjectType: e.target.value, relatedObjectId: '' }))}>
            <option value="EA_ASSET">{t('innov.related_ea_asset')}</option>
            <option value="ADM_CYCLE">{t('innov.related_adm_cycle')}</option>
            <option value="GOVERNANCE_REVIEW">{t('innov.related_review')}</option>
            <option value="EA_PLAN">{t('innov.related_plan')}</option>
            <option value="STRATEGY">{t('innov.related_strategy')}</option>
          </select>
          {form.relatedObjectType === 'EA_ASSET' ? (
            <select style={S.input} value={form.relatedObjectId} onChange={e => setForm(f => ({ ...f, relatedObjectId: e.target.value }))}>
              <option value="">{t('innov.select_ea_asset')}</option>
              {assets.map((a: any) => <option key={a.id} value={a.id}>{a.name} [{a.assetType}]</option>)}
            </select>
          ) : (
            <input style={S.input} placeholder={t('innov.object_id')} value={form.relatedObjectId} onChange={e => setForm(f => ({ ...f, relatedObjectId: e.target.value }))} />
          )}
          {error && <div style={{ fontSize: 11, color: '#e74c3c' }}>{error}</div>}
          <div style={S.row}>
            <button style={S.btn('primary')} onClick={link}>{t('innov.link')}</button>
            <button style={S.btn()} onClick={() => { setLinking(false); setError('') }}>{t('innov.cancel')}</button>
          </div>
        </div>
      )}

      {relationships.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{t('innov.no_relationships')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {relationships.map((r: any) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
              <span style={S.badge('#8e44ad')}>{isAR ? RELATED_OBJECT_TYPE_LABEL[r.relatedObjectType]?.ar : RELATED_OBJECT_TYPE_LABEL[r.relatedObjectType]?.en}</span>
              <span style={{ flex: 1, fontFamily: 'monospace', color: 'var(--text-dim)' }}>{r.relatedObjectId}</span>
              <span style={{ color: 'var(--text-dim)' }}>{r.relationshipType}</span>
              <button style={{ ...S.btn('danger'), padding: '2px 8px', fontSize: 11 }} onClick={() => unlink(r.id)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
